<template>
  <div :class="{ 'image-analysis-with-visibility': commerceVisibilityOpen }">
    <div
      v-if="commerceVisibilityMounted"
      v-show="commerceVisibilityOpen"
      class="commerce-visibility-container"
    >
      <CommerceVisibilityReport
        v-bind="commerceVisibilityProps"
        embedded
        :active="commerceVisibilityOpen && commerceVisibilityTabActive"
        :initial-selection="commerceVisibilityInitialSelection"
        @selection-change="onCommerceVisibilitySelection"
        @back="closeCommerceVisibility"
      />
    </div>
    <StepProgressBar
      v-if="!isOptixLab"
      :title="step > 2 ? selectedPrompts.map(p => p.name).join(', ') : 'Analyze Images'"
      :current-step="step - 1"
      :max-steps="lastStep - 1"
    />
    <div
      v-else-if="!isConfigViewOpen"
      class="step-progress-bar mb-3"
    >
      <div class="d-flex justify-content-between align-items-start">
        <div>
          <!-- Editable title for Visual Playbook Analysis -->
          <div
            v-if="!isConfigViewOpen && isVisualPlayBookAnalysis() && step === 3"
            class="editable-title-container"
          >
            <!-- Edit mode: show input -->
            <input
              v-if="isEditingReportName"
              ref="reportNameInput"
              v-model="visualBenchmarkReportName"
              type="text"
              class="editable-report-title"
              placeholder="Enter report name"
              @blur="isEditingReportName = false"
              @keyup.enter="isEditingReportName = false"
              @keyup.escape="isEditingReportName = false"
            >
            <!-- Display mode: show title with hover pencil -->
            <div
              v-else
              class="d-flex align-items-center report-title-display"
              @click="startEditingReportName"
            >
              <h5 class="mb-0 font-weight-bold">
                {{ visualBenchmarkReportName || 'Untitled Report' }}
              </h5>
              <b-icon
                icon="pencil"
                class="text-muted ml-2 edit-pencil-icon"
              />
            </div>
          </div>
          <!-- Regular title for non-Visual Playbook -->
          <h5
            v-else-if="!isConfigViewOpen"
            class="mb-0 font-weight-bold"
          >
            {{ stepTitle }}
          </h5>
          <!-- Subtitle: For Visual Playbook show 'Visual Insights by Category', otherwise show stepDescription -->
          <p
            v-if="(step === 2 || step === 3) && !isConfigViewOpen"
            class="text-muted mt-1 mb-0"
          >
            {{ isVisualPlayBookAnalysis() && step === 3 ? 'Visual Insights by Category' : (stepDescription || '\u00A0') }}
          </p>
        </div>
        <!-- Hide Configure Audiences button for Visual Playbook analysis -->
        <div
          v-if="isOptixLab && !isConfigViewOpen && !(isVisualPlayBookAnalysis() && step === 3)"
          class="d-flex align-items-center"
          style="gap: 8px;"
        >
          <b-button
            variant="outline-secondary"
            size="sm"
            class="configure-audiences-btn"
            @click="toggleAudiencesList"
          >
            <b-icon icon="people" />
            Configure Audiences
          </b-button>
          <div class="position-relative">
            <b-button
              variant="outline-secondary"
              size="sm"
              class="pim-dam-gear-btn"
              @click="togglePimDamDropdown"
            >
              <b-icon icon="sliders" />
            </b-button>
            <div
              v-if="showPimDamDropdown"
              class="pim-dam-dropdown"
            >
              <div class="pim-dam-dropdown-header">
                PIM / DAM Connections
              </div>
              <div class="pim-dam-dropdown-item">
                <span class="pim-dam-label">
                  Salsify
                  <span
                    class="pim-dam-dot"
                    :class="{ 'connected': isSalsifyConnected }"
                  />
                </span>
                <span
                  v-if="isSalsifyConnected"
                  class="pim-dam-status connected"
                >
                  Connected
                </span>
                <a
                  v-else
                  href="javascript:void(0)"
                  class="pim-dam-connect-link"
                  @click="openSetupConnectionModal"
                >
                  Connect
                </a>
              </div>
              <div class="pim-dam-dropdown-item">
                <span class="pim-dam-label">
                  Bynder
                  <span
                    class="pim-dam-dot"
                    :class="{ 'connected': isBynderConnected }"
                  />
                </span>
                <span
                  v-if="isBynderConnected"
                  class="pim-dam-status connected"
                >
                  Connected
                </span>
                <a
                  v-else
                  href="javascript:void(0)"
                  class="pim-dam-connect-link"
                  @click="openSetupConnectionModal"
                >
                  Connect
                </a>
              </div>
              <div
                v-if="!isSalsifyConnected && !isBynderConnected"
                class="pim-dam-warning"
              >
                <i class="bi bi-exclamation-circle" />
                No PIM/DAM connected
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="isOptixLab && !isConfigViewOpen && (step === 2 || (step === 3 && viewMode === 'list'))"
      class="d-flex align-items-center mb-3"
      style="gap: 12px;"
    >
      <div
        class="position-relative flex-grow-1"
      >
        <i
          class="bi bi-search position-absolute"
          style="left: 12px; top: 50%; transform: translateY(-50%); color: #6c757d;"
        />
        <input
          id="search-analysis-types"
          v-model="promptsSearchQuery"
          type="text"
          class="form-control"
          placeholder="Search analysis types..."
          style="padding-left: 38px; padding-right: 38px;"
          @input="updatePromptsSearchQuery"
        >
        <button
          v-if="promptsSearchQuery"
          type="button"
          class="btn btn-link position-absolute"
          title="Clear search"
          style="right: 5px; top: 50%; transform: translateY(-50%); padding: 0; color: #6c757d; text-decoration: none;"
          @click="promptsSearchQuery = ''"
        >
          <i class="bi bi-x-circle" />
        </button>
      </div>
      <select
        v-model="promptsTagFilter"
        class="form-control"
        style="width: auto; min-width: 150px;"
      >
        <option value="">
          All
        </option>
        <option
          v-for="tag in availableTags"
          :key="tag"
          :value="tag"
        >
          {{ tag }}
        </option>
      </select>
      <!-- View Toggle Buttons -->
      <div class="view-toggle-buttons">
        <button
          type="button"
          class="view-toggle-btn"
          :class="{ 'active': viewMode === 'list' }"
          title="List View"
          @click="setViewMode('list')"
        >
          <i class="bi bi-list" />
        </button>
        <button
          type="button"
          class="view-toggle-btn"
          :class="{ 'active': viewMode === 'gallery' }"
          title="Gallery View"
          @click="setViewMode('gallery')"
        >
          <i class="bi bi-grid" />
        </button>
      </div>
    </div>

    <!-- Audiences Configuration View -->
    <template v-if="showAudiencesList && isOptixLab">
      <div class="audiences-config__backrow">
        <button
          type="button"
          class="btn btn-link audiences-config__back d-flex align-items-center mb-4"
          @click="toggleAudiencesList"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="mr-2"
          >
            <path d="m12 19-7-7 7-7" />
            <path d="M19 12H5" />
          </svg>
          Back to Optix Lab
        </button>
      </div>
      <AudiencePage 
        :show-modal="showAudienceModalFlag" 
        :from-analysis="creatingFromAnalysisFlow"
        @update-audiences-analysis="updateAudienceAnalysis"
        @update-audiences="updateAudience"
      />
    </template>

    <!-- Brand Guidelines Configuration View -->
    <template v-if="showBrandGuidelinesList && isOptixLab">
      <div
        v-if="!isBrandGuidelinesEditMode"
        class="audiences-config__backrow"
      >
        <button
          type="button"
          class="btn btn-link audiences-config__back d-flex align-items-center mb-4"
          @click="toggleBrandGuidelinesList"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="mr-2"
          >
            <path d="m12 19-7-7 7-7" />
            <path d="M19 12H5" />
          </svg>
          Back to Optix Lab
        </button>
      </div>

      <div
        class="optix-brand-guidelines"
        :class="{ 'optix-brand-guidelines--editing': isBrandGuidelinesEditMode }"
      >
        <BrandGuidelines
          :from-analysis="creatingBrandGuidelineFromAnalysisFlow"
          @edit-mode-change="isBrandGuidelinesEditMode = $event"
          @update-brand-guidelines-analysis="updateBrandGuidelinesAnalysis"
          @update-brand-guidelines="updateBrandGuidelines"
        />
      </div>
    </template>

    <!-- Step 1: Image Type Selection  -->
    <template v-if="step === 1 && !isConfigViewOpen">
      <div class="card-selection-container">
        <div class="card-grid">
          <div 
            class="selection-card"
            :class="{ 'selected': selectedImageType === 'pdp' }" 
            @click="select('pdp')"
          >
            <div 
              v-if="selectedImageType === 'pdp'"
              class="selected-indicator"
            >
              <i class="bi bi-check2 checkmark" />
            </div>
            <div class="card-content">
              <div class="icon-container">
                <i class="bi bi-handbag font-icons" />
              </div>
              <h3 class="card-title">
                PDP Images
              </h3>
              <p class="card-description">
                Product pages, eCommerce carousels, mobile readiness, and packaging views
              </p>
            </div>
            <button 
              class="select-button" 
              :class="{ 'selected': selectedImageType === 'pdp' }"
            >
              {{ selectedImageType === 'pdp' ? 'Selected' : 'Select' }}
            </button>
          </div>
          <div 
            class="selection-card" 
            :class="{ 'selected': selectedImageType === 'banner' }" 
            @click="select('banner')"
          >
            <div 
              v-if="selectedImageType === 'banner'"
              class="selected-indicator"
            >
              <i class="bi bi-check2 checkmark" />
            </div>
            <div class="card-content">
              <div class="icon-container">
                <i class="bi bi-megaphone font-icons" />
              </div>
              <h3 class="card-title">
                Banner Ads
              </h3>
              <p class="card-description">
                Display creatives, call-to action effectiveness, size compliance
              </p>
            </div>
            <button 
              class="select-button" 
              :class="{ 'selected': selectedImageType === 'banner' }"
            >
              {{ selectedImageType === 'banner' ? 'Selected' : 'Select' }}
            </button>
          </div>
        </div>
      </div>
    </template>
      
    <!-- Step 2: Analysis Type Selection -->
    <div v-else-if="step === 2 && !isConfigViewOpen">
      <div
        v-if="isOptixLab && dashboardPrompts.length === 0 && promptsSearchQuery"
        class="text-center py-5"
      >
        <i class="fas fa-search fa-3x text-muted mb-3" />
        <p class="text-muted">
          No analysis types found matching "{{ promptsSearchQuery }}".
        </p>
        <button
          class="btn btn-secondary"
          @click="promptsSearchQuery = ''"
        >
          Clear Search
        </button>
      </div>
      <div
        v-else-if="isOptixLab"
        class="prompts-grid"
      >
        <div
          v-for="prompt in dashboardPrompts"
          :key="prompt.id"
          class="prompt-card cursor-pointer"
          @click.stop="startAnalysisWithPrompt(prompt)"
        >
          <span
            v-if="prompt.tag"
            class="prompt-tag-badge"
          >
            {{ prompt.tag }}
          </span>
          <div class="prompt-card-header">
            <div class="prompt-icon">
              <img
                v-if="prompt.icon === 'custom-ab-test'"
                src="/img/icons/ab-testing.svg"
                style="width: 20px; height: 20px;"
                alt="A/B Test icon"
              >
              <img
                v-else-if="prompt.icon === 'custom-image-order'"
                src="/img/icons/image-order.svg"
                style="width: 20px; height: 20px;"
                alt="Image Order icon"
              >
              <img
                v-else-if="prompt.icon === 'custom-prioritize'"
                src="/img/icons/prioritize.svg"
                style="width: 20px; height: 20px;"
                alt="Prioritize icon"
              >
              <img
                v-else-if="prompt.icon === 'custom-holidays'"
                src="/img/icons/gift-box.svg"
                style="width: 20px; height: 20px;"
                alt="Holidays icon"
              >
              <i
                v-else-if="prompt.icon && prompt.icon.startsWith('lucide-')"
                :data-lucide="prompt.icon.replace('lucide-', '')"
                class="lucide-icon"
                style="width: 20px; height: 20px;"
              />
              <i
                v-else-if="prompt.icon && prompt.icon.trim()"
                :class="prompt.icon"
              />
              <i
                v-else
                class="bi bi-lightbulb-fill"
              />
            </div>
            <div class="prompt-meta">
              <div class="prompt-title">
                {{ prompt.display_name || prompt.name }}
              </div>
              <div class="prompt-desc">
                {{ prompt.short_description || '' }}
              </div>
            </div>
          </div>
          <div class="prompt-card-footer">
            <button
              class="btn btn-sm w-100"
              type="button"
            >
              <span class="float-left">Start Analysis</span> <i class="bi bi-chevron-right m-0 float-right" />
            </button>
          </div>
        </div>
      </div>
      <b-form-group v-else>
        <template #label>
          <div class="d-flex align-items-center gap-2">
            <span>Analysis Type:</span>
          </div>
        </template>


        <Multiselect
          v-model="selectedPrompts"
          label="name"
          track-by="id"
          :allow-empty="true"
          :options="promptOptions"
          :disabled="customPrompts.length === 0 || customPrompts[0] === 'loading'"
          :multiple="true"
        />
      </b-form-group>
      <div 
        v-if="presets.length"
      >
        <span>Selected images:</span>
        <div class="image-grid">
          <div
            v-for="(template, index) in presets"
            :key="index"
            class="image-card"
          >
            <img
              :src="getPresetThumbnailUrl(template.thumbnail)"
              :alt="`generated_image_${index + 1}`"
              loading="lazy"
            >
          </div>
        </div>
      </div>

      <div v-if="!isOptixLab">
        <div class="form-group">
          <div
            class="form-check"
          >
            <input
              id="background_job"
              v-model="isBackgroundJob"
              class="form-check-input"
              type="checkbox"
            >
            <label
              id="background_job_tooltip"
              for="background_job"
            >
              Run in background
            </label>
            <b-tooltip
              target="background_job_tooltip"
              triggers="hover"
              title="The report will be generated automatically in the background. You'll be notified once it's ready."
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Step 3: Upload Images -->
    <div
      v-else-if="step === 3"
      v-show="!isConfigViewOpen"
      :class="{ 'list-view-container': isOptixLab && viewMode === 'list' }"
    >
      <div :class="{ 'list-view-split': isOptixLab && viewMode === 'list' }">
        <!-- Left Panel: Analysis Types with Checkboxes (List View Only) -->
        <div
          v-if="isOptixLab && viewMode === 'list'"
          class="list-view-left-panel"
        >
          <div class="analysis-types-list">
            <!-- Select All -->
            <div
              class="analysis-type-item select-all-item"
            >
              <b-form-checkbox
                :checked="allPromptsSelectedListView"
                @change="toggleSelectAllPromptsListView"
              />
              <span
                class="analysis-type-name cursor-pointer"
                @click="toggleSelectAllPromptsListView"
              >Select all</span>
              <a
                v-if="selectedPromptsListView.length > 0"
                href="#"
                class="clear-selection-link ml-auto"
                @click.stop.prevent="clearAllPromptsListView"
              >
                Clear
              </a>
            </div>

            <!-- Analysis Type Items -->
            <div
              v-for="prompt in dashboardPrompts"
              :key="'list-' + prompt.id"
              class="analysis-type-item"
              :class="{ 'selected': isPromptSelectedListView(prompt) }"
              @click="togglePromptSelectionListView(prompt)"
            >
              <a
                v-if="prompt.launch_url"
                :href="prompt.launch_url"
                class="btn btn-sm btn-outline-success mr-2"
                @click.stop.prevent="openStandaloneReport(prompt)"
              >Open →</a>
              <b-form-checkbox
                v-else
                :checked="isPromptSelectedListView(prompt)"
                @change="togglePromptSelectionListView(prompt)"
                @click.stop
              />
              <div
                class="analysis-type-icon"
                :class="{ 'selected': isPromptSelectedListView(prompt) }"
              >
                <img
                  v-if="prompt.icon === 'custom-ab-test'"
                  src="/img/icons/ab-testing.svg"
                  style="width: 18px; height: 18px;"
                  alt="A/B Test icon"
                >
                <img
                  v-else-if="prompt.icon === 'custom-image-order'"
                  src="/img/icons/image-order.svg"
                  style="width: 18px; height: 18px;"
                  alt="Image Order icon"
                >
                <img
                  v-else-if="prompt.icon === 'custom-prioritize'"
                  src="/img/icons/prioritize.svg"
                  style="width: 18px; height: 18px;"
                  alt="Prioritize icon"
                >
                <img
                  v-else-if="prompt.icon === 'custom-holidays'"
                  src="/img/icons/gift-box.svg"
                  style="width: 18px; height: 18px;"
                  alt="Holidays icon"
                >
                <i
                  v-else-if="prompt.icon && prompt.icon.startsWith('lucide-')"
                  :data-lucide="prompt.icon.replace('lucide-', '')"
                  class="lucide-icon"
                  style="width: 18px; height: 18px;"
                />
                <i
                  v-else-if="prompt.icon && prompt.icon.trim()"
                  :class="prompt.icon"
                />
                <i
                  v-else
                  class="bi bi-lightbulb-fill"
                />
              </div>
              <div class="analysis-type-name-wrapper">
                <span class="analysis-type-name">{{ prompt.display_name || prompt.name }}</span>
                <b-badge
                  v-if="prompt.tag"
                  class="prompt-tag-badge-inline"
                >
                  {{ prompt.tag }}
                </b-badge>
              </div>
              <!-- Info icon with tooltip (shown on hover) -->
              <i
                v-if="prompt.short_description"
                v-b-tooltip.hover.left
                :title="prompt.short_description"
                class="bi bi-info-circle analysis-type-info-icon ml-auto"
                @click.stop
              />
            </div>
          </div>
        </div>

        <!-- Right Panel (List View) / Main Content (Gallery View) -->
        <div :class="{ 'list-view-right-panel': isOptixLab && viewMode === 'list' }">
          <b-tabs
            v-if="!isVisualPlayBookAnalysis() && !isShopperInsightsReport()"
            v-model="activeTab"
            :class="[
              'custom-accessibility-tabs',
              { 'reversed-tabs': isImageComparisonAnalysis() }
            ]"
            content-class="mt-3"
            justified
          >
            <b-tab
              v-if="!isImageOrderAnalysis()"
              :active="activeTab === 0"
            >
              <template #title>
                <b-icon
                  icon="upload"
                  class="mr-2"
                />
                {{
                  isImageComparisonAnalysis()
                    ? "Step 2: Upload Images"
                    : "Upload Images"
                }}
              </template>
              <b-overlay
                :show="isLoadingRerunImages || isLoadingFileBrowserImages"
                rounded="sm"
                opacity="0.75"
                spinner-variant="primary"
                :z-index="2000"
              >
                <template #overlay>
                  <div class="image-loading-overlay text-center">
                    <b-spinner
                      label="Loading..."
                      variant="primary"
                      class="image-loading-spinner"
                    />

                    <div class="mt-3 font-weight-bold">
                      {{
                        isLoadingFileBrowserImages
                          ? 'Loading selected files...'
                          : 'Loading images from previous report...'
                      }}
                    </div>

                    <div
                      v-if="isLoadingFileBrowserImages"
                      class="text-muted mt-1"
                    >
                      {{ fileBrowserImagesLoaded }} of {{ fileBrowserImagesTotal }} files loaded
                    </div>

                    <div
                      v-else
                      class="text-muted mt-1"
                    >
                      Please wait while we prepare your images
                    </div>

                    <div
                      v-if="isLoadingFileBrowserImages && fileBrowserImagesTotal > 0"
                      class="image-loading-progress mt-3"
                    >
                      <div
                        class="image-loading-progress-bar"
                        :style="{
                          width: `${Math.round((fileBrowserImagesLoaded / fileBrowserImagesTotal) * 100)}%`
                        }"
                      />
                    </div>
                  </div>
                </template>
                <div
                  v-if="!isVisualPlayBookAnalysis()"
                  ref="uploadContainer"
                  tabindex="0"
                  class="upload-container"
                >
                  <!--              <p class="mb-0">-->
                  <!--                {{ isComparisonAnalysis() ? 'Upload Images for Comparison' : 'Upload Images or Choose from File Browser' }}-->
                  <!--              </p>-->
                  <!-- Paste from clipboard instruction - commented for future use
                  <div class="paste-instruction">
                    <b-icon icon="image" />
                    <span>Paste image(s) from clipboard (Ctrl+V)</span>
                  </div>
                  -->

                  <Vue2Dropzone
                    id="imageUploadDropzone"
                    ref="fileInput"
                    key="imageUploadDropzone"
                    :options="dropzoneOptions"
                    @vdropzone-file-added="onFileAdded"
                    @vdropzone-removed-file="setFile(false)"
                    @vdropzone-thumbnail="onThumbnailGenerated"
                  />

                  <div
                    v-if="isProductComparisonAnalysis()"
                    class="mt-3 rounded border bg-light p-3"
                  >
                    <b-form-checkbox
                      v-model="compareWithProductUrl"
                      switch
                    >
                      <span class="font-weight-bold">Compare with product URL</span>
                    </b-form-checkbox>

                    <small class="text-muted d-block mt-1">
                      Compare the uploaded Proposed Assets with the Current PDP images from one product page.
                    </small>

                    <b-form-group
                      v-if="compareWithProductUrl"
                      label="Product URL"
                      label-for="comparison-product-url"
                      class="mt-3 mb-0"
                    >
                      <b-form-input
                        id="comparison-product-url"
                        v-model.trim="comparisonProductUrl"
                        type="text"
                        placeholder="https://www.amazon.com/dp/... or another retailer product URL"
                      />
                    </b-form-group>
                  </div>

                  <div class="d-flex align-items-start mt-2 flex-wrap">
                    <div
                      v-if="!isProductComparisonAnalysis()"
                      class="d-flex flex-wrap align-items-center gap-2"
                    >
                      <b-dropdown
                        variant="outline-secondary"
                        class="import-dropdown"
                      >
                        <template #button-content>
                          <b-icon
                            icon="folder2-open"
                            class="mr-2"
                          />
                          Import From...
                        </template>
                        <b-dropdown-item @click="openSelectFolderModal('default')">
                          <b-icon
                            icon="folder"
                            class="mr-2"
                          />
                          File Browser
                        </b-dropdown-item>
                        <b-dropdown-item @click="openLayoutModal()">
                          <b-icon
                            icon="layout-text-sidebar-reverse"
                            class="mr-2"
                          />
                          Layouts
                        </b-dropdown-item>
                        <b-dropdown-item
                          :disabled="isLoading"
                          @click="usePreviousImages(0)"
                        >
                          <b-icon
                            icon="images"
                            class="mr-2"
                          />
                          Previous Images
                          <b-spinner
                            v-if="isLoading"
                            small
                            class="ml-2"
                          />
                        </b-dropdown-item>
                        <b-dropdown-item
                          v-if="isBynderAvailable"
                          @click="openExternalAssetsModal('bynder')"
                        >
                          <b-icon
                            icon="image"
                            class="mr-2"
                          />
                          Bynder
                        </b-dropdown-item>
                        <b-dropdown-item
                          :disabled="isCapturingScreenshot"
                          @click="openScreenshotModal"
                        >
                          <b-icon
                            icon="camera"
                            class="mr-2"
                          />
                          Page Screenshot...
                          <b-spinner
                            v-if="isCapturingScreenshot"
                            small
                            class="ml-2"
                          />
                        </b-dropdown-item>
                      </b-dropdown>
                      <b-dropdown
                        v-if="uploadedFilesCount > 0"
                        variant="outline-secondary"
                        class="export-dropdown"
                      >
                        <template #button-content>
                          <b-icon
                            icon="download"
                            class="mr-2"
                          />
                          Export...
                        </template>
                        <b-dropdown-item @click="openSelectFolderModal('selectAndCreate')">
                          <b-icon
                            icon="file-earmark-image"
                            class="mr-2"
                          />
                          Save Images to Files
                        </b-dropdown-item>
                        <b-dropdown-item v-b-modal.export-to-layout-modal>
                          <b-icon
                            icon="layout-text-sidebar-reverse"
                            class="mr-2"
                          />
                          Export Images to Layout
                        </b-dropdown-item>
                      </b-dropdown>
                    </div>

                    <div
                      v-if="(uploadedFilesCount > 0 || (manualItemUrl.trim() && !hasUrlInput)) && !isImageComparisonAnalysis() && !isProductComparisonAnalysis() && !isDimensionsReport()"
                      class="ml-3"
                    >
                      <b-button
                        v-b-toggle.manual-item-url-collapse
                        variant="link"
                        class="p-0 align-baseline manual-item-url-btn"
                        :aria-expanded="showManualUrl || isImageComparisonAnalysis() ? true : false"
                        aria-controls="manual-item-url-collapse"
                      >
                        {{
                          isImageComparisonAnalysis()
                            ? "Enter Salsify product ID"
                            : "Optional: Enter ASIN / TCIN / URL for Report"
                        }}

                        <b-icon
                          v-if="isImageComparisonAnalysis()"
                          :icon="showManualUrl ? 'chevron-up' : 'chevron-down'"
                          class="ml-1"
                        />
                      </b-button>

                      <b-collapse
                        id="manual-item-url-collapse"
                        v-model="showManualUrl"
                        class="mt-2"
                      >
                        <b-form-group
                          label-for="manual-item-url"
                          class="mb-0"
                        >
                          <small 
                            v-if="!isImageComparisonAnalysis()"
                            class="text-muted d-block mb-2"
                          >
                            This will be displayed in the report even when using uploaded images
                          </small>

                          <b-form-textarea
                            v-if="manualItemUrl && manualItemUrl.includes('\n')"
                            id="manual-item-url"
                            v-model="manualItemUrl"
                            placeholder="Enter ASINs, TCINs, or URLs (separated by spaces or line breaks)"
                            rows="4"
                            trim
                          />
                          <b-form-input
                            v-else
                            id="manual-item-url"
                            v-model="manualItemUrl"
                            :placeholder="
                              isImageComparisonAnalysis()
                                ? 'Enter Salsify ID'
                                : 'e.g., B08N5WRWNW or https://www.amazon.com/dp/B08N5WRWNW'
                            "
                            trim
                          />
                        </b-form-group>
                      </b-collapse>
                    </div>
                  </div>
                </div>
              </b-overlay>
            </b-tab>

            <b-tab
              v-if="!isImageOrderAnalysis() && !isImageComparisonAnalysis() && !isShopperInsightsReport()"
              :active="activeTab === 1"
            >
              <template #title>
                <b-icon
                  icon="link-45deg"
                  class="mr-2"
                />
                URLs / ASINs
              </template>
              <div v-if="isComparisonAnalysis()">
                <div class="rounded border p-3 mb-3">
                  <div class="d-flex align-items-flex-start gap-2 flex-column">
                    <b-form-group
                      label="Enter URLs or Product IDs for comparison"
                    >
                      <b-form-textarea
                        v-model="comparisonMultipleInput"
                        placeholder="Enter URLs, ASINs, Walmart IDs, TCINs or Salsify IDs for comparison (separated by spaces or line breaks) "
                        rows="4"
                        class="flex-grow-1"
                      />
                      <div class="use-previous-asin-hint-row form-text text-muted">
                        <span>You can enter multiple items separated by spaces or line breaks</span>
                        <a
                          href="#"
                          class="use-previous-asin-link"
                          :class="{ 'use-previous-asin-link--disabled': isLoading }"
                          :aria-disabled="isLoading ? 'true' : 'false'"
                          @click.prevent="onUsePreviousAsinClick"
                        >
                          Use Previous ASIN/URL
                          <b-spinner
                            v-if="isLoading"
                            small
                            class="ml-1"
                          />
                        </a>
                      </div>
                    </b-form-group>
                  </div>
                </div>
              </div>
              <div
                v-else
                class="rounded border p-3 mb-3"
              >
                <div class="d-flex align-items-flex-start gap-2 flex-column">
                  <b-form-group
                    label="Enter URLs or Product IDs"
                  >
                    <b-form-textarea
                      v-model="multipleUrlsInput"
                      placeholder="Enter URLs, ASINs, Walmart IDs, TCINs or Salsify IDs (separated by spaces or line breaks)"
                      rows="4"
                      class="flex-grow-1"
                    />
                    <div class="use-previous-asin-hint-row form-text text-muted">
                      <span>You can enter multiple items separated by spaces or line breaks</span>
                      <a
                        href="#"
                        class="use-previous-asin-link"
                        :class="{ 'use-previous-asin-link--disabled': isLoading }"
                        :aria-disabled="isLoading ? 'true' : 'false'"
                        @click.prevent="onUsePreviousAsinClick"
                      >
                        Use Previous ASIN/URL
                        <b-spinner
                          v-if="isLoading"
                          small
                          class="ml-1"
                        />
                      </a>
                    </div>
                  </b-form-group>
                </div>

                <!-- Group Selection for Multiple URLs -->
                <div
                  v-if="hasMultipleUrls && canManageGroups"
                  class="mt-3"
                >
                  <div class="d-flex align-items-center gap-2">
                    <label
                      class="mb-0"
                      style="min-width: 150px;"
                    >
                      Add to Group:
                    </label>
                    <Multiselect
                      v-if="!createNewGroup"
                      v-model="selectedGroupObj"
                      track-by="id"
                      label="name"
                      :custom-label="groupNameWithReportsCount"
                      :options="availableGroups"
                      :disabled="isLoadingGroups"
                      class="flex-grow-1"
                      placeholder="Select a group (optional)"
                    />

                    <b-form-input
                      v-if="createNewGroup"
                      v-model="newGroupName"
                      placeholder="Enter new group name"
                      :state="newGroupName.length > 0 && newGroupName.length <= 255 ? true : null"
                      class="flex-grow-1"
                      size="sm"
                    />

                    <b-button
                      variant="link"
                      size="sm"
                      class="text-nowrap"
                      @click="createNewGroup = !createNewGroup"
                    >
                      {{ createNewGroup ? 'Select existing' : 'Create new' }}
                    </b-button>
                  </div>
                  <small class="text-muted d-block mt-1">
                    {{ urls.length }} URLs detected. Each will create a separate report{{ selectedGroupId || (createNewGroup && newGroupName) ? ' and be added to the group' : '' }}.
                  </small>
                </div>
              </div>
            </b-tab>
            <b-tab
              v-if="!isShopperInsightsReport() && !isDimensionsReport()"
              :active="activeTab === 2"
            >
              <template #title>
                <b-icon
                  icon="list-ul"
                  class="mr-2"
                />
                {{
                  isImageComparisonAnalysis()
                    ? "Step 1: Select a product from a product List"
                    : "Product Lists"
                }}
              </template>
              <!-- Product List Selection -->
              <div class="rounded border p-3 mb-3 bg-light">
                <div class="row">
                  <div class="col-md-6 mt-1">
                    <b-form-group label="Select Product Lists">
                      <Multiselect
                        v-model="selectedProductLists"
                        :options="availableProductLists"
                        :multiple="true"
                        :searchable="true"
                        :disabled="isLoadingProductLists"
                        :loading="isLoadingProductLists"
                        track-by="id"
                        label="name"
                        placeholder="Select product lists"
                        :allow-empty="true"
                        @input="loadProductsFromLists"
                      >
                        <template slot="noResult">
                          No product lists found
                        </template>
                      </Multiselect>
                      <small
                        v-if="isLoadingProductLists"
                        class="text-info"
                      >
                        <i class="fas fa-spinner fa-spin" /> Loading lists...
                      </small>
                    </b-form-group>
                  </div>
                  <div
                    v-if="availableProducts.length > 0"
                    class="col-md-6"
                  >
                    <b-form-group>
                      <div class="d-flex justify-content-between align-items-center">
                        <span>
                          Select Individual Products:
                        </span>
                        <div class="w-25 mb-1">
                          <b-form-select
                            v-model="selectedColumnFilter"
                            :options="availableColumnFilters"
                            size="sm"
                            value-field="value"
                            text-field="text"
                          />
                        </div>
                      </div>
                      <Multiselect
                        v-model="selectedProducts"
                        :options="filteredProducts"
                        :multiple="true"
                        :searchable="true"
                        :disabled="isLoadingProducts"
                        :loading="isLoadingProducts"
                        :close-on-select="false"
                        track-by="id"
                        label="display_name"
                        placeholder="Select specific products"
                        :allow-empty="true"
                        :limit="3"
                        :limit-text="limitText"
                        :max-height="300"
                        @input="populateSelectedProducts"
                      >
                        <template
                          slot="option"
                          slot-scope="props"
                        >
                          <div class="multiselect-option">
                            <strong>{{ props.option.display_name }}</strong>
                            <small class="text-muted d-block">{{ props.option.product_list_name }}</small>
                          </div>
                        </template>
                        <template slot="noResult">
                          No products found
                        </template>
                      </Multiselect>
                      <small
                        v-if="isLoadingProducts"
                        class="text-info"
                      >
                        <i class="fas fa-spinner fa-spin" /> Loading products...
                      </small>
                      <div class="mt-2">
                        <b-button
                          v-if="!isImageComparisonAnalysis()"
                          :variant="selectedProducts.length === filteredProducts.length ? 'primary' : 'outline-primary'"
                          size="sm"
                          class="mr-2"
                          :disabled="isLoadingProducts || filteredProducts.length === 0"
                          @click="selectAll"
                        >
                          <b-icon :icon="selectedProducts.length === filteredProducts.length ? 'check-square' : 'square'" /> Select All
                        </b-button>
                        <b-button
                          v-if="selectedProducts.length > 0"
                          variant="outline-danger"
                          size="sm"
                          @click="clearAll"
                        >
                          <i class="fas fa-times" /> Clear All
                        </b-button>
                      </div>
                    </b-form-group>
                  </div>
                </div>
              </div>
            </b-tab>
          </b-tabs>
          <div
            v-if="isShopperInsightsReport()"
          >
            <form id="ShopperInsightsForm">
              <div class="mb-3">
                <label class="form-label">Brand name</label>
                <input
                  v-model="shopperInsightsFormValues.brand_name"
                  type="text"
                  class="form-control"
                  name="brand_name"
                  required
                >
              </div>

              <div class="mb-3">
                <label class="form-label">Market</label>
                <input
                  v-model="shopperInsightsFormValues.market"
                  type="text"
                  class="form-control"
                  name="market"
                  required
                >
                <div class="form-text text-muted">
                  country/region
                </div>
              </div>

              <div class="mb-3">
                <label class="form-label">Category</label>
                <input
                  v-model="shopperInsightsFormValues.category"
                  type="text"
                  class="form-control"
                  name="category"
                  required
                >
              </div>

              <div class="mb-3">
                <label class="form-label">Key product or product lines (if relevant)</label>
                <textarea
                  v-model="shopperInsightsFormValues.product_lines"
                  class="form-control"
                  name="product_lines"
                  rows="2"
                  required
                />
              </div>

              <div class="mb-3">
                <label class="form-label">Objective</label>
                <textarea
                  v-model="shopperInsightsFormValues.objective"
                  class="form-control"
                  name="objective"
                  rows="3"
                  required
                />
                <div class="form-text text-muted">
                  Clarify the primary goal (e.g., Premiumization, Support price increase, Improve conversion rate, Differentiate in category, Launch new product, Market expansion).
                </div>
              </div>

              <div class="mb-3">
                <label class="form-label">Competitors</label>
                <textarea
                  v-model="shopperInsightsFormValues.competitors"
                  class="form-control"
                  name="competitors"
                  rows="2"
                />
                <div class="form-text text-muted">
                  Known competitors
                </div>
              </div>

              <div class="mb-3">
                <label class="form-label">Adjacent Categories</label>
                <input
                  v-model="shopperInsightsFormValues.adjacent_categories"
                  type="text"
                  class="form-control"
                  name="adjacent_categories"
                  required
                >
              </div>

              <div class="mb-3">
                <label class="form-label">Current Situation</label>
                <textarea
                  v-model="shopperInsightsFormValues.current_situation"
                  class="form-control"
                  name="current_situation"
                  rows="4"
                  required
                />
                <div class="form-text text-muted">
                  What is currently not working? Do you have data from Ask Rufus or platform AI assistants to identify specific "Category Friction"? Any constraints (brand guidelines, assets, etc.)?
                </div>
              </div>
            </form>
          </div>
          <div
            v-if="!isVisualPlayBookAnalysis() && !(isImageComparisonAnalysis() && activeTab === 1)"
            class="right-panel-footer mt-3"
          >
            <div
              v-if="!isDimensionsReport()"
              class="d-flex align-items-center mb-3"
            >
              <i
                class="bi bi-people mr-2"
                style="font-size: 1.25rem; color: #6c757d;"
              />

              <div class="flex-grow-1 audience-multiselect-wrapper">
                <Multiselect
                  v-model="selectedAudiences"
                  :options="audiences"
                  :multiple="true"
                  :searchable="true"
                  :loading="isLoadingAudiences"
                  :disabled="isLoadingAudiences"
                  track-by="id"
                  label="name"
                  placeholder="Choose audience..."
                  :close-on-select="false"
                  :allow-empty="true"
                  open-direction="auto"
                />
              </div>

              <b-button
                variant="outline-secondary"
                size="md"
                class="ml-2 btn-group-style h-fit"
                @click="openNewAudienceModal"
              >
                <i class="bi bi-plus-circle mr-1" />
                New Audience
              </b-button>

              <div
                class="d-flex align-items-center ml-3"
                style="min-width: 280px;"
              >
                <i
                  class="bi bi-shop mr-2"
                  style="font-size: 1.1rem; color: #6c757d;"
                />
                <select
                  v-model="selectedRetailerPrompt"
                  class="form-control"
                  style="height: 45px; border-color: #dee2e6;"
                >
                  <option :value="null">
                    Retailer (optional)
                  </option>
                  <option
                    v-for="retailer in retailerList"
                    :key="retailer.value"
                    :value="retailer.value"
                  >
                    {{ retailer.label }}
                  </option>
                </select>
              </div>
            </div>

            <div
              v-if="promptContainsAudience"
              class="d-flex align-items-center mb-3"
              style="font-size: 0.875rem; color: #6c757d;"
            >
              <i
                class="bi bi-info-circle"
                style="margin-right: 6px; font-size: 1rem;"
              />
              <span>This analysis works best with an audience selected</span>
            </div>


            <div
              v-if="!isDimensionsReport()"
              class="row mb-3"
            >
              <div class="col-md-6">
                <div class="d-flex align-items-center">
                  <i
                    class="bi bi-palette mr-2"
                    style="font-size: 1.15rem; color: #6c757d;"
                  />

                  <div class="flex-grow-1 audience-multiselect-wrapper">
                    <Multiselect
                      v-model="selectedBrandGuidelines"
                      :options="brandGuidelines"
                      :multiple="true"
                      :searchable="true"
                      :loading="isLoadingBrandGuidelines"
                      :disabled="isLoadingBrandGuidelines"
                      track-by="id"
                      label="brand_name"
                      placeholder="Choose brand guidelines..."
                      :close-on-select="false"
                      :allow-empty="true"
                      open-direction="auto"
                    >
                      <template
                        slot="option"
                        slot-scope="props"
                      >
                        <div>
                          <strong>{{ props.option.brand_name }}</strong>
                          <small
                            v-if="props.option.voice || props.option.visual_style"
                            class="text-muted d-block"
                          >
                            {{ [props.option.voice, props.option.visual_style].filter(Boolean).join(' · ') }}
                          </small>
                        </div>
                      </template>
                    </Multiselect>
                  </div>

                  <b-button
                    variant="outline-secondary"
                    size="md"
                    class="ml-2 btn-group-style h-fit"
                    @click="openNewBrandGuideline"
                  >
                    <i class="bi bi-plus-circle mr-1" />
                    New
                  </b-button>
                </div>
              </div>
              <div class="col-md-6">
                <div class="d-flex align-items-center">
                  <i
                    class="bi bi-calendar-event mr-2"
                    style="font-size: 1.15rem; color: #6c757d;"
                  />

                  <div class="flex-grow-1 audience-multiselect-wrapper">
                    <Multiselect
                      v-model="selectedSeasonalities"
                      :options="seasonalities"
                      :multiple="true"
                      :searchable="true"
                      :loading="isLoadingSeasonalities"
                      :disabled="isLoadingSeasonalities"
                      track-by="id"
                      label="name"
                      placeholder="Choose seasonality..."
                      :close-on-select="false"
                      :allow-empty="true"
                      open-direction="auto"
                    >
                      <template
                        slot="option"
                        slot-scope="props"
                      >
                        <div>
                          <strong>{{ props.option.name }}</strong>
                          <small
                            v-if="props.option.description"
                            class="text-muted d-block"
                          >
                            {{ props.option.description }}
                          </small>
                        </div>
                      </template>
                    </Multiselect>
                  </div>

                  <b-button
                    variant="outline-secondary"
                    size="md"
                    class="ml-2 btn-group-style h-fit"
                    @click="showSeasonalityModal = true"
                  >
                    <i class="bi bi-plus-circle mr-1" />
                    New
                  </b-button>
                </div>
              </div>
            </div>

            <SeasonalityModal
              :show.sync="showSeasonalityModal"
              @created="onSeasonalityCreated"
            />

            <div
              v-if="selectedPromptEditableTables.length"
              class="editable-prompt-fields-card rounded border bg-light mb-3"
            >
              <div
                class="d-flex justify-content-between align-items-center p-3 cursor-pointer"
                role="button"
                @click="editableKpiSettingsExpanded = !editableKpiSettingsExpanded"
              >
                <div>
                  <div class="font-weight-bold">
                    KPI Settings
                  </div>
                  <small class="text-muted">
                    These KPI fields come from the selected prompt and will be sent with the analysis request.
                  </small>
                </div>

                <div class="d-flex align-items-center">
                  <b-badge
                    v-if="selectedPromptEditableTables.length"
                    variant="secondary"
                    class="mr-2"
                  >
                    {{ selectedPromptEditableTables.length }}
                    {{ selectedPromptEditableTables.length === 1 ? 'block' : 'blocks' }}
                  </b-badge>

                  <i
                    class="fas"
                    :class="editableKpiSettingsExpanded ? 'fa-chevron-up' : 'fa-chevron-down'"
                  />
                </div>
              </div>

              <b-collapse v-model="editableKpiSettingsExpanded">
                <div class="px-3 pb-3">
                  <div
                    v-for="(table, tableIndex) in selectedPromptEditableTables"
                    :key="`editable-kpi-block-${tableIndex}`"
                    class="editable-kpi-table-card rounded border bg-white p-3 mb-3"
                  >
                    <div class="d-flex justify-content-between align-items-center mb-3">
                      <div>
                        <div class="font-weight-bold">
                          {{ table.label }}
                        </div>
                        <small class="text-muted">
                          {{ table.type === 'kpi_list' ? 'KPI Items' : 'KPI Table' }}
                        </small>
                      </div>

                      <b-badge
                        :variant="getEditableTableKpiTotal(table) === 100 ? 'success' : (getEditableTableKpiTotal(table) > 100 ? 'danger' : 'warning')"
                      >
                        Total: {{ getEditableTableKpiTotal(table) }}%
                      </b-badge>
                    </div>

                    <div
                      v-if="table.type === 'kpi_list'"
                      class="mb-3"
                    >
                      <label class="small text-muted mb-1">
                        KPI title / intro text
                      </label>
                      <b-form-textarea
                        v-model="table.intro"
                        rows="3"
                        size="sm"
                        placeholder="Example: An evaluation of each image in the Evaluation & Recommendations separate column using the following weighted criteria..."
                      />
                    </div>

                    <div
                      v-for="(kpi, index) in table.kpis"
                      :key="`editable-kpi-${tableIndex}-${index}`"
                      class="editable-kpi-row mb-2"
                    >
                      <div class="row align-items-start">
                        <div class="col-md-3">
                          <label class="small text-muted mb-1">KPI name</label>
                          <b-form-input
                            v-model="kpi.name"
                            size="sm"
                            placeholder="KPI name"
                          />
                        </div>

                        <div class="col-md-2">
                          <label class="small text-muted mb-1">Weight</label>
                          <b-input-group
                            size="sm"
                            append="%"
                          >
                            <b-form-input
                              v-model.number="kpi.weight"
                              type="number"
                              min="0"
                              max="100"
                              placeholder="0"
                            />
                          </b-input-group>
                        </div>

                        <div class="col-md-6">
                          <label class="small text-muted mb-1">Evaluation</label>
                          <b-form-textarea
                            v-model="kpi.evaluation"
                            rows="2"
                            size="sm"
                            placeholder="Evaluation instruction"
                          />
                        </div>

                        <div class="col-md-1 text-right pt-4">
                          <b-button
                            variant="outline-danger"
                            size="sm"
                            title="Remove KPI"
                            @click.stop="removeEditableTableKpi(tableIndex, index)"
                          >
                            <i class="fas fa-trash" />
                          </b-button>
                        </div>
                      </div>
                    </div>

                    <div class="text-right mt-2">
                      <b-button
                        variant="outline-primary"
                        size="sm"
                        @click.stop="addEditableTableKpi(tableIndex)"
                      >
                        <i class="fas fa-plus mr-1" />
                        Add KPI
                      </b-button>
                    </div>
                  </div>
                </div>
              </b-collapse>
            </div>

            <div
              v-if="uploadedFilesCount > 0 && !isDimensionsReport()"
              class="image-type-labels-settings mb-2"
            >
              <div
                class="advanced-toggle mb-2"
                @click="isImageTypeLabelsOpen = !isImageTypeLabelsOpen"
              >
                <i
                  class="fas fa-chevron-down mr-2"
                  :class="{ 'rotate-icon': isImageTypeLabelsOpen }"
                />
                <span>Image type labels</span>

                <b-badge
                  variant="secondary"
                  class="ml-2"
                >
                  {{ uploadedFilesCount }}
                </b-badge>
              </div>

              <b-collapse
                v-model="isImageTypeLabelsOpen"
                class="advanced-content mb-3"
              >
                <div class="advanced-panel p-3 border rounded bg-light">
                  <small class="text-muted d-block mb-3">
                    Optionally label each uploaded image, e.g. Hero, Lifestyle, Nutrition, Ingredients, Pack Shot.
                  </small>

                  <div
                    v-for="(uploadedFile, index) in uploadedImageFiles"
                    :key="getImageTypeLabelKey(uploadedFile, index)"
                    class="image-type-label-row d-flex align-items-center mb-2"
                    style="gap: 10px;"
                  >
                    <div
                      class="image-type-label-number text-muted text-center"
                      style="width: 70px; font-size: 13px;"
                    >
                      Image {{ index + 1 }}
                    </div>

                    <div
                      class="image-type-label-thumb rounded border bg-white d-flex align-items-center justify-content-center overflow-hidden"
                      style="width: 44px; height: 44px; flex: 0 0 44px;"
                    >
                      <img
                        v-if="uploadedFile.dataURL"
                        :src="uploadedFile.dataURL"
                        :alt="`Image ${index + 1}`"
                        style="width: 100%; height: 100%; object-fit: cover;"
                      >
                      <i
                        v-else
                        class="bi bi-image text-muted"
                      />
                    </div>

                    <b-form-input
                      v-model="imageTypeLabels[getImageTypeLabelKey(uploadedFile, index)]"
                      size="sm"
                      placeholder="e.g., Hero, Lifestyle, Nutrition, Ingredients, Pack Shot"
                      trim
                    />
                  </div>
                </div>
              </b-collapse>
            </div>

            <div
              class="advanced-toggle mb-2"
              @click="isAdvancedOpen = !isAdvancedOpen"
            >
              <i
                class="fas fa-chevron-down mr-2"
                :class="{ 'rotate-icon': isAdvancedOpen }"
              />
              <span>Advanced</span>
            </div>

            <b-collapse
              v-model="isAdvancedOpen"
              class="advanced-content mb-3"
            >
              <div class="advanced-panel p-3 border rounded bg-light">
                <div
                  v-if="previousReports.length > 0"
                  class="mb-2"
                >
                  <button
                    class="btn btn-link p-0 text-decoration-none"
                    @click="openPreviousReportModal"
                  >
                    <i class="bi bi-file-earmark-text mr-2" />
                    Incorporate previous report
                    <i
                      v-if="selectedPreviousReportId"
                      class="bi bi-check-circle-fill text-success ml-1"
                    />
                  </button>
                  <hr class="my-2">
                </div>
                <div class="mb-2">
                  <label
                    for="customer_name_input_panel"
                    class="form-label mb-1"
                    style="font-size: 14px;"
                  >
                    Customer name
                  </label>
                  <input
                    id="customer_name_input_panel"
                    v-model="customerName"
                    type="text"
                    class="form-control form-control-sm"
                    placeholder="e.g., PepsiCo"
                    @blur="saveCustomerName"
                  >
                </div>
                <div class="form-check mb-1">
                  <input
                    id="background_job_optix_panel"
                    v-model="isBackgroundJob"
                    class="form-check-input"
                    type="checkbox"
                  >
                  <label
                    for="background_job_optix_panel"
                    class="cursor-pointer mb-1"
                  >
                    Run in background
                  </label>
                </div>
                <div class="form-check mb-1">
                  <input
                    id="use_domain_as_client_name_panel"
                    v-model="useDomainAsClientName"
                    class="form-check-input"
                    type="checkbox"
                  >
                  <label
                    for="use_domain_as_client_name_panel"
                    class="cursor-pointer mb-1"
                  >
                    Use Domain as Customer Name
                  </label>
                </div>
                <div class="form-check mb-1">
                  <input
                    id="reduce_image_quality_panel"
                    v-model="isReduceImageQuality"
                    class="form-check-input"
                    type="checkbox"
                  >
                  <label
                    for="reduce_image_quality_panel"
                    class="cursor-pointer mb-1"
                  >
                    Use scaled images
                  </label>
                </div>
                <div class="form-check mb-1">
                  <input
                    id="save_settings_panel"
                    v-model="isSaveSettings"
                    class="form-check-input"
                    type="checkbox"
                  >
                  <label
                    for="save_settings_panel"
                    class="mb-1"
                  >
                    Save settings
                  </label>
                </div>
                <div
                  v-if="(uploadedFilesCount > 0 || hasUrlInput || previousReports.length > 0) && !isDimensionsReport()"
                  class="form-check mb-1"
                >
                  <input
                    id="enable-specific-images-panel"
                    v-model="enableSpecificImages"
                    class="form-check-input"
                    type="checkbox"
                  >
                  <label
                    for="enable-specific-images-panel"
                    class="cursor-pointer mb-1"
                  >
                    Report on specific images only (by image number)
                  </label>
                </div>
                <div
                  v-if="enableSpecificImages && (uploadedFilesCount > 0 || hasUrlInput || previousReports.length > 0) && !isDimensionsReport()"
                  class="mt-2"
                >
                  <input
                    id="specific-image-numbers-panel"
                    v-model="specificImageNumbers"
                    type="text"
                    class="form-control form-control-sm"
                    placeholder="e.g., 1, 3, 5"
                  >
                  <small class="text-muted d-block mt-1">
                    Enter image numbers separated by commas (e.g., 1, 3, 5). If left empty, all images will be evaluated.
                  </small>
                  <small class="text-muted d-block">
                    <i class="fas fa-info-circle mr-1" />
                    Note: All images will still be remembered for reuse in future reports.
                  </small>
                </div>
              </div>
            </b-collapse>

            <b-button
              v-if="!(isImageComparisonAnalysis() && activeTab === 1)"
              variant="primary"
              size="md"
              class="generate-report"
              :disabled="nextButtonDisabled"
              @click="nextStep"
            >
              <b-spinner
                v-if="isLoading"
                small
                class="mr-2"
              />
              <i
                v-else
                class="fas fa-play mr-2"
              />
              {{ isLoading ? 'Generating...' : 'Generate Reports' }}
            </b-button>
          </div>

          <div
            v-if="isImageComparisonAnalysis() && activeTab === 1"
            class="right-panel-footer mt-3"
          >
            <b-button
              variant="primary"
              size="md"
              class="generate-report"
              :disabled="nextButtonDisabled"
              @click="nextStep"
            >
              <b-spinner
                v-if="isLoading"
                small
                class="mr-2"
              />
              <i
                v-else
                class="fas fa-play mr-2"
              />
              {{ isLoading ? 'Generating...' : 'Continue to the next step: Upload Images' }}
            </b-button>
          </div>
          <div
            v-if="isVisualPlayBookAnalysis()"
          >
            <!-- Source selector with radio buttons -->
            <div class="mb-3">
              <label class="font-weight-bold mb-2 d-block">Source</label>
              <div
                class="d-flex align-items-center"
                style="gap: 20px;"
              >
                <b-form-radio
                  v-model="inputMode"
                  name="input-mode"
                  value="search"
                >
                  Search Term
                </b-form-radio>
                <b-form-radio
                  v-model="inputMode"
                  name="input-mode"
                  value="category"
                >
                  Category URL
                </b-form-radio>
                <b-form-radio
                  v-model="inputMode"
                  name="input-mode"
                  value="products"
                >
                  Product URLs
                </b-form-radio>
              </div>
            </div>

            <!-- Search keyword input (no link icon) -->
            <div
              v-if="inputMode === 'search'"
              class="mb-3"
            >
              <b-form-input
                id="new-report-search-keyword"
                v-model="searchKeyword"
                placeholder="Enter search keyword"
                class="visual-benchmark-input"
              />
            </div>

            <!-- Include/Exclude Brands side by side -->
            <div
              v-if="inputMode === 'search' || inputMode === 'category'"
              class="row mb-3"
            >
              <div class="col-md-6">
                <label class="mb-2 d-block">
                  <span class="font-weight-bold">Include Brands</span>
                  <span class="text-muted"> (optional)</span>
                </label>
                <b-form-input
                  id="new-report-include-brands"
                  v-model="includeBrands"
                  placeholder="Comma-separated"
                  class="visual-benchmark-input"
                />
              </div>
              <div class="col-md-6">
                <label class="mb-2 d-block">
                  <span class="font-weight-bold">Exclude Brands</span>
                  <span class="text-muted"> (optional)</span>
                </label>
                <b-form-input
                  id="new-report-exclude-brands"
                  v-model="excludeBrands"
                  placeholder="Comma-separated"
                  class="visual-benchmark-input"
                />
              </div>
            </div>

            <!-- Category URL input with link icon -->
            <div
              v-if="inputMode === 'category'"
              class="mb-3"
            >
              <div class="input-group visual-benchmark-input-group">
                <div class="input-group-prepend">
                  <span class="input-group-text">
                    <i class="bi bi-link-45deg" />
                  </span>
                </div>
                <b-form-textarea
                  ref="categoryUrlsTextarea"
                  v-model="categoryUrlsText"
                  placeholder="Enter one category URL per line"
                  rows="1"
                  class="form-control"
                  @input="handleCategoryUrlsInput"
                  @keyup="handleCategoryUrlsInput"
                  @change="handleCategoryUrlsInput"
                  @paste="handleCategoryUrlsPaste"
                />
              </div>
              <small class="text-muted mt-1 d-block">
                {{ categoryUrls.length }} URLs entered
              </small>
            </div>

            <!-- Product URLs input with link icon -->
            <div
              v-if="inputMode === 'products'"
              class="mb-3"
            >
              <div class="input-group visual-benchmark-input-group mb-2">
                <div class="input-group-prepend">
                  <span class="input-group-text">
                    <i class="bi bi-link-45deg" />
                  </span>
                </div>
                <b-form-textarea
                  ref="productUrlsTextarea"
                  v-model="productUrlsText"
                  placeholder="Enter one URL or ASIN per line (max 20)"
                  rows="1"
                  class="form-control"
                  @input="handleProductUrlsInput"
                  @keyup="handleProductUrlsInput"
                  @change="handleProductUrlsInput"
                  @paste="handleProductUrlsPaste"
                />
              </div>
              <small class="text-muted d-block mb-3">
                {{ productUrls.length }}/20 items entered
                <span
                  v-if="allProductUrls.length > 20"
                  class="text-danger"
                > - Too many items! Only first 20 will be used.</span>
              </small>

              <hr class="my-3">

              <div class="row">
                <div class="col-md-6">
                  <b-form-group label="Select from Product List:">
                    <Multiselect
                      v-model="selectedProductLists"
                      :options="availableProductLists"
                      :multiple="true"
                      :searchable="true"
                      :loading="isLoadingProductLists"
                      :disabled="isLoadingProductLists"
                      track-by="id"
                      label="name"
                      placeholder="Choose product lists"
                      @input="loadProductsFromProductLists"
                    />
                  </b-form-group>
                </div>

                <div
                  v-if="productListProducts.length > 0"
                  class="col-md-6"
                >
                  <b-form-group label="Select Individual Products:">
                    <Multiselect
                      v-model="selectedProducts"
                      :options="productListProducts"
                      :multiple="true"
                      :searchable="true"
                      :disabled="isLoadingProducts"
                      :loading="isLoadingProducts"
                      :close-on-select="false"
                      track-by="id"
                      label="display_name"
                      placeholder="Select specific products"
                      :allow-empty="true"
                      :limit="3"
                      :limit-text="limitText"
                      :max-height="300"
                      @input="populateSelectedProductsForPlaybook"
                    >
                      <template
                        slot="option"
                        slot-scope="props"
                      >
                        <div class="multiselect-option">
                          <strong>{{ props.option.display_name }}</strong>
                          <small class="text-muted d-block">{{ props.option.product_list_name }}</small>
                        </div>
                      </template>
                      <template slot="noResult">
                        No products found
                      </template>
                    </Multiselect>
                    <div class="mt-2">
                      <b-button
                        :variant="selectedProducts.length === productListProducts.length ? 'primary' : 'outline-primary'"
                        size="sm"
                        class="mr-2"
                        :disabled="isLoadingProducts || productListProducts.length === 0"
                        @click="selectAllProductsForPlaybook"
                      >
                        <b-icon :icon="selectedProducts.length === productListProducts.length ? 'check-square' : 'square'" /> Select All
                      </b-button>
                      <b-button
                        v-if="selectedProducts.length > 0"
                        variant="outline-danger"
                        size="sm"
                        @click="clearAllProductsForPlaybook"
                      >
                        <i class="fas fa-times" /> Clear All
                      </b-button>
                    </div>
                  </b-form-group>
                </div>
              </div>
            </div>

            <!-- Retailer and Results side by side (for search and category modes) -->
            <div
              v-if="inputMode === 'search' || inputMode === 'category'"
              class="row mb-3"
            >
              <div class="col-md-8">
                <label class="font-weight-bold mb-2 d-block">Retailer</label>
                <b-form-select
                  id="new-report-retailer"
                  v-model="selectedRetailer"
                  :options="retailers"
                  value-field="id"
                  text-field="name"
                  class="visual-benchmark-select"
                >
                  <template #first>
                    <b-form-select-option :value="null">
                      Select retailer
                    </b-form-select-option>
                  </template>
                </b-form-select>
              </div>
              <div class="col-md-4">
                <label class="font-weight-bold mb-2 d-block">Results</label>
                <b-form-input
                  id="new-report-number-of-processed-results"
                  v-model="countResults"
                  type="number"
                  class="visual-benchmark-input"
                />
              </div>
            </div>

            <!-- Max Images Per Product (only for products mode) -->
            <div
              v-if="inputMode === 'products'"
              class="mb-3"
            >
              <label class="font-weight-bold mb-2 d-block">Max Images Per Product</label>
              <b-form-input
                v-model="maxImagesPerProduct"
                placeholder="Number of max images per product"
                type="number"
                class="visual-benchmark-input"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Step 4: Analysis Results -->
    <div v-else-if="step === 4">
      <div
        v-if="isAnalyzing"
        class="text-center mt-5"
      >
        <div 
          v-if="!showLoadingStatus" 
          class="offset-3 col-md-6"
          style="text-align: center; padding: 20px"
        >
          <h4 class="text-success">
            <b-icon
              icon="hourglass-split"
              class="mr-2"
            />
            Analysis in progress
          </h4>
          <p 
            class="mb-0"
          >
            Do not close/reload this page...
          </p>

          <b-progress
            :value="fakePercents"
            :max="100"
            height="20px"
            class="mt-3"
            animated
            :striped="isPolling"
          >
            <b-progress-bar :value="fakePercents">
              {{ fakePercents }}%
            </b-progress-bar>
          </b-progress>
          <!--
          <p 
            class="mt-2"
          >
            Current item: {{ processedUrl }}
          </p>
          -->
          <p class="mt-2">
            {{ currentProcess }}
          </p>
        </div>
        <div
          v-else
          class="mt-2 loading-status"
        >
          <div>
            <h4 class="mt-4">
              {{ loadingStatusText }}
            </h4>
          </div>
          <div>
            {{ loadingStatusHelperText }}
          </div>
        </div>
        <div
          v-for="report in analysisReports"
          :key="typeof report === 'object' ? report.url : report"
          class="order-analysis-summary mb-4 p-3 border rounded bg-light"
        >
          <div style="text-align: center; padding: 20px">
            <h4 class="text-success">
              <b-icon
                icon="check-circle"
                class="mr-2"
              />
              Analysis Report Generated
            </h4>
            <p>
              Your analysis report has been created successfully.
            </p>
            <a
              :href="typeof report === 'object' ? report.url : report"
              target="_blank"
              class="btn btn-primary"
              :title="typeof report === 'object' ? report.originalUrl : report"
            >
              <i class="fas fa-external-link-alt mr-2" />
              Open Analysis Report
            </a>
          </div>
        </div>
      </div>
      <div v-else-if="isRedirecting">
        <div class="order-analysis-summary mb-4 p-3 border rounded bg-light">
          <div style="text-align: center; padding: 20px">
            <h4 class="text-success">
              <b-icon
                icon="check-circle"
                class="mr-2"
              />
              Analysis Report Generated Successfully!
            </h4>
            <p>
              Redirecting to your reports...
            </p>
            <div
              class="spinner-border text-primary"
              role="status"
            >
              <span class="sr-only">Loading...</span>
            </div>
          </div>
        </div>
      </div>
      <div v-else-if="!isRedirecting">
        <h3>Analysis Results</h3>
      
        <div v-if="analysisReportUrl">
          <div class="order-analysis-summary mb-4 p-3 border rounded bg-light">
            <div style="text-align: center; padding: 20px">
              <h4 class="text-success">
                <b-icon
                  icon="check-circle"
                  class="mr-2"
                />
                Analysis Report Generated
              </h4>
              <p>
                Your analysis report has been created successfully.
              </p>
              <a
                :href="analysisReportUrl"
                target="_blank"
                class="btn btn-primary"
              >
                <i class="fas fa-external-link-alt mr-2" />
                Open Analysis Report
              </a>
            </div>
          </div>
        </div>

        <div
          v-else-if="analysisReports.length > 0"
        >
          <!-- Button to Download All Reports as XLSX -->
          <div style="text-align: center; margin-top: 20px">
            <button
              class="btn btn-success mb-4"
              @click="downloadReports"
            >
              <i class="fas fa-file-excel mr-2" />
              Download All Links to Reports (XLSX)
            </button>
          </div>

          <div class="order-analysis-summary mb-4 p-3 border rounded bg-light">
            <div style="text-align: center; padding: 20px">
              <h4 class="text-success">
                <b-icon
                  icon="check-circle"
                  class="mr-2"
                />
                Analysis Report Generated
              </h4>
              <p>
                Your analysis report has been created successfully.
              </p>
              <div class="d-flex flex-wrap justify-content-center gap-2">
                <a
                  v-for="(report, index) in analysisReports"
                  :key="typeof report === 'object' ? report.url : report"
                  :href="typeof report === 'object' ? report.url : report"
                  target="_blank"
                  class="btn btn-primary"
                  style="margin: 5px"
                  :title="typeof report === 'object' ? report.originalUrl : report"
                >
                  <i class="fas fa-external-link-alt mr-2" />
                  Open Analysis Report {{ index + 1 }}
                </a>
              </div>
            </div>
          </div>
        </div>

        <div
          v-else-if="isBackgroundJob"
          class="order-analysis-summary mb-4 p-3 border rounded bg-light"
        >
          <div style="text-align: center; padding: 20px">
            <h4 class="text-success">
              <b-icon 
                icon="check-circle" 
                class="mr-2" 
              />
              Analysis in progress
            </h4>
            <p>You can navigate away and we will notify you when the report is ready.</p>

            <b-progress
              :value="overallProgressPercent"
              :max="100"
              height="20px"
              class="mt-3"
              animated
              :striped="isPolling"
            >
              <b-progress-bar :value="overallProgressPercent">
                {{ overallProgressPercent }}%
              </b-progress-bar>
            </b-progress>

            <div 
              v-if="backgroundProcesses.length" 
              class="text-left mt-3"
            >
              <div
                v-for="job in backgroundProcesses"
                :key="job.id"
                class="d-flex justify-content-between align-items-center py-1 border-bottom"
              >
                <div class="d-flex align-items-center">
                  <b-icon
                    v-if="isJobRunning(job)"
                    icon="arrow-clockwise"
                    animation="spin"
                    class="mr-2"
                  />
                  <b-icon 
                    v-else-if="isJobSucceeded(job)" 
                    icon="check-circle-fill" 
                    class="text-success mr-2" 
                  />
                  <b-icon 
                    v-else-if="isJobFailed(job)" 
                    icon="x-circle-fill" 
                    class="text-danger mr-2" 
                  />
                  <b-icon 
                    v-else-if="isJobCancelled(job)" 
                    icon="dash-circle-fill" 
                    class="text-muted mr-2" 
                  />
                  <span class="font-weight-500">
                    <template v-if="job.name">
                      {{ job.name }}
                    </template>
                    <template v-else>
                      <div class="job-id-container">
                        <small
                          class="see-job-id-link text-muted"
                          @click="$set(job, 'showJobId', !job.showJobId)"
                        >
                          {{ job.showJobId ? 'Hide Job ID' : 'See Job ID' }}
                        </small>
                        <div
                          v-if="job.showJobId"
                          class="job-id-display"
                        >
                          Job #{{ job.uuid }}
                          <b-icon
                            icon="clipboard"
                            class="copy-icon ml-1"
                            title="Copy Job ID"
                            @click.stop="copyJobId(job.uuid)"
                          />
                        </div>
                      </div>
                    </template>
                    <span v-if="job.links">
                      <a
                        v-for="link in job.links"
                        :key="link"
                        :href="link"
                        target="_blank"
                      >Open Report</a>
                    </span>
                  </span>
                </div>
                <small class="text-muted">
                  {{ (job.status || 'queued').toUpperCase() }}
                  <template v-if="typeof job.progress === 'number'"> · {{ Math.round(job.progress) }}%</template>
                </small>
              </div>
            </div>
          </div>
        </div>
      
        <div
          v-else-if="analysisError"
          class="order-analysis-summary mb-4 p-3 border rounded bg-light"
        >
          <div style="text-align: center; padding: 20px">
            <h4 :class="isImageCrawlingError ? 'text-muted' : 'text-danger'">
              <b-icon
                :icon="isImageCrawlingError ? 'info-circle' : 'exclamation-circle'"
                class="mr-2"
              />
              {{ isImageCrawlingError ? 'No Images Found' : 'Analysis Error' }}
            </h4>
            <p v-if="!isImageCrawlingError">
              There was an error while generating the analysis
            </p>
            <b-button
              variant="outline-primary"
              @click="retryAnalysis"
            >
              <b-icon
                icon="arrow-repeat"
                class="mr-2"
              />
              Try Again
            </b-button>
          </div>
        </div>
      
        <div
          v-else-if="!isRedirecting"
          class="order-analysis-summary mb-4 p-3 border rounded"
        >
          <div
            style="text-align: center; padding: 20px"
          >
            <p class="text-muted">
              No analysis results available.
            </p>
          </div>
        </div>
      
        <div class="mt-4 text-center">
          <b-button
            variant="primary"
            size="lg"
            @click="startNewAnalysis"
          >
            <b-icon icon="arrow-clockwise" />
            New Analysis
          </b-button>
        </div>
      </div>
    </div>
    
    <div
      v-if="step < lastStep && step >= 2"
      class="my-1 d-flex "
      :class="isOptixLab ? 'justify-content-between' : ''"
    >
      <div class="d-flex flex-grow-1">
        <b-button
          v-if="step > 2 && viewMode !== 'list' && !isConfigViewOpen"
          variant="secondary"
          class="btn-group-style h-fit"
          @click="previousStep"
        >
          <b-icon icon="arrow-left" />
          Previous
        </b-button>
        <b-button
          v-else-if="!isOptixLab"
          variant="secondary"
          :to="{ name: 'dashboard' }"
        >
          <b-icon
            icon="x-lg"
            scale="0.75"
          />
          Cancel
        </b-button>
      </div>

      <b-button
        v-if="(!isOptixLab || (isOptixLab && step === 2)) && !isConfigViewOpen"
        variant="primary"
        :disabled="nextButtonDisabled"
        :class="!isOptixLab ? 'ml-2' : 'd-none'"
        @click="nextStep"
      >
        {{ step < lastStep ? "Next" : "Start Analysis" }}
        <b-icon
          v-if="step < lastStep"
          icon="arrow-right"
        />
      </b-button>

      <!-- Simplified footer for Visual Playbook - only Previous and Generate Report -->
      <div
        v-if="step > 2 && isOptixLab && !isConfigViewOpen && isVisualPlayBookAnalysis()"
        class="d-flex justify-content-center flex-grow-1"
      >
        <b-button
          variant="primary"
          size="md"
          class="generate-report h-fit"
          :disabled="nextButtonDisabled"
          @click="nextStep"
        >
          <b-spinner
            v-if="isLoading"
            small
            class="mr-2"
          />
          <i
            v-else
            class="fas fa-play mr-2"
          />
          {{ isLoading ? 'Generating...' : 'Generate Reports' }}
        </b-button>
      </div>
    </div>
    <SelectFolderModal
      :mode="selectFolderMode"
      :view-tabs="selectFolderModal.viewTabs"
      :modal-title="selectFolderModal.modalTitle"
      @onSelectedItems="fileBrowserItemSelected"
    />
    <SelectLayoutModal
      @onSelectedItems="layoutItemSelected"
    />
    <ExportToLayoutModal
      :show="showExportToLayoutModal"
      @onExportToLayout="handleExportToLayout"
    />
    <ExternalAssetsView
      :source="externalAssetsSource"
      @onSelectedAssets="handleExternalAssetsSelected"
    />
    <b-modal
      id="export-status"
      :title="isExporting ? 'Copying Images To Layout...' : 'Images Copied'"

      ok-only
      :ok-disabled="isExporting"
      :hide-footer="isExporting"
    >
      <div class="text-center">
        <b-spinner
          v-if="isExporting"
          variant="primary"
          class="spinner-custom"
        />
        <div v-if="!isExporting">
          <div>
            All images were successfully copied to the layout.
          </div>
          <b-button
            v-if="layoutURL"
            variant="primary"
            class="mt-2"
            :href="layoutURL"
          >
            View Layout
          </b-button>
        </div>
      </div>
    </b-modal>
    <!-- Previous Report Selection Modal -->
    <b-modal
      id="previous-report-modal"
      title="Incorporate previous report"
      centered
      ok-title="Select"
      cancel-title="Cancel"
      :ok-disabled="!tempSelectedPreviousReportId"
      @ok="confirmPreviousReportSelection"
      @show="tempSelectedPreviousReportId = selectedPreviousReportId"
    >
      <div>
        <Multiselect
          :value="previousReports.find(r => r.id === tempSelectedPreviousReportId)"
          :options="previousReports"
          track-by="id"
          label="name"
          placeholder="Select a previous report"
          select-label=""
          deselect-label=""
          :searchable="false"
          :allow-empty="true"
          @input="val => { tempSelectedPreviousReportId = val ? val.id : null; }"
        >
          <template
            slot="option"
            slot-scope="props"
          >
            <div class="d-flex align-items-center">
              <div
                class="mr-2 rounded border overflow-hidden d-flex align-items-center justify-content-center bg-light"
                style="width: 30px; height: 30px; min-width: 30px;"
              >
                <img
                  v-if="props.option.thumbnail"
                  :src="props.option.thumbnail"
                  style="width: 100%; height: 100%; object-fit: cover;"
                >
                <i
                  v-else
                  class="bi bi-file-text text-muted"
                />
              </div>
              <div style="overflow: hidden;">
                <div class="text-truncate">
                  {{ props.option.name }}
                </div>
              </div>
            </div>
          </template>
          <template
            slot="singleLabel"
            slot-scope="props"
          >
            <div class="d-flex align-items-center">
              <div
                class="mr-2 rounded border overflow-hidden d-flex align-items-center justify-content-center bg-light"
                style="width: 20px; height: 20px; min-width: 20px;"
              >
                <img
                  v-if="props.option.thumbnail"
                  :src="props.option.thumbnail"
                  style="width: 100%; height: 100%; object-fit: cover;"
                >
                <i
                  v-else
                  class="bi bi-file-text text-muted small"
                />
              </div>
              <span class="text-truncate">{{ props.option.name }}</span>
            </div>
          </template>
        </Multiselect>

        <!-- Selected report details -->
        <div
          v-if="tempSelectedPreviousReportId && selectedPreviousReport"
          class="mt-3 p-3 bg-light rounded border"
        >
          <div class="d-flex">
            <div
              class="mr-3 rounded border overflow-hidden d-flex align-items-center justify-content-center bg-white"
              style="width: 80px; height: 80px; min-width: 80px;"
            >
              <img
                v-if="selectedPreviousReport.thumbnail"
                :src="selectedPreviousReport.thumbnail"
                style="width: 100%; height: 100%; object-fit: cover;"
              >
              <i
                v-else
                class="bi bi-file-text text-muted h3 mb-0"
              />
            </div>
            <div style="overflow: hidden; flex: 1;">
              <div
                class="font-weight-bold mb-1"
                :title="selectedPreviousReport.name"
              >
                {{ selectedPreviousReport.name }}
              </div>
              <div class="text-muted small">
                <i class="bi bi-calendar mr-1" />
                {{ selectedPreviousReport.created_at }}
              </div>
            </div>
          </div>
        </div>

        <!-- Clear selection link -->
        <div
          v-if="tempSelectedPreviousReportId || selectedPreviousReportId"
          class="mt-3"
        >
          <span
            role="button"
            class="text-danger cursor-pointer"
            @click="clearPreviousReportSelection"
          >
            <i class="bi bi-x-circle mr-1" />
            Clear selection
          </span>
        </div>
      </div>
    </b-modal>

    <!-- Page Screenshot Modal -->
    <b-modal
      id="page-screenshot-modal"
      title="Capture Page Screenshot"
      centered
      ok-title="Capture"
      cancel-title="Cancel"
      :ok-disabled="!screenshotUrl || isCapturingScreenshot"
      @ok="capturePageScreenshot"
      @hidden="resetScreenshotModal"
    >
      <div>
        <b-form-group
          label="Enter the webpage URL or Amazon ASIN"
          description="The full page will be captured as an image"
        >
          <b-form-input
            v-model="screenshotUrl"
            placeholder="https://example.com/product-page or B000XDIEDE"
            :disabled="isCapturingScreenshot"
            @keyup.enter="capturePageScreenshot"
          />
        </b-form-group>

        <div
          class="advanced-options-toggle mt-3"
          @click="showScreenshotAdvanced = !showScreenshotAdvanced"
        >
          <i
            class="fas fa-chevron-right mr-2"
            :class="{ 'rotate-90': showScreenshotAdvanced }"
          />
          <span>Advanced Options</span>
        </div>

        <b-collapse v-model="showScreenshotAdvanced">
          <div class="mt-2">
            <b-form-checkbox
              v-model="screenshotAsChunks"
              :disabled="isCapturingScreenshot"
            >
              Capture as chunks (split long page into multiple images)
            </b-form-checkbox>

            <b-collapse :visible="screenshotAsChunks">
              <div class="p-2 mt-2 border rounded">
                <b-form-row>
                  <b-col
                    cols="12"
                    md="4"
                  >
                    <b-form-group label="Chunk height (px)">
                      <b-form-input
                        v-model.number="screenshotChunkHeight"
                        type="number"
                        min="300"
                        max="3000"
                        step="50"
                        :disabled="isCapturingScreenshot"
                      />
                    </b-form-group>
                  </b-col>

                  <b-col
                    cols="12"
                    md="4"
                  >
                    <b-form-group label="Overlap (px)">
                      <b-form-input
                        v-model.number="screenshotChunkOverlap"
                        type="number"
                        min="0"
                        max="800"
                        step="10"
                        :disabled="isCapturingScreenshot"
                      />
                    </b-form-group>
                  </b-col>

                  <b-col
                    cols="12"
                    md="4"
                  >
                    <b-form-group label="Max chunks">
                      <b-form-input
                        v-model.number="screenshotMaxChunks"
                        type="number"
                        min="1"
                        max="50"
                        step="1"
                        :disabled="isCapturingScreenshot"
                      />
                    </b-form-group>
                  </b-col>
                </b-form-row>

                <small class="text-muted">
                  Tip: overlap helps if elements are cut between chunks.
                </small>
              </div>
            </b-collapse>

            <b-form-checkbox
              v-model="smartScreenshot"
              :disabled="isCapturingScreenshot"
              class="mt-2"
            >
              Smart Screenshot
            </b-form-checkbox>

            <b-form-checkbox
              v-if="screenshotUrl && detectRetailer(screenshotUrl)"
              v-model="parsePdpImages"
              :disabled="isCapturingScreenshot"
              class="mt-2"
            >
              Parse PDP Images (will be attached to the report)
            </b-form-checkbox>
          </div>
        </b-collapse>

        <!-- Loading -->
        <div
          v-if="isCapturingScreenshot"
          class="text-center mt-3"
        >
          <b-spinner
            small
            class="mr-2"
          />
          Capturing screenshot...
        </div>
      </div>
    </b-modal>

    <div
      v-if="showLightbox"
      class="lightbox-backdrop"
      @click="closeLightbox"
    >
      <div class="lightbox-header">
        <button
          type="button"
          title="Download"
          class="lightbox-download"
          @click.stop="downloadLightboxImage"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="lucide-icon lucide lucide-download"
          >
            <path d="M12 15V3" />
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <path d="m7 10 5 5 5-5" />
          </svg>
        </button>
        <button
          type="button"
          class="lightbox-close"
          @click.stop="closeLightbox"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="lucide-icon lucide lucide-x"
          >
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
          </svg>
        </button>
      </div>
      <button
        v-if="lightboxImages.length > 1 && currentLightboxIndex > 0"
        type="button"
        class="lightbox-nav lightbox-prev"
        @click.stop="prevImage"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="32"
          height="32"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <polyline points="15 18 9 12 15 6" />
        </svg>
      </button>

      <button
        v-if="lightboxImages.length > 1 && currentLightboxIndex < lightboxImages.length - 1"
        type="button"
        class="lightbox-nav lightbox-next"
        @click.stop="nextImage"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="32"
          height="32"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </button>

      <div
        class="lightbox-modal"
        @click.stop
      >
        <img
          v-if="lightboxImage"
          :src="lightboxImage"
          alt="Preview"
          class="lightbox-image"
          draggable="false"
          @dragstart.prevent
        >
      </div>

      <div
        v-if="lightboxImages.length > 1"
        class="lightbox-counter"
      >
        {{ currentLightboxIndex + 1 }} / {{ lightboxImages.length }}
      </div>
    </div>
  </div>
</template>
      
<script>
/* eslint vue/enforce-style-attribute: 0 */
import {mapGetters} from "vuex";
import Vue2Dropzone from "vue2-dropzone";
import "vue2-dropzone/dist/vue2Dropzone.min.css";
import SelectFolderModal from "@frontend/components/modules/file-browser/modals/SelectFolderModal.vue";
import ExportToLayoutModal from "@frontend/components/common/dashboard/modals/ExportToLayoutModal.vue";
import {uploadFile, uploadFolderFile} from '@frontend/services/file-uploader';
import Requests from "@frontend/services/api/file-browser";
import {
  IMPORT_INTO_EXISTS_TEMPLATE_STRATEGY
} from "@frontend/group/modules/import-template-from-psd/import-from-psd-new-template";
import {dataURLtoFile} from '@frontend/services/helpers';
import * as XLSX from 'xlsx';
import Multiselect from 'vue-multiselect';
import "vue-multiselect/dist/vue-multiselect.min.css";
import StepProgressBar from '../StepProgressBar.vue';
import SelectLayoutModal from "@frontend/components/modules/file-browser/modals/SelectLayoutModal.vue";
import ExternalAssetsView from "@frontend/components/modules/external-assets/ExternalAssetsView.vue";
import * as pdfjsLib from 'pdfjs-dist/build/pdf';
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;
import JSZip from 'jszip';
import {isNumeric} from "alpinejs/src/utils";
import AudiencePage from '@frontend/components/AudiencePage.vue'
import BrandGuidelines from '@frontend/components/BrandGuidelines.vue';
import SeasonalityModal from '@frontend/components/common/dashboard/components/SeasonalityModal.vue';

const CUSTOM_PROMPT_TYPE_ANALYTICS = 3;
const CUSTOM_PROMPT_TYPE_IMAGE_REORDER = 6;
const CUSTOM_PROMPT_TYPE_IMAGE_COMPARISON = 7;
export const SELECT_FOLDER_MODE = {
  DEFAULT: 'default',
  SELECT_AND_CREATE: 'selectAndCreate',
}
export default {
  name: "ImageAnalysis",

  components: {
    CommerceVisibilityReport: () => import('@/analytics/components/CommerceVisibilityReport.vue'),
    SelectLayoutModal,
    SelectFolderModal,
    Vue2Dropzone,
    ExportToLayoutModal,
    Multiselect,
    StepProgressBar,
    ExternalAssetsView,
    AudiencePage,
    BrandGuidelines,
    SeasonalityModal
  },
  
  props: {
    commerceVisibilityProps: { type: Object, default: () => ({}) },
    isOptixLab: {
      type: Boolean,
      default: false,
      required: false,
    },
    lastUrl: {
      type: String,
      default: '',
      required: false,
    },
    preFilter: {
      type: String,
      default: '',
      required: false,
    },
    preselectedImages: {
      type: Array,
      default: () => [],
      required: false,
    }
  },

  data: () => ({
    commerceVisibilityOpen: false,
    commerceVisibilityMounted: false,
    commerceVisibilityTabActive: true,
    commerceVisibilityInitialSelection: null,
    commerceVisibilitySelection: null,
    step: 2,
    viewMode: 'gallery',
    selectedPromptsListView: [],
    file: [],
    urlInput: "",
    manualItemUrl: "",
    multipleUrlsMode: false,
    multipleUrlsInput: "",
    comparisonMultipleMode: true,
    comparisonMultipleInput: "",
    urlToImagesMapping: {},
    selectedGroupId: null,
    availableGroups: [],
    isLoadingGroups: false,
    createNewGroup: false,
    newGroupName: "",
    isLoading: false,
    isCapturingScreenshot: false,
    isLoadingFileBrowserImages: false,
    fileBrowserImagesLoaded: 0,
    fileBrowserImagesTotal: 0,
    pendingTiffConversions: 0,
    screenshotUrl: '',
    isLoadingRerunImages: false,
    isAnalyzing: false,
    showLightbox: false,
    lightboxImage: null,
    lightboxImages: [],
    currentLightboxIndex: 0,
    selectedImageType: "pdp",
    selectedPromptId: null,
    imageTypeOptions: [
      { value: "banner", text: "Banner Ads" },
      { value: "pdp", text: "PDP Images" },
    ],
    dropzoneOptions: {
      url: "http",
      autoProcessQueue: false,
      addRemoveLinks: true,
      createImageThumbnails: true,
      acceptedFiles: "image/jpeg, image/png, image/gif, image/webp, image/avif, image/tiff, .tif, .tiff, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel.sheet.macroEnabled.12, text/csv, application/pdf",
      maxFiles: null,
      parallelUploads: 10,
      uploadMultiple: true,
      maxThumbnailFilesize: 50,
      // Dropzone's own thumbnail generator reuses its success callback as the <img> element's
      // onerror handler, so a browser-undecodable source (e.g. TIFF) invokes it with the raw
      // error Event instead of a data URL. Its default "thumbnail" handler then does
      // `thumbnailElement.src = dataUrl` unconditionally, which the browser coerces to the
      // string "[object Event]" and requests as a URL. This override reproduces the default
      // behavior but skips the broken assignment when dataUrl isn't a usable string, leaving the
      // generic file icon in place instead.
      thumbnail: function (file, dataUrl) {
        if (!file.previewElement || typeof dataUrl !== 'string') {
          return;
        }

        file.previewElement.classList.remove('dz-file-preview');

        file.previewElement.querySelectorAll('[data-dz-thumbnail]').forEach((thumbnailElement) => {
          thumbnailElement.alt = file.name;
          thumbnailElement.src = dataUrl;
        });

        setTimeout(() => {
          file.previewElement.classList.add('dz-image-preview');
        }, 1);
      },
      dictDefaultMessage: `
      <div class="dropzone-message">
        <div class="dropzone-icon">
          <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" y1="3" x2="12" y2="15"></line></svg>
        </div>
        <p class="dropzone-text">
          Drop images here or <a href="#" onclick="event.preventDefault()">browse</a>
        </p>
        <p class="dropzone-subtext">
          PNG, JPG, WebP, TIFF · or paste from clipboard
        </p>
      </div>
    `,
      dictMaxFilesExceeded: null,
    },
    imageAPreviewUrl: null,
    imageBPreviewUrl: null,
    selectedExportFormat: "pdf",
    analysisReportUrl: null,
    analysisError: false,
    errorMessage: null,
    isRedirecting: false,
    isSavingSettings: false,
    isLoadingSettings: false,
    hasSavedSettings: false,
    isSaveSettings: true,
    isReduceImageQuality: true,
    isApplyEachVariation: false,
    isAdvancedOpen: false,
    isImageTypeLabelsOpen: false,
    imageTypeLabels: {},
    isDetectingPdfImageLabels: false,
    exportFormatOptions: [
      { value: "pdf", text: "PDF Document (.pdf)" },
      { value: "docx", text: "Word Document (.docx)" },
      { value: "xlsx", text: "Excel Spreadsheet (.xlsx)" },
    ],
    customPrompts: ["loading"],
    selectFolderMode: "default",
    showExportToLayoutModal: false,
    isExporting: false,
    layoutURL: null,
    externalAssetsSource: 'bynder',
    isBynderAvailable: false,
    fromUrl: false,
    urlsToScrape: [],
    searchKeyword: '',
    visualBenchmarkReportName: '',
    isEditingReportName: false,
    includeBrands: '',
    excludeBrands: '',
    countResults: 4,
    productUrls: [''], // Array to hold multiple product URLs
    productUrlsText: '', // Text content for textarea binding
    isUpdatingFromTextarea: false, // Flag to prevent infinite loops
    allProductUrls: [], // Track all URLs entered (including excess)
    categoryUrls: [''], // Array to hold multiple category URLs
    categoryUrlsText: '', // Text content for category textarea binding
    isUpdatingCategoryFromTextarea: false, // Flag to prevent infinite loops
    inputMode: 'search', // 'search', 'category', or 'products'
    retailers: [
      {
        id: 'amazon',
        name: 'Amazon',
      },
      {
        id: 'kroger',
        name: 'Kroger',
      },
      // {
      //   id: 'target',
      //   name: 'Target',
      // },
      {
        id: 'walmart',
        name: 'Walmart',
      },
      {
        id: 'wegmans',
        name: 'Wegmans',
      },
      {
        id: 'shoprite',
        name: 'ShopRite',
      },
      {
        id: 'giantfoodstores',
        name: 'Giant Food Stores',
      },
      {
        id: 'instacart',
        name: 'Instacart',
      }
    ],
    selectedRetailer: null,
    selectedRetailerPrompt: null,
    retailerList: [
      { value: 'amazon', label: 'Amazon' },
      { value: 'chemistwarehouse', label: 'Chemist Warehouse' },
      { value: 'coles', label: 'Coles' },
      { value: 'giantfoodstores', label: 'Giant Food Stores' },
      { value: 'homedepot', label: 'Home Depot' },
      { value: 'kroger', label: 'Kroger' },
      { value: 'lowes', label: "Lowe's" },
      { value: 'mercadolibre', label: 'Mercado Libre' },
      { value: 'shoprite', label: 'ShopRite' },
      { value: 'staples', label: 'Staples' },
      { value: 'target', label: 'Target' },
      { value: 'walmart', label: 'Walmart' },
      { value: 'wegmans', label: 'Wegmans' },
      { value: 'woolworths', label: 'Woolworths' }
    ],
    draggedElement: null,
    draggedIndex: null,
    dropzoneObserver: null,
    reinitializeTimeout: null,
    maxImagesPerProduct: 7,
    analysisReports: [],
    isMultiReports: false,
    isBackgroundJob: true,
    rerunData: null,
    isRerunMode: false,
    loadingStatusText: 'Preparing to run...',
    loadingStatusHelperText: '',
    showLoadingStatus: false,
    enableSpecificImages: true,
    specificImageNumbers: "",
    previousAutoPopulatedUrl: "",
    availableProductLists: [],
    isLoadingProductLists: false,
    selectedProductLists: [],
    availableProducts: [],
    selectedProducts: [],
    previousReports: [],
    selectedPreviousReportId: null,
    tempSelectedPreviousReportId: null,
    isLoadingPreviousReports: false,
    selectedProductRecordIds: [],
    salsifyProperties: [],
    currentProductRecordId: null,
    isLoadingProducts: false,
    productListProducts: [],
    productIdMapping: {},
    selectedColumnFilter: 'all',
    selectedPromptIds: [],
    backgroundProcesses: [], // [{ id, name?, status?, progress? (0..1) }]
    pollTimer: null,
    lastUrlData: null,
    isPolling: false,
    pollIntervalMs: 500,
    pollInFlight: false,
    reportsProcessed: 0,
    isLastMultipleUrl: false,
    collectedReportIds: [],
    activeTab: 0,
    showManualUrl: false,
    compareWithProductUrl: false,
    comparisonProductUrl: '',
    reportsParsed:[],
    presets: [],
    showAudiencesList: false,
    audiences: [],
    selectedAudiences: [],
    isLoadingAudiences: false,
    promptsSearchQuery: '',
    promptsTagFilter: '',
    showAudienceModalFlag: false,
    audienceModalMode: 'create', // 'create' or 'edit'
    showDeleteConfirmModal: false,
    creatingFromAnalysisFlow: false,
    showBrandGuidelinesList: false,
    brandGuidelines: [],
    selectedBrandGuidelines: [],
    isLoadingBrandGuidelines: false,
    creatingBrandGuidelineFromAnalysisFlow: false,
    isBrandGuidelinesEditMode: false,
    preselectedPrompt: null,
    isSalsifyConnected: false,
    isBynderConnected: false,
    showPimDamDropdown: false,
    customerName: '',
    processedUrl: '',
    currentProcess: '',
    fakePercents: 0,
    fakeTimer: null,
    fakeSpeedMultiplier: 0.3, // speed multiplier (1 = normal, 2 = faster, 0.5 = slower)
    currentItemProcessing: 0,
    overallProgressPercent: 0,
    useDomainAsClientName: false,
    screenshotAsChunks: false,
    screenshotChunkHeight: 1200,
    screenshotChunkOverlap: 120,
    screenshotMaxChunks: 20,
    parsePdpImages: false,
    parsingPdpAllowed: false,
    smartScreenshot: false,
    showScreenshotAdvanced: false,
    selectFolderModal: {
      mode: 'default',
      viewTabs: false,
      modalTitle: 'Select Images',
    },
    shopperInsightsFormValues: {
      brand_name: '',
      market: '',
      category: '',
      product_lines: '',
      objective: '',
      competitors: '',
      adjacent_categories: '',
      current_situation: ''
    },
    editablePromptFields: {},
    editableKpiSettingsExpanded: false,
    isRerunRequest: false,
    seasonalities: [],
    selectedSeasonalities: [],
    isLoadingSeasonalities: false,
    showSeasonalityModal: false,
  }),

  computed: {
    ...mapGetters("dashboard", ["user"]),
    selectedPreviousReport() {
      if (!this.tempSelectedPreviousReportId) return null;
      return this.previousReports.find(r => r.id === this.tempSelectedPreviousReportId);
    },
    hasLastUrl() {
      return !!(this.lastUrl || this.lastUrlData);
    },
    stepTitle() {
      const titles = {
        2: ('Optix Lab' + (this.isRerunRequest ? ' - Run New' : '')),
        3: this.viewMode === 'list'
          ? ('Optix Lab' + (this.isRerunRequest ? ' - Run New' : ''))
          : (this.selectedPrompts.length > 0
            ? (this.selectedPrompts[0].display_name || this.selectedPrompts[0].name)
            : 'Optix Lab'),
        4: 'Generating Report'
      }
      return titles[this.step] || ''
    },
    stepDescription() {
      if (this.step === 2) {
        return 'Test content with A/B comparisons and audience-specific analysis'
      }
      if (this.step === 3 && this.viewMode === 'list') {
        return 'Test content with A/B comparisons and audience-specific analysis'
      }
      if (this.step === 3 && this.selectedPrompts.length > 0) {
        return this.selectedPrompts[0].short_description || ''
      }
      if (this.step === 3) {
        return ''
      }
      return ''
    },
    hasMultipleUrls() {
      const urlText = this.multipleUrlsInput;

      if (urlText && urlText.trim()) {
        let regex = /[\s\n\r,]+/
        if (urlText.includes('rossmann.pl')) {
          regex = /[\s\n\r]+/
        }
        const urls = urlText.split(regex).filter(url => url.trim());
        return urls.length >= 1;
      }
      return false;
    },
    
    urls() {
      const urlText = this.multipleUrlsInput;

      if (urlText && urlText.trim()) {
        let regex = /[\s\n\r,]+/
        if (urlText.includes('rossmann.pl')) {
          regex = /[\s\n\r]+/
        }
        return urlText.split(regex).filter(url => url.trim());
      }
      return [];
    },
    
    canManageGroups() {
      if (!this.user || !this.user.id) {
          return false;
      }
      
      return this.user.id === 1 || // Master Admin has ID 1
             this.user.can_add_reports_to_groups === true || 
             this.user.can_add_reports_to_groups === 1 ||
             this.user.can_add_reports_to_groups_access === true ||
             this.user.can_add_reports_to_groups_access === 1
    },

    nextButtonDisabled() {
      switch (this.step) {
        case 1:
          return !this.selectedImageType;
        case 2:
          return !this.selectedPrompts.length;
        case 3:
          if (this.isLoadingFileBrowserImages) {
            return true;
          }
          if (this.pendingTiffConversions > 0) {
            return true;
          }
          if (this.isShopperInsightsReport()){
              return false;
          }
          if (this.isImageComparisonAnalysis() && this.activeTab === 1){
              return (!this.salsifyProperties.length > 0);
          }
          if (this.viewMode === 'list' && this.selectedPromptsListView.length === 0) {
            return true;
          }
          if (this.selectedPrompts[0]?.pdp_title_and_copy_assessment) {
            return !this.urlInput.trim();
          }

          if (this.isProductComparisonAnalysis() && this.compareWithProductUrl) {
            const files = this.enableSpecificImages && this.specificImageNumbers
              ? this.getFilteredFiles()
              : (Array.isArray(this.file) ? this.file : []);

            return files.length < 1 || !this.comparisonProductUrl.trim();
          }

          if (this.isComparisonAnalysis()) {
            let hasUrls = false;
            const urls = this.comparisonMultipleInput.split(/[\s\n\r]+/).filter(url => url.trim());
            hasUrls = urls.length >= 1;

            const hasEnoughFiles = Array.isArray(this.file) ? this.file.length >= 1 : false;
            const hasSelectedProducts = this.selectedProducts && this.selectedProducts.length > 0;

            if (this.enableSpecificImages && this.specificImageNumbers) {
              const filteredFiles = this.getFilteredFiles();
              const hasEnoughFilteredFiles = filteredFiles.length >= 1;
              return !hasUrls && !hasEnoughFilteredFiles && !hasSelectedProducts;
            }

            return !hasUrls && !hasEnoughFiles && !hasSelectedProducts;
          } else if (this.isVisualPlayBookAnalysis()) {
            if (this.inputMode === 'search') {
              return !this.searchKeyword || !this.selectedRetailer || !this.countResults;
            } else if (this.inputMode === 'category') {
              const hasValidCategoryUrls = this.categoryUrls.some(url => url.trim() !== '');
              return !hasValidCategoryUrls || !this.countResults;
            } else if (this.inputMode === 'products') {
              const hasValidProductUrls = this.productUrls.some(url => url.trim() !== '');
              const hasProductListSelection = this.selectedProducts && this.selectedProducts.length > 0;
              return !hasValidProductUrls && !hasProductListSelection;
            }
            return true;
          } else {
            const hasUrl = this.multipleUrlsInput.trim();
            const hasSelectedProducts = this.selectedProducts && this.selectedProducts.length > 0;
            const hasFiles = Array.isArray(this.file) ? this.file.length > 0 : !!this.file;

            if (hasUrl) {
              return false;
            }
            if (hasSelectedProducts) {
              return false;
            }
            if (Array.isArray(this.file)) {
              return this.file.length === 0;
            }
            return !this.file;
          }
        default:
          return false;
      }
    },
    lastStep() {
      return 4;
    },
    uploadedFilesCount() {
      if (Array.isArray(this.file)) {
        return this.file.length;
      }
      return this.file ? 1 : 0;
    },
    hasUrlInput() {
      if (this.isComparisonAnalysis()) {
        return this.comparisonMultipleInput.trim();
      } else {
        return this.multipleUrlsInput.trim();
      }
    },
    selectedPrompt: {
      get () {
        if (!this.selectedPromptId || this.customPrompts[0] === 'loading') {
          return null;
        }
        return this.customPrompts.find(prompt => prompt.id === this.selectedPromptId);
      },
      set (val) {
        this.selectedPromptId = val?.id
      }
    },
    selectedPrompts: {
      get () {
        if(!this.selectedPromptIds.length) {
          return [];
        }
        return this.customPrompts.filter(prompt => this.selectedPromptIds.includes(prompt.id));
      },
      set (val) {
        this.selectedPromptIds = []
        if (val && Array.isArray(val)) {
          val.forEach(prompt => {
            if (prompt?.id) {
              this.selectedPromptIds.push(prompt.id);
            }
          })
        }
      }
    },
    promptContainsAudience() {
      if (!this.selectedPrompts.length || !this.selectedPrompts[0]?.prompt) {
        return false;
      }
      return /%audience%/i.test(this.selectedPrompts[0].prompt);
    },
    currentSettings() {
      return {
        selectedImageType: this.selectedImageType,
        selectedPromptId: this.selectedPromptIds,
        isReduceImageQuality: this.isReduceImageQuality,
        shopperInsightsFormValues: this.shopperInsightsFormValues
      };
    },
    dashboardPrompts() {
      if (this.customPrompts[0] === 'loading') {
        return [];
      }
      const prompts = this.customPrompts;
      let filteredPrompts = prompts.filter(prompt => {
        for (const p of prompts) {
          if (p.show_variations === false && Array.isArray(p.variations) && p.variations.includes(prompt.id)) {
            return false;
          }
        }
        return true;
      });

      if (this.promptsSearchQuery) {
        const query = this.promptsSearchQuery.toLowerCase();
        filteredPrompts = filteredPrompts.filter(prompt => {
          const name = (prompt.name || '').toLowerCase();
          const displayName = (prompt.display_name || '').toLowerCase();
          const description = (prompt.short_description || '').toLowerCase();
          return name.includes(query) || displayName.includes(query) || description.includes(query);
        });
      }

      if (this.promptsTagFilter) {
        filteredPrompts = filteredPrompts.filter(prompt => {
          return prompt.tag === this.promptsTagFilter;
        });
      }

      const mapped = filteredPrompts.map(p => ({ ...p, $isDisabled: this.isDisabled(p) }));
      // Selected prompts float to the top
      const selectedIds = new Set(this.selectedPromptsListView.map(p => p.id));
      return mapped.sort((a, b) => {
        const aSelected = selectedIds.has(a.id) ? 0 : 1;
        const bSelected = selectedIds.has(b.id) ? 0 : 1;
        return aSelected - bSelected;
      });
    },
    availableTags() {
      if (this.customPrompts[0] === 'loading') {
        return [];
      }
      const tags = this.customPrompts
        .filter(p => p.tag)
        .map(p => p.tag);
      if (tags.indexOf('Popular') > -1) {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.promptsTagFilter = 'Popular';
      }
      return [...new Set(tags)].sort();
    },
    promptOptions() {
      if (this.customPrompts[0] === 'loading') {
        return [];
      }

      let prompts = this.customPrompts.filter(prompt => !prompt.launch_url);
      
      const filteredPrompts = prompts.filter(prompt => {
        // Find if this prompt should be removed
        for (const p of prompts) {
          if (
            p.show_variations === false &&
            Array.isArray(p.variations) &&
            p.variations.includes(prompt.id)
          ) {
            return false; // remove this prompt
          }
        }
        return true; // keep this prompt
      });

      return filteredPrompts.map(prompt => ({
        id: prompt.id,
        name: this.isPromptAParent(prompt) ? prompt.name : "└─ "+prompt.name,
        $isDisabled: this.isDisabled(prompt),
      }));

    },
    isImageCrawlingError() {
      return this.errorMessage === "Cannot find any images";
    },
    selectedGroupObj: {
      get () {
        return _.find(this.availableGroups, ['id', this.selectedGroupId]);
      },
      set (group) {
        this.selectedGroupId = group?.id;
      }
    },
    totalJobs() {
      return this.backgroundProcesses.length;
    },
    completedJobs() {
      // succeeded|failed|cancelled
      const terminal = new Set(['succeeded', 'completed', 'failed', 'cancelled']);
      return this.backgroundProcesses.filter(j => terminal.has(this.normStatus(j.status))).length;
    },
    availableColumnFilters() {
      const columns = [
        { value: 'all', text: 'All' },
      ];

      const columnFields = [
        { field: 'material_code', label: 'Material Code' },
        { field: 'gtin', label: 'GTIN' },
        { field: 'asin', label: 'ASIN' },
        { field: 'tcin', label: 'TCIN' },
        { field: 'walmart_id', label: 'Walmart ID' },
        { field: 'salsify_id', label: 'Salsify ID' },
      ];

      columnFields.forEach(col => {
        const hasValue = this.availableProducts.some(product => {
          const value = product[col.field];

          return value && value.toString().trim() !== '';
        });

        if (hasValue) {
          columns.push({ value: col.field, text: col.label });
        }
      });

      return columns;
    },

    filteredProducts() {
      if (this.selectedColumnFilter === 'all') {
        return this.availableProducts;
      }

      return this.availableProducts.filter(product => {
        const value = product[this.selectedColumnFilter];

        return value && value.toString().trim() !== '';
      }).map(product => {
        return {
          ...product,
          display_name: product[this.selectedColumnFilter],
        };
      });
    },
    allPromptsSelectedListView() {
      const selectable = this.dashboardPrompts.filter(prompt => !prompt.launch_url);
      return selectable.length > 0 && selectable.every(prompt => this.isPromptSelectedListView(prompt));
    },
    canGenerateListViewReport() {
      if (this.selectedPromptsListView.length === 0) {
        return false;
      }
      const hasFiles = Array.isArray(this.file) && this.file.length > 0;
      const hasUrls = this.multipleUrlsInput && this.multipleUrlsInput.trim().length > 0;
      const hasProducts = this.selectedProducts && this.selectedProducts.length > 0;
      return hasFiles || hasUrls || hasProducts;
    },
    selectedPromptEditableFieldsKey() {
      return this.selectedPrompts[0]?.id || null;
    },

    selectedPromptEditableTables() {
      const key = this.selectedPromptEditableFieldsKey;

      if (!key || !this.editablePromptFields[key]) {
        return [];
      }

      return this.editablePromptFields[key].tables || [];
    },
    isConfigViewOpen() {
      return this.showAudiencesList || this.showBrandGuidelinesList;
    },
    uploadedImageFiles() {
      if (Array.isArray(this.file)) {
        return this.file;
      }

      return this.file ? [this.file] : [];
    },
    hasImageTypeLabels() {
      return Object.values(this.imageTypeLabels || {}).some(value => String(value || '').trim());
    },
  },

  watch: {
    comparisonProductUrl(val) {
      this.processTextareaUrls(val, (processedText) => {
        if (processedText !== val) {
          this.comparisonProductUrl = processedText;
        }
      });
    },
    screenshotUrl(newVal) {
      if (newVal) {
        const trimmed = newVal.trim();
        const asinPattern = /^[A-Z0-9]{10}$/i;
        if (asinPattern.test(trimmed)) {
          this.screenshotUrl = `https://www.amazon.com/dp/${trimmed.toUpperCase()}`;
        }
        
        if (this.detectRetailer(newVal)) {
          this.parsePdpImages = true;
          this.smartScreenshot = true;
        }
      }
    },
    selectedPromptsListView(newVal) {
      this.selectedPrompts = [...newVal];
    },
    selectedPromptIds: {
      async handler(newVal, oldVal) {
        // Fetch previous reports whenever a prompt is selected
        if (newVal && newVal.length > 0) {
            this.selectedPreviousReportId = null;
            this.fetchPreviousReports(newVal[0]);

            // Initialize report name for Visual Playbook analysis
            const newPrompt = this.customPrompts.find(p => p.id === newVal[0]);
            if (newPrompt) {
              this.hydrateEditablePromptFieldsForPrompt(newPrompt);
            }
            if (newPrompt && newPrompt.analysisFormat?.type === 'visual-playbook') {
              this.visualBenchmarkReportName = newPrompt.display_name || newPrompt.name || '';
            }

            // The Product Lists tab is hidden for this report (its builder
            // has no product-list path). If it was left active from a
            // previously selected report, fall back to Upload Images so the
            // tab strip never lands on a hidden tab.
            if (newPrompt?.dimensions_report && this.activeTab === 2) {
              this.activeTab = 0;
            }
        } else {
            this.previousReports = [];
            this.selectedPreviousReportId = null;
        }

        if (newVal && newVal.length > 0 && oldVal && oldVal.length > 0) {
          const oldPrompt = this.customPrompts.find(p => p.id === oldVal[0]);
          const newPrompt = this.customPrompts.find(p => p.id === newVal[0]);

          if (oldPrompt && newPrompt) {
            if (newPrompt?.name === 'HomeHardware Report') {
              this.useDomainAsClientName = true;
            }
            const wasVisualPlaybook = oldPrompt.analysisFormat?.type === 'visual-playbook';
            const isVisualPlaybook = newPrompt.analysisFormat?.type === 'visual-playbook';

            if (wasVisualPlaybook !== isVisualPlaybook && this.selectedProductLists.length > 0) {
              if (isVisualPlaybook) {
                await this.loadProductsFromProductLists();
              } else {
                await this.loadProductsFromLists(false);
              }
            }
          }
        }
      },
      deep: true,
      immediate: true
    },

    urlInput: async function (val) {
      if (this.isVisualPlayBookAnalysis()) {
        if (val.includes('walmart.com')) {
          this.selectedRetailer = 'walmart';
        } else if (val.includes('amazon.com')) {
          this.selectedRetailer = 'amazon';
        } else if (val.includes('wegmans.com')) {
          this.selectedRetailer = 'wegmans';
        } else if (val.includes('shoprite.com')) {
          this.selectedRetailer = 'shoprite';
        } else if (val.includes('giantfoodstores.com')) {
          this.selectedRetailer = 'giant';
        } else if (val.includes('instacart.com')) {
          this.selectedRetailer = 'instacart';
        } else {
          this.selectedRetailer = null;
        } 
      }
      
      // Auto-populate manual item URL from urlInput
      this.updateManualItemUrl();
      
      // Load groups if URLs detected
      if (this.hasMultipleUrls && this.availableGroups.length === 0 && this.canManageGroups) {
        await this.loadGroups();
      }
    },
    
    multipleUrlsInput: function (val) {
      // Auto-populate manual item URL from multipleUrlsInput
      this.updateManualItemUrl();

      this.processTextareaUrls(val, (processedText) => {
        this.multipleUrlsInput = processedText;
      });
    },

    comparisonMultipleInput: function (val) {
      this.updateManualItemUrl();

      this.processTextareaUrls(val, (processedText) => {
        this.comparisonMultipleInput = processedText;
      });
    },

    productUrls: {
      handler(newUrls) {
        if (!this.isUpdatingFromTextarea) {
          const filteredUrls = newUrls.filter(url => url && url.trim());
          this.productUrlsText = filteredUrls.join('\n');
        }
      },
      deep: true
    },

    categoryUrls: {
      handler(newUrls) {
        if (!this.isUpdatingCategoryFromTextarea) {
          const filteredUrls = newUrls.filter(url => url && url.trim());
          this.categoryUrlsText = filteredUrls.join('\n');
          // Update urlInput with first category URL for backward compatibility
          if (filteredUrls.length > 0) {
            this.urlInput = filteredUrls[0];
          } else {
            this.urlInput = '';
          }
        }
      },
      deep: true
    },

    dashboardPrompts: {
      handler() {
        this.$nextTick(() => {
          this.initializeLucideIcons();
        });
      },
      deep: true
    },

    'backgroundProcesses.length'(len) {
      if (isNumeric(this.backgroundProcesses?.[0])) return;
      
      if (len > 0) this.startPolling();
      else this.stopPolling();
    },

    selectedColumnFilter(newValue) {
      if (newValue !== 'all' && this.selectedProducts.length > 0) {
        this.selectedProducts = this.selectedProducts.filter(product => {
          const value = product[newValue];

          return value && value.toString().trim() !== '';
        });

        this.populateSelectedProducts();
      }
    },
    file: {
      handler() {
        this.syncImageTypeLabels();
      },
      deep: true
    },
  },

  async mounted() {
    this.restoreCommerceVisibilityFromUrl();
    window.addEventListener('popstate', this.restoreCommerceVisibilityFromUrl);
    window.addEventListener('optix:tab-shown', this.handleCommerceVisibilityTab);
    let urlParams = new URLSearchParams(location.search);
    this.preselectedPrompt = urlParams.get('promptID') || urlParams.get('prompt');
    const isRerunRequest = urlParams.get('rerun') === 'true' || (window.location.hash || '').includes('rerun=true');
    let initialRerunData = null;
    if (isRerunRequest) {
      try {
        initialRerunData = JSON.parse(localStorage.getItem('rerunData'));
      } catch (error) {
        console.error('Error parsing initial rerun data:', error);
      }
    }
    const isRunNewRequest = isRerunRequest && initialRerunData && !initialRerunData.promptId;
    await this.$store.dispatch("dashboard/fetchUserData");
    this.loadCustomerName();
    await this.checkBynderAvailability();
    await this.loadSavedSettings(isRerunRequest);
    await this.loadCustomPrompts();
    await this.loadProductLists();

    if (this.canManageGroups) {
      await this.loadGroups();
    }

    this.checkForRerunMode();

    if (isRunNewRequest) {
      this.viewMode = 'gallery';
      this.step = 2;
      this.isRerunRequest = true;
      //update title to "Optix Lab - Run New"
    }

    if (this.isRerunMode && (!this.rerunData || !this.rerunData.promptId)) {
      this.selectedPromptIds = [];
      this.selectedPromptsListView = [];
      this.selectedPrompt = null;
    }

    this.checkURLForPromptID();

    this.checkForPreSelectedProductList();

    this.seedLastUrlFromProp();
    this.fetchLastUrl();

    if (this.$refs.uploadContainer) {
      this.$refs.uploadContainer.focus();
    }

    this.setupGlobalListeners();

    window.addEventListener(
      "unhandledrejection",
      this.handleUnhandledRejection
    );

    if (this.preFilter) {
      this.promptsSearchQuery = this.preFilter
    }

    window.addEventListener('optix-lab-rerun', this.handleOptixLabRerun);

    window.addEventListener('optix-lab-filter', (event) => {
      if (event.detail && event.detail.filter) {
        this.promptsTagFilter = '';
        this.promptsSearchQuery = event.detail.filter;
        this.$nextTick(() => {
          if (this.dashboardPrompts.length > 0 && this.dashboardPrompts[0].tag) {
            this.promptsTagFilter = this.dashboardPrompts[0].tag;
          }
        });
      }
    });

    window.addEventListener('optix-lab-tag-filter', (event) => {
      if (event.detail && event.detail.tag !== undefined) {
        this.promptsTagFilter = event.detail.tag;
        this.promptsSearchQuery = '';
      }
    });

    window.addEventListener('optix-lab-select-prompt', async (event) => {
      const { promptId, images, loadingImages, urls } = event.detail || {};

      if (loadingImages === true) {
        this.isLoadingRerunImages = true;
        return;
      }
      if (loadingImages === false) {
        this.isLoadingRerunImages = false;
      }

      if (promptId) {
        if (!this.customPrompts || this.customPrompts[0] === 'loading') {
          await this.loadCustomPrompts();
        }
        const allPrompts = this.customPrompts && this.customPrompts[0] !== 'loading' ? this.customPrompts : [];
        const prompt = allPrompts.find(p => String(p.id) === String(promptId));
        if (prompt) {
          if (this.step !== 2) {
            this.step = 2;
            if (this.$refs.fileInput) {
              this.$refs.fileInput.removeAllFiles(true);
            }
            this.file = [];
            await this.$nextTick();
          }
          if (prompt.tag !== this.promptsTagFilter) {
            this.promptsTagFilter = prompt.tag || '';
          }
          if (this.viewMode === 'list') {
            this.selectedPromptsListView = [prompt];
          }
          this.startAnalysisWithPrompt(prompt);
        }
      }

      if (urls && urls.length > 0) {
        await this.$nextTick();
        await new Promise(resolve => setTimeout(resolve, 100));
        if (this.$refs.fileInput) this.$refs.fileInput.removeAllFiles(true);
        this.file = [];
        this.activeTab = 1;
        this.multipleUrlsInput = urls.join('\n');
      } else if (images && images.length > 0) {
        await this.$nextTick();
        await new Promise(resolve => setTimeout(resolve, 100));
        this.multipleUrlsInput = '';
        this.activeTab = 0;
        for (const img of images) {
          try {
            const res = await fetch(img.dataUrl);
            const blob = await res.blob();
            const file = new File([blob], 'image.' + (img.type || 'image/png').split('/')[1], { type: img.type || 'image/png' });
            if (this.$refs.fileInput) this.$refs.fileInput.addFile(file);
          } catch (e) {
            console.error('Failed to preload image into Run Reports:', e);
          }
        }
      } else {
        await this.$nextTick();
        await new Promise(resolve => setTimeout(resolve, 100));
        this.multipleUrlsInput = '';
        if (this.$refs.fileInput) this.$refs.fileInput.removeAllFiles(true);
        this.file = [];
        this.activeTab = 0;
      }
    });

    window.addEventListener('optix-lab-select-prompt-type', (event) => {
      if (event.detail && event.detail.type) {
        const allPrompts = this.customPrompts && this.customPrompts[0] !== 'loading' ? this.customPrompts : [];
        const prompt = allPrompts.find(p => p.analysisFormat?.type === event.detail.type);
        if (prompt) {
          this.promptsSearchQuery = '';
          this.step = 2;
          this.selectedPromptIds = [];
          this.$nextTick(() => {
            this.startAnalysisWithPrompt(prompt);
          });
        }
      }
    });

    window.addEventListener('hashchange', this.handleHashChange);

    if (this.isOptixLab) {
      this.selectedImageType = 'pdp';

      const storedViewMode = localStorage.getItem('optix-lab-view-mode');
      if (!isRunNewRequest && storedViewMode && (storedViewMode === 'list' || storedViewMode === 'gallery')) {
        this.viewMode = storedViewMode;
      }

      if (isRunNewRequest) {
        this.viewMode = 'gallery';
        this.step = 2;
      } else if (this.viewMode === 'list' || this.isRerunMode) {
        this.step = 3;

        if (this.viewMode === 'list' && this.selectedPromptIds.length > 0) {
          const prompts = this.customPrompts.filter(p => this.selectedPromptIds.includes(p.id));
          if (prompts.length > 0) {
            this.selectedPromptsListView = prompts;

            const selectedPrompt = prompts[0];
            if (this.promptsTagFilter && selectedPrompt.tag !== this.promptsTagFilter) {
              this.promptsTagFilter = selectedPrompt.tag || '';
            }
          }
        }
      } else {
        this.step = 2;
      }

      window.toggleAudiencesViewGlobal = this.toggleAudiencesList;

      await this.loadAudiences();
      await this.loadBrandGuidelines();
      this.loadSeasonalities();

      this.$nextTick(() => {
        const button = document.getElementById('configureAudiencesButton');
        if (button) {
          button.classList.remove('text-muted');
          button.style.pointerEvents = 'auto';
          button.style.opacity = '1';
        }
      });
    }

    if (window.location.hash && window.location.hash === '#optix-lab') {
      this.presets = JSON.parse(localStorage.getItem('optix-lab-presets')) || [];

      const storedFilter = localStorage.getItem('optix-lab-filter');
      if (storedFilter) {
        this.promptsSearchQuery = storedFilter;
        localStorage.removeItem('optix-lab-filter');
      }
    }
    
    if (this.preselectedImages?.length) {
      this.presets = this.preselectedImages.map(preset => {
        return {
          thumbnail: preset.replace('/share?file=', ''),
          name: preset.split('/').pop(),
        }
      })
    }

    this.isSalsifyConnected = await this.checkPlatformAvailability('salsify');
    this.isBynderConnected = await this.checkPlatformAvailability('bynder');

    this.$nextTick(() => {
      this.setupDropzoneObserver();

      if (this.preselectedPrompt && !this.isRerunMode && !this.commerceVisibilityOpen) {
        const prompt = this.customPrompts.find(item => String(item.id) === String(this.preselectedPrompt));
        if (prompt?.launch_url) this.openStandaloneReport(prompt);
        else if (this.viewMode !== 'list') this.startAnalysisWithPrompt({id: +this.preselectedPrompt});
      }
    });

    this.$root.$on('optix:open-file-browser', this.handleOpenFileBrowser);

    // A/B hero handoff from Creative Studio: select the Image Comparison analysis and
    // load the two generated images as real comparison images. The "URLs/ASINs" field
    // SCRAPES its inputs as product pages, so raw image URLs there yield no images — we
    // upload them instead by reusing the existing `optix-lab-select-prompt` event, which
    // selects the prompt and adds the images as files on the Upload Images tab. (POR-13385)
    const isAbHandoff = new URLSearchParams(location.search).get('imageComparison') === 'true';
    if (isAbHandoff && this.isOptixLab) {
      let abData = null;
      try { abData = JSON.parse(localStorage.getItem('rerunData')); } catch (e) { abData = null; }
      if (abData && abData.isImageComparison) {
        const prompts = (this.customPrompts && this.customPrompts[0] !== 'loading') ? this.customPrompts : [];
        // Pick the "A/B Test - Primary Images" report deterministically — two generated
        // hero images = the product's PRIMARY image. Match by exact name so environments
        // with several A/B reports don't select an arbitrary one; fall back to any
        // Primary-Images A/B (excluding the KPI variant), then any compare-two-images
        // prompt, then the type-7 Image Comparison prompt. (POR-13385)
        const promptName = (p) => String((p && (p.name || p.display_name)) || '').replace(/^└─\s*/, '').trim().toLowerCase();
        const cmp = prompts.find((p) => promptName(p) === 'a/b test - primary images')
          || prompts.find((p) => /a\/b test\s*-\s*primary images/.test(promptName(p)) && !promptName(p).includes('kpi'))
          || prompts.find((p) => p.compare_two_images)
          || prompts.find((p) => String(p.type) === String(CUSTOM_PROMPT_TYPE_IMAGE_COMPARISON));
        const urls = Array.isArray(abData.imageUrls) && abData.imageUrls.length
          ? abData.imageUrls
          : String(abData.comparisonUrls || '').split(/[\s\n\r]+/).filter(Boolean);
        if (cmp && urls.length) {
          window.dispatchEvent(new CustomEvent('optix-lab-select-prompt', {
            detail: { promptId: cmp.id, images: urls.map((u) => ({ dataUrl: u, type: 'image/png' })) },
          }));
        }
        try { localStorage.removeItem('rerunData'); } catch (e) { /* ignore */ }
      }
    }
  },

  updated() {
    this.$nextTick(() => {
      if (window.lucide) {
        window.lucide.createIcons();
      }
    });
  },

  beforeDestroy() {
    window.removeEventListener('popstate', this.restoreCommerceVisibilityFromUrl);
    window.removeEventListener('optix:tab-shown', this.handleCommerceVisibilityTab);
    this.clearRerunData();
    this.removeGlobalListeners();

    window.removeEventListener(
      "unhandledrejection",
      this.handleUnhandledRejection
    );

    document.removeEventListener('click', this.closePimDamDropdown);

    // Remove Optix Lab rerun event listener
    window.removeEventListener('optix-lab-rerun', this.handleOptixLabRerun);

    // Remove hash change listener
    window.removeEventListener('hashchange', this.handleHashChange);

    if (window.toggleAudiencesViewGlobal) {
      window.toggleAudiencesViewGlobal = null;

      const button = document.getElementById('configureAudiencesButton');
      if (button) {
        button.classList.add('text-muted');
        button.style.pointerEvents = 'none';
        button.style.opacity = '0.6';
      }
    }

    if (this.imageAPreviewUrl && this.imageAPreviewUrl.startsWith("blob:")) {
      URL.revokeObjectURL(this.imageAPreviewUrl);
    }

    if (this.imageBPreviewUrl && this.imageBPreviewUrl.startsWith("blob:")) {
      URL.revokeObjectURL(this.imageBPreviewUrl);
    }

    if (this.dropzoneObserver) {
      this.dropzoneObserver.disconnect();
      this.dropzoneObserver = null;
    }

    this.cleanupAllDragAndDrop();
    this.stopPolling();
    this.$root.$off('optix:open-file-browser', this.handleOpenFileBrowser);
  },

  methods: {
    parseEditablePromptFields(prompt) {
      const promptText = prompt?.prompt ?? '';

      return {
        tables: this.parseEditableTableBlocks(promptText),
      };
    },

    parseEditableTableBlocks(promptText) {
      const blocks = [];
      const text = promptText || '';

      const regex = /@?\{\{editable\s+"([^"]+)"\s+type="(table|kpi_list)"\s*\}\}([\s\S]*?)@?\{\{\/editable\}\}/g;

      let match;
      while ((match = regex.exec(text)) !== null) {
        const label = match[1];
        const type = match[2];
        const content = match[3] || '';

        if (type === 'table') {
          const parsed = this.parseEditableKpiTableContent(content);

          if (parsed.kpis.length) {
            blocks.push({
              label,
              type,
              conditionOpen: parsed.conditionOpen,
              conditionClose: parsed.conditionClose,
              headers: parsed.headers,
              rows: parsed.rows,
              kpis: parsed.kpis,
            });
          }
        }

        if (type === 'kpi_list') {
          const parsedList = this.parseEditableKpiListContent(content);

          if (parsedList.kpis.length) {
            blocks.push({
              label,
              type,
              intro: parsedList.intro,
              kpis: parsedList.kpis,
            });
          }
        }
      }

      return blocks;
    },

    parseEditableKpiTableContent(content) {
      const evaluationMap = this.parseKpiEvaluationListMap(content);
      const markdownTable = this.extractFirstMarkdownTable(content);
      const conditionOpenMatch = content.match(/\{\{#if_[^}]+\}\}/);
      const conditionCloseMatch = content.match(/\{\{\/if_[^}]+\}\}/);

      if (!markdownTable) {
        return {
          conditionOpen: conditionOpenMatch ? conditionOpenMatch[0] : '',
          conditionClose: conditionCloseMatch ? conditionCloseMatch[0] : '',
          headers: [],
          rows: [],
          kpis: [],
        };
      }

      const lines = markdownTable
        .split('\n')
        .map(line => line.trim())
        .filter(Boolean);

      const headers = this.parseMarkdownTableRow(lines[0]);
      const rows = lines.slice(2).map(line => this.parseMarkdownTableRow(line));

      const kpis = [];

      rows.forEach((row, rowIndex) => {
        const criteria = row[0] || '';
        const lower = criteria.toLowerCase().trim();

        if (
          lower === 'type' ||
          lower.includes('conversion score') ||
          lower.includes('verdict')
        ) {
          return;
        }

        const parsed = this.splitKpiCriteria(criteria);
        const evaluationKey = this.normalizeKpiName(parsed.name);

        kpis.push({
          name: parsed.name,
          weight: parsed.weight,
          evaluation: evaluationMap[evaluationKey] || '',
          rowIndex,
        });
      });

      return {
        conditionOpen: conditionOpenMatch ? conditionOpenMatch[0] : '',
        conditionClose: conditionCloseMatch ? conditionCloseMatch[0] : '',
        headers,
        rows,
        kpis,
      };
    },

    parseEditableKpiListContent(content) {
      const lines = (content || '')
        .split('\n')
        .map(line => line.trim())
        .filter(Boolean);

      const kpis = [];
      const introLines = [];

      lines.forEach(line => {
        const match = line.match(/^[-*]\s+\*\*(.+?)\s*\((\d+(?:\.\d+)?)%\)\*\*:?\s*(.*)$/);

        if (!match) {
          if (!line.startsWith('-') && !line.startsWith('*')) {
            introLines.push(line);
          }

          return;
        }

        kpis.push({
          name: match[1].trim(),
          weight: Number(match[2]),
          evaluation: (match[3] || '').trim(),
        });
      });

      return {
        intro: introLines.join('\n'),
        kpis,
      };
    },

    parseKpiEvaluationListMap(content) {
      const map = {};
      const lines = (content || '').split('\n');

      lines.forEach(line => {
        const trimmed = line.trim();
        const match = trimmed.match(/^[-*]\s+\*\*(.+?)\s*\((\d+(?:\.\d+)?)%\)\*\*:?\s*(.*)$/);

        if (!match) {
          return;
        }

        const name = match[1].trim();
        const evaluation = (match[3] || '').trim();

        map[this.normalizeKpiName(name)] = evaluation;
      });

      return map;
    },

    extractFirstMarkdownTable(content) {
      const lines = (content || '').split('\n');
      const tableLines = [];
      let insideTable = false;

      lines.forEach(line => {
        const trimmed = line.trim();

        if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
          insideTable = true;
          tableLines.push(trimmed);
          return;
        }

        if (insideTable) {
          insideTable = false;
        }
      });

      if (tableLines.length < 3) {
        return '';
      }

      return tableLines.join('\n');
    },

    parseMarkdownTableRow(row) {
      return (row || '')
        .split('|')
        .map(cell => cell.trim())
        .filter((cell, index, arr) => index > 0 && index < arr.length - 1);
    },

    splitKpiCriteria(criteria) {
      const text = criteria || '';
      const match = text.match(/\((\d+(?:\.\d+)?)%\)/);

      return {
        name: text.replace(/\s*\(\d+(?:\.\d+)?%\)/, '').trim(),
        weight: match ? Number(match[1]) : null,
      };
    },

    normalizeKpiName(name) {
      return String(name || '')
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .trim();
    },

    hydrateEditablePromptFieldsForPrompt(prompt) {
      if (!prompt?.id) {
        return;
      }

      if (!this.editablePromptFields || typeof this.editablePromptFields !== 'object') {
        this.$set(this, 'editablePromptFields', {});
      }

      const parsed = this.parseEditablePromptFields(prompt);

      if (!parsed.tables.length) {
        if (this.editablePromptFields[prompt.id]) {
          this.$delete(this.editablePromptFields, prompt.id);
        }

        return;
      }

      this.$set(this.editablePromptFields, prompt.id, parsed);
    },

    getEditableTableKpiTotal(table) {
      if (!table || !Array.isArray(table.kpis)) {
        return 0;
      }

      return table.kpis.reduce((sum, item) => {
        const value = parseFloat(item.weight);

        return sum + (Number.isNaN(value) ? 0 : value);
      }, 0);
    },

    addEditableTableKpi(tableIndex) {
      const key = this.selectedPromptEditableFieldsKey;

      if (!key || !this.editablePromptFields[key]?.tables?.[tableIndex]) {
        return;
      }

      const table = this.editablePromptFields[key].tables[tableIndex];

      table.kpis.push({
        name: table.type === 'kpi_list' ? 'New KPI Item' : 'New KPI',
        weight: 0,
        evaluation: '',
      });

      if (table.type === 'table') {
        this.syncEditableTableRowsFromKpis(table);
      }
    },

    removeEditableTableKpi(tableIndex, kpiIndex) {
      const key = this.selectedPromptEditableFieldsKey;

      if (!key || !this.editablePromptFields[key]?.tables?.[tableIndex]) {
        return;
      }

      const table = this.editablePromptFields[key].tables[tableIndex];

      table.kpis.splice(kpiIndex, 1);

      if (table.type === 'table') {
        this.syncEditableTableRowsFromKpis(table);
      }
    },
    
    syncEditableTableRowsFromKpis(table) {
      if (!table || !Array.isArray(table.rows)) {
        return;
      }

      const headers = Array.isArray(table.headers) ? table.headers : [];
      const columnCount = Math.max(headers.length, 2);

      const makeRow = (firstCell, fillValue = '[Evaluation]') => {
        const row = [firstCell];

        for (let i = 1; i < columnCount; i++) {
          row.push(fillValue);
        }

        return row;
      };

      const normalizeLabel = value => String(value || '')
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .trim();

      const existingRows = Array.isArray(table.rows) ? table.rows : [];

      const typeRow = existingRows.find(row => normalizeLabel(row?.[0]) === 'type')
        || makeRow('Type', '[Insert]');

      const conversionScoreRow = existingRows.find(row => normalizeLabel(row?.[0]) === 'conversion score')
        || makeRow('Conversion Score', '[##]%');

      const verdictRow = existingRows.find(row => normalizeLabel(row?.[0]) === 'verdict')
        || makeRow('Verdict', '');

      if (verdictRow.length > 1 && !verdictRow[1]) {
        verdictRow[1] = '[Winner] wins because...';
      }

      const kpiRows = [];

      if (Array.isArray(table.kpis)) {
        table.kpis.forEach(kpi => {
          if (!kpi || typeof kpi !== 'object') {
            return;
          }

          const name = String(kpi.name || '').trim();

          if (!name) {
            return;
          }

          const weight = kpi.weight !== null && kpi.weight !== undefined && kpi.weight !== ''
            ? ` (${kpi.weight}%)`
            : '';

          const row = makeRow(`${name}${weight}`, '[Evaluation]');

          kpiRows.push(row);
        });
      }

      this.$set(table, 'rows', [
        typeRow,
        ...kpiRows,
        conversionScoreRow,
        verdictRow,
      ]);
    },

    syncAllEditableTablesRowsFromKpis() {
      const key = this.selectedPromptEditableFieldsKey;

      if (!key || !this.editablePromptFields[key]?.tables) {
        return;
      }

      this.editablePromptFields[key].tables.forEach(table => {
        if (table.type === 'table') {
          this.syncEditableTableRowsFromKpis(table);
        }
      });
    },
    async fetchPreviousReports(promptId) {
      if (!promptId) {
        this.previousReports = [];
        return;
      }
      
      this.isLoadingPreviousReports = true;
      try {
        const response = await axios.get('/analytics-history/recent-reports', {
          params: { prompt_id: promptId }
        });
        
        if (response.data && response.data.success) {
          this.previousReports = response.data.data;
        } else {
          this.previousReports = [];
        }
      } catch (error) {
        console.error('Failed to fetch previous reports:', error);
        this.previousReports = [];
      } finally {
        this.isLoadingPreviousReports = false;
      }
    },

    confirmPreviousReportSelection() {
      this.selectedPreviousReportId = this.tempSelectedPreviousReportId;
    },

    clearPreviousReportSelection() {
      this.tempSelectedPreviousReportId = null;
      this.selectedPreviousReportId = null;
    },

    openPreviousReportModal() {
      this.$bvModal.show('previous-report-modal');
    },

    updatePromptsSearchQuery(event) {
      this.promptsSearchQuery = event.target.value;
    },

    setViewMode(mode) {
      this.viewMode = mode;
      localStorage.setItem('optix-lab-view-mode', mode);
      if (mode === 'list') {
        this.step = 3;
      } else {
        this.step = 2;
        if (this.selectedPromptsListView.length > 0) {
          this.selectedPrompts = [...this.selectedPromptsListView];
        }
      }
    },
    togglePromptSelectionListView(prompt) {
      if (this.openStandaloneReport(prompt)) return;
      const index = this.selectedPromptsListView.findIndex(p => p.id === prompt.id);
      if (index > -1) {
        this.selectedPromptsListView.splice(index, 1);
      } else {
        this.selectedPromptsListView.push(prompt);
      }
    },
    isPromptSelectedListView(prompt) {
      return this.selectedPromptsListView.some(p => p.id === prompt.id);
    },
    toggleSelectAllPromptsListView() {
      if (this.allPromptsSelectedListView) {
        this.selectedPromptsListView = [];
      } else {
        this.selectedPromptsListView = this.dashboardPrompts.filter(prompt => !prompt.launch_url);
      }
    },
    clearAllPromptsListView() {
      this.selectedPromptsListView = [];
    },
    async generateListViewReports() {
      if (this.selectedPromptsListView.length === 0) {
        toastr.error('Please select at least one analysis type');
        return;
      }

      this.selectedPrompts = [...this.selectedPromptsListView];

      this.runAnalysis();
    },

    copyJobId(uuid) {
      navigator.clipboard.writeText(uuid).then(() => {
        toastr.success('Job ID copied to clipboard');
      }).catch(() => {
        toastr.error('Failed to copy Job ID');
      });
    },

    generateFakePercentage() {
      if (!this.totalJobs || this.totalJobs <= 0) return;

      // Clear previous timer if any
      if (this.fakeTimer) {
        clearInterval(this.fakeTimer);
        this.fakeTimer = null;
      }

      // Base target based on current item
      const rawTarget = (this.currentItemProcessing / this.totalJobs) * 100;
      const target = Math.min(99, rawTarget);

      // Start smooth fake animation towards target
      this.fakeTimer = setInterval(() => {
        // Minimal step so that progress always moves
        const minStep = 0.3 * this.fakeSpeedMultiplier;
        // Randomized step to look less linear
        const randomStep = (Math.random() * 1.7 + 0.3) * this.fakeSpeedMultiplier;
        const step = Math.max(minStep, randomStep);

        if (this.fakePercents < target) {
          this.fakePercents = +Math.min(target, this.fakePercents + step).toFixed(2);
        } else {
          // Reached current target for this item
          clearInterval(this.fakeTimer);
          this.fakeTimer = null;
        }
      }, 120); // interval in ms
    },

    // Call this when all jobs are really finished to push to 100%
    finishFakePercentage() {
      if (this.fakeTimer) {
        clearInterval(this.fakeTimer);
        this.fakeTimer = null;
      }

      this.fakeTimer = setInterval(() => {
        const step = 2.5 * this.fakeSpeedMultiplier;

        if (this.fakePercents < 100) {
          this.fakePercents = Math.min(100, this.fakePercents + step);
        } else {
          this.fakePercents = 100;
          clearInterval(this.fakeTimer);
          this.fakeTimer = null;
        }
      }, 80);
    },
    normStatus(s) {
      return String(s || 'queued').toLowerCase();
    },
    isJobRunning(job) {
      const s = this.normStatus(job.status);
      return s === 'running' || s === 'processing' || s === 'in_progress' || s === 'queued';
    },
    isJobSucceeded(job) {
      const s = this.normStatus(job.status);
      return s === 'succeeded' || s === 'completed' || s === 'done';
    },
    isJobFailed(job) {
      const s = this.normStatus(job.status);
      return s === 'failed' || s === 'error';
    },
    isJobCancelled(job) {
      const s = this.normStatus(job.status);
      return s === 'cancelled' || s === 'canceled' || s === 'aborted';
    },

    startPolling() {
      if (this.pollTimer) return;
      this.isPolling = true;
      this.pollOnce();
      this.pollTimer = setInterval(this.pollOnce, this.pollIntervalMs);
    },

    stopPolling() {
      this.isPolling = false;
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },

    async pollOnce() {
      if (!this.backgroundProcesses.length) {
        this.stopPolling();
        return;
      }
      if (this.pollInFlight) return;
      this.pollInFlight = true;

      try {
        const ids = this.backgroundProcesses.map(j => j.uuid);
        // { jobs: [{ id, status, progress? (0..1), meta? }] }
        const { data } = await axios.post('/analysis-reports/get-jobs-status', { ids });

        if (data && Array.isArray(data.jobs)) {
          this.mergeStatuses(data.jobs);
          
          data.jobs.forEach(job => {
            if (job?.analytic_history_ids && Array.isArray(job?.analytic_history_ids)) {
              this.reportsParsed.push(...job.analytic_history_ids)
            }
          })
        }

        this.updateOverallProgressPercent();

        if (this.completedJobs === this.totalJobs) {
          this.stopPolling();
          setTimeout(() => {
            if (this.reportsParsed.length) {
              this.redirectToAnalyticsHistory(this.reportsParsed, true)
            }
          }, 200)
          // this.$bvToast.toast('All background jobs finished', { title: 'Done', variant: 'success' });
        }
      } catch (e) {
        console.error(e);
      } finally {
        this.pollInFlight = false;
      }
    },

    mergeStatuses(serverJobs) {
      const byId = new Map(serverJobs.map(j => [j.id, j]));
      this.backgroundProcesses = this.backgroundProcesses.map(local => {
        const upd = byId.get(local.uuid);
        if (!upd) return local;
        return {
          ...local,
          status: upd.status ?? local.status,
          progress: typeof upd.progress === 'number' ? upd.progress : local.progress,
          links: upd.links ?? [],
        };
      });
    },

    updateOverallProgressPercent() {
      if (!this.totalJobs || !this.backgroundProcesses.length) return;
      const total = this.backgroundProcesses.reduce((acc, job) => acc + (job.status === "Completed" ? 100 : job.progress || 0), 0);
      this.overallProgressPercent = Math.min(100, Math.round(total / this.backgroundProcesses.length));
    },

    addBackgroundJob(job) {
      this.backgroundProcesses.push({ ...job, status: 'queued', progress: 0 });
    },
    isComparisonAnalysis(neededPrompt = null) {
      return this.selectedPrompts[0]?.compare_two_images || this.selectedPrompts[0]?.compare_two_products || (neededPrompt && (neededPrompt.compare_two_images || neededPrompt.compare_two_products));
    },
    isProductComparisonAnalysis(neededPrompt = null) {
      return this.selectedPrompts[0]?.compare_two_products || (neededPrompt && neededPrompt.compare_two_products);
    },
    isVisualPlayBookAnalysis(neededPrompt = null) {
      return this.selectedPrompts[0]?.analysisFormat?.type === 'visual-playbook' || (neededPrompt && neededPrompt.analysisFormat?.type === 'visual-playbook');
    },
    isImageOrderAnalysis() {
      return this.selectedPrompts[0]?.type == CUSTOM_PROMPT_TYPE_IMAGE_REORDER;
    },
    isImageComparisonAnalysis() {
      return this.selectedPrompts[0]?.type == CUSTOM_PROMPT_TYPE_IMAGE_COMPARISON;
    },
    isDimensionsReport() {
      // Deliberately selectedPrompts[0] (matching every sibling isXAnalysis()
      // method here), NOT the singular `selectedPrompt`. `selectedPrompt` is
      // only populated during the step-4 submission loop
      // (this.selectedPrompt = this.selectedPrompts[key]) - at step 3, where
      // this form-collapsing logic runs, it is still null (or stale from a
      // previous report), which was verified live to silently defeat every
      // v-if gated on this method. selectedPromptIds/selectedPrompts is
      // always freshly reset by startAnalysisWithPrompt() and the list-view
      // toggle, so it is the reliable source at step 3.
      return !!this.selectedPrompts[0]?.dimensions_report;
    },
    isShopperInsightsReport() {
      return this.selectedPrompts[0]?.name == 'Shopper Insights Report';
    },      
    isShopperInsightsFormComplete(){
      const form = document.getElementById("ShopperInsightsForm");
      if (form){
        const requiredFields = form.querySelectorAll('[required]');
        let valid = true;

        requiredFields.forEach(field => {
            if (!field.value.trim()) {
                field.classList.add('is-invalid');
                valid = false;
            } else {
                field.classList.remove('is-invalid');
            }
        });

        if (!valid){
          toastr.error('Please complete all required fields before continuing.');
        }
        return valid;
      }else{
        return false;
      }
    },  
    isDisabled(prompt) {
      if (!this.selectedPrompts.length) return false;
      
      if (this.selectedPrompts[0]?.id === prompt.id) return false;

      if (this.isComparisonAnalysis(this.selectedPrompts[0]) && !this.isComparisonAnalysis(prompt)) return true;
      if (this.isProductComparisonAnalysis(this.selectedPrompts[0]) && !this.isProductComparisonAnalysis(prompt)) return true;
      if (this.isVisualPlayBookAnalysis(this.selectedPrompts[0]) && !this.isVisualPlayBookAnalysis(prompt)) return true;
      
      if (this.isComparisonAnalysis(prompt)) return true;
      if (this.isProductComparisonAnalysis(prompt)) return true;
      if (this.isVisualPlayBookAnalysis(prompt)) return true;
      
      
      return false;
    },
    openStandaloneReport(prompt) {
      const fullPrompt = this.customPrompts.find(item => String(item.id) === String(prompt?.id)) || prompt;
      if (!fullPrompt?.launch_url) return false;
      const url = new URL(fullPrompt.launch_url, window.location.origin);
      if (url.origin !== window.location.origin) return false;
      // The older /dashboard/analyze-images route has no Optix endpoint props.
      if (!this.commerceVisibilityProps.createUrl) {
        window.location.assign(url.href);
        return true;
      }
      this.commerceVisibilityMounted = true;
      this.commerceVisibilityOpen = true;
      this.commerceVisibilityTabActive = true;
      this.syncCommerceVisibilityUrl();
      return true;
    },
    readCommerceVisibilitySelection() {
      const params = new URL(window.location.href).searchParams;
      const id = params.get('visibility_report');
      const uuid = params.get('visibility_uuid');
      if (!/^[1-9][0-9]*$/.test(id || '') || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(uuid || '')) return null;
      const { reportUrlTemplate, jsonUrlTemplate } = this.commerceVisibilityProps;
      if (!reportUrlTemplate || !jsonUrlTemplate) return null;
      const fill = template => template.replace('__ID__', id).replace('__UUID__', uuid);
      return { id, uuid, url: fill(reportUrlTemplate), json_url: fill(jsonUrlTemplate) };
    },
    restoreCommerceVisibilityFromUrl() {
      if (!this.isOptixLab || !this.commerceVisibilityProps.createUrl) return;
      const selection = this.readCommerceVisibilitySelection();
      this.commerceVisibilityOpen = Boolean(selection);
      if (!selection) return;
      this.commerceVisibilityInitialSelection = selection;
      this.commerceVisibilitySelection = selection;
      this.commerceVisibilityMounted = true;
      this.commerceVisibilityTabActive = true;
    },
    onCommerceVisibilitySelection(selection) {
      this.commerceVisibilitySelection = selection;
      this.syncCommerceVisibilityUrl();
    },
    syncCommerceVisibilityUrl() {
      if (!this.isOptixLab) return;
      const url = new URL(window.location.href);
      url.searchParams.delete('visibility_report');
      url.searchParams.delete('visibility_uuid');
      if (this.commerceVisibilityOpen && this.commerceVisibilityTabActive) {
        const selection = this.commerceVisibilitySelection;
        if (selection) {
          url.searchParams.set('visibility_report', selection.id);
          url.searchParams.set('visibility_uuid', selection.uuid);
        }
        url.hash = 'optix-lab';
      }
      // Replace only this entry; polling must not add browser-history entries.
      window.history.replaceState(window.history.state, '', url.toString());
    },
    handleCommerceVisibilityTab(event) {
      if (!this.isOptixLab) return;
      this.commerceVisibilityTabActive = event.detail.tab === '#optix-lab';
      if (this.commerceVisibilityOpen) this.syncCommerceVisibilityUrl();
    },
    closeCommerceVisibility() {
      this.commerceVisibilityOpen = false;
      this.syncCommerceVisibilityUrl();
    },
    startAnalysisWithPrompt(prompt) {
      if (!prompt || this.openStandaloneReport(prompt)) return;
      if (this.commerceVisibilityOpen) this.closeCommerceVisibility();

      this.fetchPreviousReports(prompt.id);

      this.selectedPromptIds = [prompt.id];

      this.hydrateEditablePromptFieldsForPrompt(prompt);
      
      if (this.rerunData) {
          this.applyRerunPreloadData(this.rerunData);
          this.$nextTick(() => {
            this.applyRerunPreloadData(this.rerunData);
          });
          this.loadRerunImages();
      }

      this.$nextTick(() => {
        if (typeof this.nextStep === 'function') {
          this.nextStep();
        }
      });
    },
    async loadGroups() {
      if (!this.canManageGroups) {
        return;
      }

      this.isLoadingGroups = true;
      try {
        const response = await axios.get('/analytics-history-groups', {
          params: { perPage: 'all' }
        });
        this.availableGroups = response.data.data || [];
      } catch (error) {
        console.error('Failed to load groups:', error);
      } finally {
        this.isLoadingGroups = false;
      }
    },

    async loadProductLists(forceParseProducts = false) {
      this.isLoadingProductLists = true;
      try {
        const response = await axios.get('/product-id-list/all');
        this.availableProductLists = response.data.productIdLists || [];

        if (this.availableProductLists.length === 1) {
          this.selectedProductLists = [this.availableProductLists[0]];
          if (this.isVisualPlayBookAnalysis()) {
            await this.loadProductsFromProductLists();
          } else {
            await this.loadProductsFromLists(true);
          }
        }
        
        if (forceParseProducts) {
          await this.loadProductsFromLists(true, true);
        }
      } catch (error) {
        console.error('Failed to load product lists:', error);
      } finally {
        this.isLoadingProductLists = false;
      }
    },

    async loadProductsFromLists(isAutoSelect = false, forceParseProducts = false) {
      if (!forceParseProducts && (!this.selectedProductLists || this.selectedProductLists.length === 0)) {
        this.availableProducts = [];
        this.selectedProducts = [];
        return;
      }
      
      let selectedProductList = this.selectedProductLists || [];
      
      if (forceParseProducts) {
        selectedProductList = this.availableProductLists;
      }

      this.isLoadingProducts = true;
      try {
        const allProducts = [];
        this.productIdMapping = {};

        for (const productList of selectedProductList) {
          const productListId = productList.id || productList;
          const response = await axios.get(`/product-id-list/${productListId}/data`);
          const records = response.data.records || [];

          records.forEach(record => {
            const productId = record.asin || record.tcin || record.walmart_id || record.salsify_id || record.url;

            if (productId && productId.trim() !== '') {
              const displayName = this.getProductDisplayName(record);

              allProducts.push({
                id: record.id,
                record_id: record.id,
                product_id: productId,
                display_name: displayName,
                product_list_name: productList.name,
                material_code: record.material_code,
                gtin: record.gtin,
                asin: record.asin,
                tcin: record.tcin,
                walmart_id: record.walmart_id,
                salsify_id: record.salsify_id,
                url: record.url,
                is_salsify: productId == record.salsify_id ? true : false,
              });

              this.productIdMapping[productId] = {
                asin: record.asin,
                tcin: record.tcin,
                walmart_id: record.walmart_id,
                salsify_id: record.salsify_id,
                url: record.url,
                product_list: productList.name
              };
            }
          });
        }

        this.availableProducts = allProducts;
        this.selectedProducts = [];
        this.selectedProductRecordIds = [];

      } catch (error) {
        console.error('Failed to load products from lists:', error);
        toastr.error('Failed to load products from lists');
      } finally {
        this.isLoadingProducts = false;
      }
    },

    getProductDisplayName(record) {
      const productId = record.asin || record.tcin || record.walmart_id || record.salsify_id || record.url;

      return productId;
    },

    async populateSelectedProducts() {

      if (!this.selectedProducts || this.selectedProducts.length === 0) {
        this.multipleUrlsInput = '';
        this.comparisonMultipleInput = '';
        this.productUrlsText = '';
        this.categoryUrlsText = '';
        this.selectedProductRecordIds = []; // Clear record IDs
        return;
      }

      // force one product selection only
      if (this.isImageComparisonAnalysis() && this.selectedProducts.length > 1) {
          const last = this.selectedProducts[this.selectedProducts.length - 1];
          this.selectedProducts = [last];
          this.populateSelectedProducts();
      }

      // for now prepend "salsify:" to identify salsify products
      const selectedProductIds = this.selectedProducts.map(product => 
        product.is_salsify ? `salsify:${product.product_id}` : product.product_id
      );

      if (this.isImageComparisonAnalysis()){
        this.salsifyProperties = [];
        try {
            const response = await axios.post('/analysis-reports/get_salsify_proerties', {
                salsify_id: selectedProductIds
            });
            this.salsifyProperties = response.data;
        } catch (error) {
            console.error(error);
        }
        this.nextButtonDisabled;
      }

      this.selectedProductRecordIds = this.selectedProducts.map(product => product.record_id);

      if (this.isComparisonAnalysis()) {
        this.comparisonMultipleInput = selectedProductIds.join('\n');
      } else if (this.isVisualPlayBookAnalysis()) {
        if (this.inputMode === 'products') {
          this.productUrlsText = selectedProductIds.slice(0, 20).join('\n');
          this.handleProductUrlsInput();
        } else if (this.inputMode === 'category') {
          this.categoryUrlsText = selectedProductIds.join('\n');
          this.handleCategoryUrlsInput();
        }
      } else {
        this.multipleUrlsInput = selectedProductIds.join('\n');
      }
    },

    selectAll() {
      if (this.selectedProducts.length === this.filteredProducts.length) {
        return this.clearAll();
      }
      this.selectedProducts = [...this.filteredProducts];
      this.populateSelectedProducts();
    },

    clearAll() {
      this.selectedProducts = [];
      this.populateSelectedProducts();
    },

    limitText(count) {
      return `and ${count} more`;
    },

    async loadProductsFromProductLists() {
      if (!this.selectedProductLists || this.selectedProductLists.length === 0) {
        this.productListProducts = [];
        this.selectedProducts = [];
        return;
      }

      this.isLoadingProducts = true;
      try {
        const allProducts = [];
        for (const productList of this.selectedProductLists) {
          const productListId = productList.id || productList;
          const response = await axios.get(`/product-id-list/${productListId}/data`);
          const records = response.data.records || [];

          records.forEach(record => {
            const productId = record.asin || record.tcin || record.walmart_id || record.salsify_id || record.url;
            if (productId) {
              const displayName = this.getProductDisplayNameForPlaybook(record);
              allProducts.push({
                ...record,
                id: record.id,
                record_id: record.id,
                product_id: productId,
                display_name: displayName,
                product_list_name: productList.name,
                asin: record.asin,
                tcin: record.tcin,
                walmart_id: record.walmart_id,
                salsify_id: record.salsify_id,
                url: record.url
              });
            }
          });
        }

        this.productListProducts = allProducts;
      } catch (error) {
        console.error('Failed to load products from lists:', error);
        toastr.error('Failed to load products from lists');
      } finally {
        this.isLoadingProducts = false;
      }
    },

    getProductDisplayNameForPlaybook(record) {
      const productId = record.asin || record.tcin || record.walmart_id || record.salsify_id || record.url;
      let type = '';

      if (record.asin) type = 'ASIN';
      else if (record.tcin) type = 'TCIN';
      else if (record.walmart_id) type = 'Walmart ID';
      else if (record.salsify_id) type = 'Salsify ID';
      else if (record.url) type = 'URL';

      return `${productId} (${type})`;
    },

    populateSelectedProductsForPlaybook() {
      if (!this.selectedProducts || this.selectedProducts.length === 0) {
        this.selectedProductRecordIds = [];
        return;
      }

      this.selectedProductRecordIds = this.selectedProducts.map(product => product.record_id);
    },

    selectAllProductsForPlaybook() {
      if (this.selectedProducts.length === this.productListProducts.length) {
        return this.clearAllProductsForPlaybook();
      }
      this.selectedProducts = [...this.productListProducts];
      this.populateSelectedProductsForPlaybook();
    },

    clearAllProductsForPlaybook() {
      this.selectedProducts = [];
      this.populateSelectedProductsForPlaybook();
    },
    groupNameWithReportsCount(group) {
      return `${group.name} (${group.reports_count} reports)`
    },

    getProductIdForUrl(url) {
      if (this.productIdMapping[url]) {
        return this.productIdMapping[url];
      }

      if (!url.includes('http://') && !url.includes('https://')) {
        if (/^B[A-Z0-9]{9}$/i.test(url)) {
          return url; // It's an ASIN
        }
        if (/^A-\d{8,9}$/i.test(url)) {
          return url; // It's a TCIN
        }
        if (/^\d{1,11}$/.test(url)) {
          return url; // It's a Walmart ID
        }
      }

      if (url.includes('amazon.com') && url.includes('/dp/')) {
        const asinMatch = url.match(/\/dp\/([A-Z0-9]{10})/i);
        if (asinMatch) {
          return asinMatch[1];
        }
      }

      if (url.includes('target.com')) {
        const tcinMatch = url.match(/\/A-(\d{8,9})/i);
        if (tcinMatch) {
          return `A-${tcinMatch[1]}`;
        }
      }

      if (url.includes('walmart.com') && url.includes('/ip/')) {
        const walmartMatch = url.match(/\/ip\/(?:[^\/\?#]+\/)?(\d{1,11})(?:[\?#\/]|$)/);
        if (walmartMatch) {
          return walmartMatch[1];
        }
      }

      if (url.includes('wegmans.com') && url.includes('/shop/product/')) {
        const wegmansMatch = url.match(/\/shop\/product\/(\d+)/);
        if (wegmansMatch) {
          return wegmansMatch[1];
        }
      }

      return null;
    },

    expandProductIdentifierToUrl(identifier) {
      if (identifier.includes('http://') || identifier.includes('https://')) {
        return identifier;
      }

      if (/^B[A-Z0-9]{9}$/i.test(identifier)) {
        return `https://www.amazon.com/dp/${identifier}`;
      }

      if (/^A-\d{8,9}$/i.test(identifier)) {
        const tcin = identifier.replace('A-', '');
        return `https://www.target.com/p/-/A-${tcin}`;
      }

      if (/^\d{1,11}$/.test(identifier)) {
        return `https://www.walmart.com/ip/${identifier}`;
      }
      
      if (/^\d{11,16}$/.test(identifier) && this.isSalsifyConnected) {
        return 'salsify:' + identifier;
      }

      return identifier;
    },

    generateAutoGroupName() {
      if (!this.selectedPrompt || !this.selectedPrompt.name) {
        return null;
      }

      const promptName = this.selectedPrompt.name;
      const now = new Date();

      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const month = months[now.getMonth()];
      const day = now.getDate();
      const year = now.getFullYear();

      let hours = now.getHours();
      const minutes = now.getMinutes();
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12;
      hours = hours ? hours : 12;
      const minutesStr = minutes < 10 ? '0' + minutes : minutes;

      const timezoneStr = now.toLocaleTimeString('en-us', { timeZoneName: 'short' }).split(' ')[2];

      return `${promptName} - ${month} ${day} ${year} - ${hours}:${minutesStr}${ampm} ${timezoneStr}`;
    },

    async createGroupIfNeeded() {
      if (!this.createNewGroup || !this.newGroupName.trim()) {
        return this.selectedGroupId;
      }
      
      try {
        await axios.post('/analytics-history-groups', {
          name: this.newGroupName,
          selectedIds: [],
          action: 'reattach',
          justCreate: true
        });
        
        await this.loadGroups();
        
        const newGroup = this.availableGroups.find(g => g.name === this.newGroupName);
        if (newGroup) {
          this.selectedGroupId = newGroup.id;
          this.createNewGroup = false;
          this.newGroupName = '';
          return newGroup.id;
        }
      } catch (error) {
        console.error('Failed to create group:', error);
        toastr.error('Failed to create group');
      }
      
      return null;
    },
    
    async addReportsToGroup(reportIds, groupId) {
      if (!groupId || !reportIds || reportIds.length === 0) {
        return;
      }
      
      try {
        await axios.put(`/analytics-history-groups/${groupId}/attach`, {
          selectedIds: reportIds,
          action: 'reattach'
        });
        
        toastr.success(`Successfully added ${reportIds.length} report(s) to group`);
      } catch (error) {
        console.error('Failed to add reports to group:', error);
        toastr.error('Failed to add reports to group');
      }
    },
    
    async processMultipleUrlsSeparately() {
      const urlText = this.multipleUrlsInput;

      if (!urlText.trim()) {
        toastr.error('Please enter URLs to process');
        return;
      }

      let regex = /[\s\n\r,]+/
      if (urlText.includes('rossmann.pl')) {
        regex = /[\s\n\r]+/
      }
      
      const urls = urlText.split(regex).filter(url => url.trim());
      
      if (urls.length === 0) {
        toastr.error('No valid URLs found');
        return;
      }
      
      let groupId = null;
      if (this.selectedGroupId || (this.createNewGroup && this.newGroupName)) {
        groupId = await this.createGroupIfNeeded();
      }
      
      const batchId = urls.length > 1 ? `batch_${Date.now()}_${Math.random().toString(36).substr(2, 9)}` : null;
      
      const reportIds = [];
      const createdReports = [];
      
      for (let i = 0; i < urls.length; i++) {
        const url = urls[i];
        toastr.info(`Processing URL ${i + 1} of ${urls.length}...`);
        
        try {
          const processedUrl = this.processURLInput(url);
          
          const report = await this.createSingleUrlReport(processedUrl, `Report ${i + 1} - ${url}`, true, batchId);
          
          if (report && report.analyticsHistoryId) {
            reportIds.push(report.analyticsHistoryId);
            report.originalUrl = url;
            createdReports.push(report);
          }
        } catch (error) {
          console.error(`Failed to process URL ${url}:`, error);
          toastr.error(`Failed to process URL: ${url}`);
        }
      }
      
      if (groupId && reportIds.length > 0) {
        await this.addReportsToGroup(reportIds, groupId);
      }
      
      toastr.success(`Successfully created ${createdReports.length} report(s)`);
      
      if (createdReports.length > 0) {
        if (createdReports.length > 0) {
          const reportIds = createdReports
            .map(report => report.analyticsHistoryId)
            .filter(id => id);

          if (reportIds.length > 0) {
            this.redirectToAnalyticsHistory(reportIds);
          } else {
            this.redirectToAnalyticsHistory();
          }
        } else {
          this.redirectToAnalyticsHistory();
        }
      }
    },
    
    async processMultipleUrlsWithSameImages() {
      const urlText = this.multipleUrlsInput;
      if (!urlText.trim()) {
        toastr.error('Please enter URLs to process');
        return;
      }

      let regex = /[\s\n\r,]+/
      if (urlText.includes('rossmann.pl')) {
        regex = /[\s\n\r]+/
      }

      const urls = urlText.split(regex).filter(url => url.trim());

      if (urls.length === 0) {
        toastr.error('No valid URLs found');
        return;
      }

      const filesToProcess = this.getFilteredFiles();
      if (!filesToProcess || filesToProcess.length === 0) {
        toastr.error('No images found to process');
        return;
      }

      let groupId = null;
      if (this.selectedGroupId || (this.createNewGroup && this.newGroupName)) {
        groupId = await this.createGroupIfNeeded();
      }
      
      const batchId = urls.length > 1 ? `batch_${Date.now()}_${Math.random().toString(36).substr(2, 9)}` : null;
      
      const reportIds = [];
      const createdReports = [];
      
      for (let i = 0; i < urls.length; i++) {
        const url = urls[i];
        
        const correspondingImages = this.urlToImagesMapping[url];
        
        if (!correspondingImages || correspondingImages.length === 0) {
          toastr.warning(`Skipping URL ${i + 1}: ${url} - No images found from previous report`);
          continue;
        }
        
        toastr.info(`Processing URL ${i + 1} of ${urls.length} with ${correspondingImages.length} corresponding image(s)...`);
        
        try {
          const processedUrl = this.processURLInput(url);
          
          const report = await this.createReportWithUploadedImages(processedUrl, `Report ${i + 1} - ${url}`, correspondingImages, batchId);
          
          if (report && report.analyticsHistoryId) {
            reportIds.push(report.analyticsHistoryId);
            report.originalUrl = url;
            createdReports.push(report);
          }
        } catch (error) {
          console.error(`Failed to process URL ${url}:`, error);
          toastr.error(`Failed to process URL: ${url}`);
        }
      }
      
      if (groupId && reportIds.length > 0) {
        await this.addReportsToGroup(reportIds, groupId);
      }
      
      toastr.success(`Successfully created ${createdReports.length} report(s) using images originally scraped from each URL`);
      
      if (createdReports.length > 0) {
        if (createdReports.length > 0) {
          const reportIds = createdReports
            .map(report => report.analyticsHistoryId)
            .filter(id => id);

          if (reportIds.length > 0) {
            this.redirectToAnalyticsHistory(reportIds);
          } else {
            this.redirectToAnalyticsHistory();
          }
        } else {
          this.redirectToAnalyticsHistory();
        }
      }
    },
    
    fileToDataURL(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = (e) => reject(e);
        reader.readAsDataURL(file);
      });
    },
    
    async createReportWithUploadedImages(url, reportTitle, uploadedFiles, batchId = null) {
      if (!this.selectedPrompt) {
        throw new Error('No prompt selected');
      }
      
      let allFiles = [];
      let productTitle = reportTitle;
      
      for (const file of uploadedFiles) {
        let imageUrl;
        
        if (file.dataURL) {
          imageUrl = file.dataURL;
        } else if (file instanceof File || file instanceof Blob) {
          imageUrl = await this.fileToDataURL(file);
        } else if (file.image) {
          imageUrl = file.image;
        } else if (typeof file === 'string') {
          imageUrl = file;
        } else {
          console.warn('Unknown file format:', file);
          continue;
        }
        
        allFiles.push({
          type: 'image_url',
          image_url: { 
            url: imageUrl
          }
        });
      }
      
      if (url && url.startsWith('http')) {
        try {
          const { data } = await axios.post(`/webscrape`, { url, skip_images: true });
          if (data && data.title) {
            productTitle = data.title;
          }
        } catch (error) {
          console.warn('Could not fetch product title:', error);
        }
      }

      let prompt = this.processAudienceInPrompt(this.selectedPrompt.prompt);

      const apiSettings = {
        model: this.selectedPrompt.model || 'gpt-5',
        messages: [
          {
            role: 'user',
            content: [
              {
                type: 'text',
                text: prompt
              },
              ...allFiles
            ]
          }
        ],
        response_format: this.selectedPrompt.responseFormat || undefined
      };
      
      const apiResponse = await this.callOpenAI(apiSettings);
      
      const content = apiResponse.content || apiResponse;
      const isJsonResponse = this.selectedPrompt.responseFormat && this.selectedPrompt.responseFormat.type === 'json_object';
      
      let processedResult;
      if (isJsonResponse) {
        try {
          processedResult = JSON.parse(content);
        } catch (error) {
          console.error('Error parsing JSON response:', error);
          processedResult = { markdown: content };
        }
      } else {
        processedResult = { markdown: content };
      }
      
      processedResult.images = allFiles.map(file => file.image_url.url);
      processedResult.reportType = this.selectedPrompt.name;
      processedResult.selectedPromptId = this.selectedPrompt.id;
      processedResult.title = this.selectedPrompt.name;
      processedResult.item_url = url;
      processedResult.product_title = productTitle;
      
      const analysisResult = {
        api_response: apiResponse,
        api_call_data: apiSettings,
        webscrape_response_html: null,
        type: this.selectedPrompt?.analysisFormat?.type || 'generic-markdown',
        data: processedResult,
        from_url: false,
        product_urls: []
      };

      if (batchId) {
        analysisResult.batch_id = batchId;
        processedResult.batch_id = batchId;
      }

      analysisResult.created_from_previous_images = true;
      processedResult.created_from_previous_images = true;

      const finalProductRecordId = this.currentProductRecordId;
      if (finalProductRecordId) {
        analysisResult.product_id_list_records_id = finalProductRecordId;
      }

      if (this.customerName && this.customerName.trim()) {
        analysisResult.customer_name = this.customerName.trim();
      }

      const analysisResponse = await axios.post('/banner/groups/0/analysis-reports', analysisResult);

      if (this.selectedPrompt.join_images_and_text) {
        try {
          await axios.get(`/analysis-report/join-images/${analysisResponse.data.id}`);
        } catch (error) {
          //nothing
        }
      }
      
      const analysisUrl = `/analysis-reports/${analysisResponse.data.id}/${analysisResponse.data.uuid}`;
      
      return {
        url: analysisUrl,
        analyticsHistoryId: analysisResponse.data.analytics_history_id,
        result: analysisResponse.data.data
      };
    },
    
    async createSingleUrlReport(url, reportTitle, skipOpenWindow = false, batchId = null) {
      if (!this.selectedPrompt) {
        throw new Error('No prompt selected');
      }
      
      let allFiles = [];
      let webScrapeResponseHtml = null;
      let ugcImagePositions = null;
      let productTitle = reportTitle;
      
      if (url && url.startsWith('http')) {
        try {
          const { data } = await axios.post(`/webscrape`, { url });
          
          if (data && data.title) {
            productTitle = data.title;
          }
          
          if (data && data.response_html) {
            webScrapeResponseHtml = data.response_html;
          }
          
          if (data && data.ugc_images) {
            ugcImagePositions = data.ugc_images;
          }
          
          if (data && data.images && data.images.length > 0) {
            const filteredImages = this.getFilteredImages(data.images);
            
            for (const imageUrl of filteredImages) {
              try {
                const { highRes, lowRes } = await this.downloadImageWithBothQualities(imageUrl);
                allFiles.push({
                  type: 'image_url',
                  image_url: { 
                    url: this.isReduceImageQuality ? lowRes : highRes,
                    highResUrl: highRes
                  }
                });
              } catch (error) {
                console.error('Error processing image:', error);
              }
            }
          }
        } catch (error) {
          console.error('Error scraping URL:', error);
          throw error;
        }
      }

      let prompt = this.processAudienceInPrompt(this.selectedPrompt.prompt);
      prompt = prompt.replaceAll('{{url}}', url);

      if (this.selectedPrompt?.pdp_title_and_copy_assessment && webScrapeResponseHtml) {
        prompt = `${prompt}\n\nScraped Content from URL:\n${webScrapeResponseHtml}`;
      }
      
      const imageContents = allFiles.map(image => ({
        type: 'image_url',
        image_url: {
          url: image.image_url.url,
          highResUrl: image.image_url.highResUrl || image.image_url.url
        }
      }));
      
      let apiSettings = {
        messages: [
          {
            role: "user",
            content: [
              {
                type: "text",
                text: prompt,
              },
              ...imageContents,
            ],
          },
        ],
        model: this.selectedPrompt.model,
      };
      
      const responseFormat = this.selectedPrompt.responseFormat?.structuredOutputSchema || 
                            this.selectedPrompt.analysisFormat;
      
      if (this.selectedPrompt.use_openai_websearch) {
        apiSettings.websearch = true;
      }

      if (responseFormat) {
        apiSettings.response_format = responseFormat;
      }
      
      const responseMessage = await this.callOpenAI(apiSettings);
      
      if (!responseMessage || !responseMessage.content) {
        throw new Error('No response from API');
      }
      
      const result = await this.processAnalysisResponse(
        responseMessage,
        apiSettings,
        imageContents,
        this.selectedPrompt,
        webScrapeResponseHtml,
        skipOpenWindow,
        url,  // Pass the specific URL being processed
        batchId  // Pass the batch ID for multi-URL sessions
      );
      
      if (result && result.analytics_history_id) {
        result.analyticsHistoryId = result.analytics_history_id;
      }
      
      return result;
    },
     initializeDragAndDrop(isSecondary = false) {
      this.$nextTick(() => {
        let dropzone = this.$refs.fileInput?.dropzone;
        let dropzoneElement = this.$refs.fileInput?.$el;
        
        if (!dropzone || !dropzoneElement) return;

        const previews = this.getAllPreviewElements(dropzoneElement);
        
        if (previews.length === 0) return;

        this.setupDraggableElements(previews, dropzone);
      });
    },
    select(view) {
      this.selectedImageType = view;
    },
    validateImageNumbers() {
      if (!this.specificImageNumbers || this.specificImageNumbers.trim() === '') {
        return true;
      }
      
      const numbers = this.specificImageNumbers.split(',').map(n => n.trim());
      const maxImages = this.uploadedFilesCount;
      
      for (const num of numbers) {
        const parsed = parseInt(num);
        if (isNaN(parsed) || parsed < 1 || parsed > maxImages) {
          return false;
        }
      }
      
      return true;
    },
    validateImageNumbersForUrl() {
      if (!this.specificImageNumbers || this.specificImageNumbers.trim() === '') {
        return true;
      }
      
      const numbers = this.specificImageNumbers.split(',').map(n => n.trim());
      
      for (const num of numbers) {
        const parsed = parseInt(num);
        if (isNaN(parsed) || parsed < 1) {
          return false;
        }
      }
      
      return true;
    },
    getSelectedImageIndices() {
      if (!this.enableSpecificImages || !this.specificImageNumbers || this.specificImageNumbers.trim() === '') {
        return null;
      }
      
      const numbers = this.specificImageNumbers.split(',').map(n => n.trim());
      return numbers.map(num => parseInt(num) - 1);
    },
    getFilteredFiles() {
      const selectedIndices = this.getSelectedImageIndices();
      if (selectedIndices === null) {
        return this.file;
      }
      
      return selectedIndices
        .filter(index => index >= 0 && index < this.file.length)
        .map(index => this.file[index]);
    },
    getFilteredImages(images) {
      const selectedIndices = this.getSelectedImageIndices();
      if (selectedIndices === null || !Array.isArray(images)) {
        return images;
      }
      
      return selectedIndices
        .filter(index => index >= 0 && index < images.length)
        .map(index => images[index]);
    },
    updateManualItemUrl() {
      let targetUrl = '';
      
      if (this.isComparisonAnalysis()) {
        if (this.comparisonMultipleInput.trim()) {
          const urls = this.comparisonMultipleInput.split(/[\s\n\r]+/).filter(url => url.trim());
          if (urls.length > 0) {
            targetUrl = urls[0];
          }
        }
      } else {
        const mainUrl = this.multipleUrlsMode ? this.multipleUrlsInput.trim() : this.urlInput.trim();
        if (mainUrl) {
          if (this.multipleUrlsMode) {
            const urls = mainUrl.split(/[\s\n\r]+/).filter(url => url.trim());
            if (urls.length > 0) {
              targetUrl = urls[0];
            }
          } else {
            targetUrl = mainUrl;
          }
        }
      }
      
      if (targetUrl && (!this.manualItemUrl.trim() || this.manualItemUrl === this.previousAutoPopulatedUrl)) {
        this.manualItemUrl = targetUrl;
        this.previousAutoPopulatedUrl = targetUrl;
      } else if (!targetUrl && this.manualItemUrl === this.previousAutoPopulatedUrl) {
        this.manualItemUrl = '';
        this.previousAutoPopulatedUrl = '';
      }
    },

    checkURLForPromptID() {
      if (this.commerceVisibilityOpen) return;
      const urlParams = new URLSearchParams(window.location.search);
      let promptID = urlParams.get('promptID') || urlParams.get('prompt');
      const productListId = urlParams.get('productListId');

      if (this.isRerunMode === false && this.rerunData && !this.rerunData.promptId && urlParams.get('rerun') === 'true') {
        // Run New flow: do not auto-select any prompt from the list.
        promptID = null;
      }

      if (!promptID && this.isOptixLab) {
        const hash = window.location.hash;
        if (hash.includes('promptID=')) {
          const hashParams = new URLSearchParams(hash.split('?')[1] || '');
          promptID = hashParams.get('promptID') || hashParams.get('prompt');
        }
      }

      if (this.isRerunMode && this.rerunData && this.rerunData.promptId) {
        promptID = this.rerunData.promptId;
      }

      if (promptID) {
        const numericPromptId = parseInt(promptID, 10);
        if (!isNaN(numericPromptId)) {
          const prompt = this.customPrompts.find(item => String(item.id) === String(numericPromptId));
          if (this.openStandaloneReport(prompt)) return;
          this.selectedPromptIds = [numericPromptId];

          if (this.viewMode === 'list') {
            const prompt = this.customPrompts.find(p => p.id === numericPromptId);
            if (prompt) {
              this.selectedPromptsListView = [prompt];
            }
          }
        }
      }

      if (!this.isRerunMode) {
        const urlFromParam = urlParams.get('url');
        if (urlFromParam && !this.manualItemUrl) {
          this.manualItemUrl = decodeURIComponent(urlFromParam);
        }
      }
      
    },

    async checkForPreSelectedProductList() {
      const urlParams = new URLSearchParams(window.location.search);
      const productListId = urlParams.get('productListId');

      if (productListId && this.isOptixLab) {
        const waitForProductLists = () => {
          return new Promise((resolve) => {
            const checkInterval = setInterval(() => {
              if (this.availableProductLists && this.availableProductLists.length > 0 && !this.isLoadingProductLists) {
                clearInterval(checkInterval);
                resolve();
              }
            }, 100);
          });
        };

        await waitForProductLists();

        const foundProductList = this.availableProductLists.find(list => list.id == productListId);

        if (foundProductList) {
          this.selectedProductLists = [foundProductList];

          if (this.isVisualPlayBookAnalysis()) {
            await this.loadProductsFromProductLists();

            if (this.productListProducts && this.productListProducts.length > 0) {
              this.selectedProducts = [...this.productListProducts];
              this.populateSelectedProductsForPlaybook();

              this.$nextTick(() => {
                this.activeTab = 2;
              });
            }
          } else {
            await this.loadProductsFromLists(false);

            if (this.availableProducts && this.availableProducts.length > 0) {
              this.selectedProducts = [...this.availableProducts];
              this.populateSelectedProducts();

              this.$nextTick(() => {
                this.activeTab = 2;
              });
            }
          }
        } else {
          toastr.warning(`Product list with ID ${productListId} not found`);
        }
      }
    },
    async loadRerunImages() {
      this.isLoadingRerunImages = true;
      
      try {
        const rerunData = JSON.parse(localStorage.getItem('rerunData'));
        this.applyRerunPreloadData(rerunData);
        if (!rerunData) {
          toastr.error('Invalid rerun data structure');
          this.isLoadingRerunImages = false;
          return;
        }
        
        if (!rerunData.images || !Array.isArray(rerunData.images)) {
          console.error('Invalid rerun data structure:', rerunData);
          toastr.error('Invalid rerun data structure');
          this.isLoadingRerunImages = false;
          return;
        }
        
        const images = rerunData.images;
        
        if (this.$refs.fileInput) {
          this.$refs.fileInput.removeAllFiles(true);
        }
        
        this.imageAPreviewUrl = null;
        this.imageBPreviewUrl = null;
        
        for (const [index, imageUrl] of images.entries()) {
          try {
            const fullImageUrl = imageUrl.startsWith('http') ? imageUrl : window.location.origin + imageUrl;
            
            const response = await fetch(fullImageUrl);
            if (!response.ok) {
              throw new Error(`Failed to fetch image: ${response.statusText}`);
            }
            
            const blob = await response.blob();

            const imageLabel = Array.isArray(rerunData.image_labels)
              ? rerunData.image_labels.find(
                item => Number(item?.image_number) === index + 1
              )
              : null;

            const fileName = imageLabel?.file_name || `rerun-image-${index + 1}.jpg`;

            const file = new File([blob], fileName, {
              type: blob.type || 'image/jpeg'
            });
            
            if (index === 0) {
              this.imageAPreviewUrl = fullImageUrl;
            }
            
            if (index === 1 && this.isComparisonAnalysis()) {
              this.imageBPreviewUrl = fullImageUrl;
            }
            
            this.$refs.fileInput.addFile(file);
            
          } catch (error) {
            console.error('Error processing rerun image:', error);
            toastr.warning(`Failed to load image ${index + 1}`);
          }
        }

        await this.$nextTick();

        if (this.$refs.fileInput) {
          this.file = this.$refs.fileInput.getAcceptedFiles();
        }

        this.syncImageTypeLabels();
        this.applyRerunImageLabels(rerunData.image_labels);
        
        if (rerunData.itemUrl) {
          this.manualItemUrl = rerunData.itemUrl;
        } else if (rerunData.manualItemUrl) {
          this.manualItemUrl = rerunData.manualItemUrl;
        }
        
        toastr.success(`Loaded ${images.length} images from previous report`);
        
        this.rerunData = rerunData;
        
      } catch (error) {
        console.error('Error loading rerun images:', error);
        toastr.error('Failed to load rerun images');
      } finally {
        this.isLoadingRerunImages = false;
      }
    },
    async loadVisualPlaybookData() {
      this.isLoadingRerunImages = true;
      
      try {
        const rerunData = JSON.parse(localStorage.getItem('rerunData'));
        if (!rerunData) {
          toastr.error('Invalid visual playbook data');
          this.isLoadingRerunImages = false;
          return;
        }
        
        if (!rerunData.is_visual_playbook || !rerunData.visual_playbook_data) {
          toastr.error('Invalid visual playbook data');
          this.isLoadingRerunImages = false;
          return;
        }
        
        const visualPlaybookData = rerunData.visual_playbook_data;

        this.applyRerunPreloadData({
          ...rerunData,
          retail: rerunData.retail || visualPlaybookData?.details?.retailer || rerunData.retailer || null,
        });
        
        if (visualPlaybookData && visualPlaybookData.details) {
          
          const inputMode = visualPlaybookData.details.input_mode;
          
          if (inputMode === 'products' && visualPlaybookData.details.product_urls) {
            this.inputMode = 'products';
            
            const productUrls = visualPlaybookData.details.product_urls;
            this.productUrls = [...productUrls]; // Copy the array
            this.productUrlsText = productUrls.filter(url => url.trim()).join('\n');

            if (visualPlaybookData.details.product_list_names && visualPlaybookData.details.product_list_names.length > 0) {
              toastr.info('Manual URLs loaded. Product list selections need to be reselected to include them.');
            }

          }
          else if (inputMode === 'category' && visualPlaybookData.details.category_urls) {
            this.inputMode = 'category';
            
            const categoryUrls = visualPlaybookData.details.category_urls;
            this.categoryUrls = [...categoryUrls]; // Copy the array
            this.categoryUrlsText = categoryUrls.filter(url => url.trim()).join('\n');
            
          }
          else if (visualPlaybookData.details.search_term) {
            this.inputMode = 'search';
            this.searchKeyword = visualPlaybookData.details.search_term;
            this.includeBrands = visualPlaybookData.details.include_brands;
            this.excludeBrands = visualPlaybookData.details.exclude_brands;
          }
          else if (visualPlaybookData.details.url) {
            this.inputMode = 'search';
            this.multipleUrlsMode = true;
            this.multipleUrlsInput = visualPlaybookData.details.url;
            this.urlInput = visualPlaybookData.details.url;
            this.manualItemUrl = visualPlaybookData.details.url;
          }
          
          if (visualPlaybookData.details.retailer) {
            this.selectedRetailer = this.normalizeRerunRetailer(visualPlaybookData.details.retailer);
            this.selectedRetailerPrompt = this.normalizeRerunRetailer(visualPlaybookData.details.retailer);
          }
        }
        
        if (this.$refs.fileInput) {
          this.$refs.fileInput.removeAllFiles(true);
        }
        
        this.file = [];
        
        this.step = 3;
        
        toastr.success('Loaded visual playbook report data - ready to run analysis');
        
      } catch (error) {
        console.error('Error loading visual playbook data:', error);
        toastr.error('Failed to load visual playbook data');
      } finally {
        this.isLoadingRerunImages = false;
      }
    },
    normalizeRerunRetailer(retailer) {
      if (!retailer) {
        return null;
      }

      const value = String(retailer).trim();
      if (!value) {
        return null;
      }

      const normalized = value.toLowerCase();
      const normalizedMap = {
        amazon: 'amazon',
        walmart: 'walmart',
        target: 'target',
        wegmans: 'wegmans',
        shoprite: 'shoprite',
        'giant food stores': 'giantfoodstores',
        giantfoodstores: 'giantfoodstores',
        'home depot': 'homedepot',
        homedepot: 'homedepot',
        instacart: 'instacart',
        lowes: 'lowes',
        "lowe's": 'lowes',
        'mercado libre': 'mercadolibre',
        mercadolibre: 'mercadolibre',
      };

      if (normalizedMap[normalized]) {
        return normalizedMap[normalized];
      }

      const allRetailers = [
        ...(this.retailers || []),
        ...(this.retailerList || []),
      ];

      const matchedRetailer = allRetailers.find((item) => {
        const optionValue = String(item?.value || '').trim().toLowerCase();
        const optionLabel = String(item?.label || item?.name || '').trim().toLowerCase();
        return optionValue === normalized || optionLabel === normalized;
      });

      return matchedRetailer ? matchedRetailer.value : normalized.replace(/[^a-z0-9]/g, '');
    },
    applyRerunPreloadData(rerunData) {
      if (!rerunData) {
        return;
      }

      const retailerValue = this.normalizeRerunRetailer(rerunData.retail || rerunData.retailer);
      if (retailerValue) {
        this.selectedRetailer = retailerValue;
        this.selectedRetailerPrompt = retailerValue;
      }

      if (rerunData.audience) {
        const audienceName = String(rerunData.audience).trim();
        if (audienceName) {
          const lowerAudienceName = audienceName.toLowerCase();
          const matchedAudience = (this.audiences || []).find((audience) => {
            const audienceLabel = String(audience?.name || '').trim().toLowerCase();
            const audienceDescription = String(audience?.description || '').trim().toLowerCase();
            return audienceLabel === lowerAudienceName || audienceDescription === lowerAudienceName;
          });

          this.selectedAudiences = matchedAudience ? [matchedAudience] : [{
            id: `rerun-audience-${lowerAudienceName.replace(/[^a-z0-9]+/g, '-') || 'value'}`,
            name: audienceName,
            description: audienceName,
            isRerunAudience: true,
          }];
        }
      }
    },
    checkForRerunMode() {
      const urlParams = new URLSearchParams(window.location.search);
      let isRerun = urlParams.get('rerun') === 'true';

      if (!isRerun && this.isOptixLab) {
        const hash = window.location.hash;
        if (hash.includes('rerun=true')) {
          isRerun = true;
        }
      }

      if (isRerun) {
        const urlFromParam = urlParams.get('url');
        if (urlFromParam) {
          this.manualItemUrl = decodeURIComponent(urlFromParam);
        }
        
        const rerunDataString = localStorage.getItem('rerunData');
        if (rerunDataString) {
          try {
            this.rerunData = JSON.parse(rerunDataString);

            const hasPromptId = !!this.rerunData?.promptId;

            if (!hasPromptId) {
              // Run New: keep the user on the prompt list and preload the previous report data only.
              this.isRerunMode = false;
              this.step = 2;
              this.applyRerunPreloadData(this.rerunData);
              return;
            }

            this.isRerunMode = true;
            
            if (this.rerunData && this.rerunData.timestamp) {
              const now = Date.now();
              const dataAge = now - this.rerunData.timestamp;
              const maxAge = 24 * 60 * 60 * 1000;
              
              if (dataAge > maxAge) {
                toastr.warning('Re-run data has expired. Starting fresh analysis.');
                this.clearRerunData();
                return;
              }
              
              if (this.rerunData.is_visual_playbook) {
                // For visual playbook reports, just populate form and go to step 3
                this.loadVisualPlaybookData();
              } else {
                // For regular reports, set step to 3 and load images
                this.step = 3;
                
                this.$nextTick(() => {
                  this.loadRerunImages();
                });
                
                this.isLoadingRerunImages = true;
                toastr.info('Loading images from previous report...');
              }
            } else {
              toastr.warning('Invalid re-run data. Starting fresh analysis.');
              this.clearRerunData();
            }
          } catch (error) {
            console.error('Error parsing rerun data:', error);
            toastr.error('Failed to load re-run data. Starting fresh analysis.');
            this.clearRerunData();
          }
        } else {
          toastr.warning('No re-run data found. Starting fresh analysis.');
          this.isRerunMode = false;
        }
      }
    },
    clearRerunData() {
      this.rerunData = null;
      this.isRerunMode = false;
      localStorage.removeItem('rerunData');
    },
    handleOptixLabRerun(event) {
      if (!this.isOptixLab) return;

      if (event.detail && event.detail.rerunData) {
        this.checkForRerunMode();
        this.checkURLForPromptID();
      }
    },
    handleHashChange() {
      if (!this.isOptixLab) return;

      const hash = window.location.hash;
      if (hash.includes('rerun=true') && !this.isRerunMode) {
        this.checkForRerunMode();
        this.checkURLForPromptID();
      }
    },
    getAllPreviewElements(dropzoneElement) {
      const selectors = [
        '.dz-preview',
        '.dropzone-previews .dz-preview',
        '.dz-default .dz-preview',
        '[data-dz-thumbnail]'
      ];

      let previews = [];
      
      for (const selector of selectors) {
        const found = dropzoneElement.querySelectorAll(selector);
        if (found.length > 0) {
          previews = Array.from(found);
          break;
        }
      }

      if (previews.length === 0) {
        const allElements = dropzoneElement.querySelectorAll('*');
        previews = Array.from(allElements).filter(el => {
          return el.classList.contains('dz-preview') || 
                el.querySelector('[data-dz-thumbnail]') ||
                el.querySelector('.dz-image') ||
                (el.style && el.style.backgroundImage);
        });
      }

      return previews;
    },
    setupDraggableElements(previews, dropzone) {
      previews.forEach((preview, index) => {
        this.removeDragListeners(preview);
        
        preview.draggable = true;
        preview.style.cursor = 'move';
        preview.style.transition = 'all 0.2s ease';
        
        preview.dataset.fileIndex = index;
        preview.dataset.isDraggable = 'true';
        
        const boundHandlers = {
          dragstart: this.handleDragStart.bind(this),
          dragover: this.handleDragOver.bind(this),
          drop: this.handleDrop.bind(this),
          dragend: this.handleDragEnd.bind(this),
          dragenter: this.handleDragEnter.bind(this),
          dragleave: this.handleDragLeave.bind(this)
        };

        preview._boundHandlers = boundHandlers;
        
        Object.entries(boundHandlers).forEach(([event, handler]) => {
          preview.addEventListener(event, handler);
        });
      });
    },
    removeDragListeners(element) {
      if (element._boundHandlers) {
        Object.entries(element._boundHandlers).forEach(([event, handler]) => {
          element.removeEventListener(event, handler);
        });
        delete element._boundHandlers;
      }
    },
    handleDragStart(e) {
      e.stopPropagation();
      
      this.draggedElement = e.currentTarget;
      this.draggedIndex = parseInt(this.draggedElement.dataset.fileIndex);
      
      this.draggedElement.style.opacity = '0.5';
      this.draggedElement.style.transform = 'rotate(2deg)';
      this.draggedElement.classList.add('dragging');
      
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', this.draggedIndex.toString());
    },
    handleDragOver(e) {
      if (!this.draggedElement) return;
      
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = 'move';
      
      const targetElement = e.currentTarget;
      if (targetElement && targetElement !== this.draggedElement && targetElement.dataset.isDraggable) {
        targetElement.classList.add('drag-over');
      }
    },
    handleDragEnter(e) {
      if (!this.draggedElement) return;
      
      e.preventDefault();
      e.stopPropagation();
      
      const targetElement = e.currentTarget;
      if (targetElement && targetElement !== this.draggedElement && targetElement.dataset.isDraggable) {
        targetElement.classList.add('drag-over');
      }
    },
    handleDragLeave(e) {
      if (!this.draggedElement) return;
      
      const targetElement = e.currentTarget;
      if (targetElement && targetElement.dataset.isDraggable) {
        targetElement.classList.remove('drag-over');
      }
    },
    handleDrop(e) {
      if (!this.draggedElement) return;
      
      e.preventDefault();
      e.stopPropagation();
      
      const targetElement = e.currentTarget;
      if (!targetElement || targetElement === this.draggedElement || !targetElement.dataset.isDraggable) {
        this.handleDragEnd(e);
        return;
      }
      
      targetElement.classList.remove('drag-over');
      
      const targetIndex = parseInt(targetElement.dataset.fileIndex);
      
      this.reorderFiles(this.draggedIndex, targetIndex);
      
      this.handleDragEnd(e);
    },
    handleDragEnd(e) {
      if (!this.draggedElement) return;
      
      this.draggedElement.style.opacity = '1';
      this.draggedElement.style.transform = 'none';
      this.draggedElement.classList.remove('dragging');
      
      const dropzoneElement = this.$refs.fileInput?.$el;
      if (dropzoneElement) {
        const allPreviews = dropzoneElement.querySelectorAll('[data-is-draggable="true"]');
        allPreviews.forEach(preview => {
          preview.classList.remove('drag-over');
        });
      }
      
      this.draggedElement = null;
      this.draggedIndex = null;
    },
    reorderFiles(fromIndex, toIndex) {
      if (fromIndex === toIndex) return;
      
      const dropzone = this.$refs.fileInput?.dropzone;
      if (!dropzone || !dropzone.files) return;
      
      try {
        const files = [...dropzone.files];
        const previews = this.getAllPreviewElements(this.$refs.fileInput.$el);
        
        const movedFile = dropzone.files.splice(fromIndex, 1)[0];
        dropzone.files.splice(toIndex, 0, movedFile);
        
        if (Array.isArray(this.file)) {
          const movedComponentFile = this.file.splice(fromIndex, 1)[0];
          this.file.splice(toIndex, 0, movedComponentFile);
        }

        this.syncImageTypeLabels();
        
        this.updateVisualOrder(previews, fromIndex, toIndex);
        
      } catch (error) {
        console.error('Error reordering files:', error);
      }
    },
    updateVisualOrder(previews, fromIndex, toIndex) {
      if (!previews || previews.length === 0) return;
      
      const container = previews[0].parentNode;
      if (!container) return;
      
      const draggedElement = previews[fromIndex];
      const targetElement = previews[toIndex];
      
      if (!draggedElement || !targetElement) return;
      
      draggedElement.remove();
      
      if (fromIndex < toIndex) {
        if (targetElement.nextSibling) {
          container.insertBefore(draggedElement, targetElement.nextSibling);
        } else {
          container.appendChild(draggedElement);
        }
      } else {
        container.insertBefore(draggedElement, targetElement);
      }
      
      this.$nextTick(() => {
        this.updateAllFileIndices();
      });
    },
    updateAllFileIndices() {
      const dropzoneElement = this.$refs.fileInput?.$el;
      if (!dropzoneElement) return;

      const previews = this.getAllPreviewElements(dropzoneElement);
      
      previews.forEach((preview, index) => {
        preview.dataset.fileIndex = index;
      });
      
      setTimeout(() => {
        this.initializeDragAndDrop();
      }, 100);
    },
    cleanupAllDragAndDrop() {
      const dropzoneElement = this.$refs.fileInput?.$el;
      if (!dropzoneElement) return;

      const draggableElements = dropzoneElement.querySelectorAll('[data-is-draggable="true"]');
      draggableElements.forEach(element => {
        this.removeDragListeners(element);
        element.draggable = false;
        delete element.dataset.isDraggable;
        delete element.dataset.fileIndex;
      });
    },
    setupDropzoneObserver() {
      if (!this.$refs.fileInput) return;

      const dropzoneElement = this.$refs.fileInput.$el;
      if (!dropzoneElement) return;

      this.dropzoneObserver = new MutationObserver((mutations) => {
        let shouldReinitialize = false;

        mutations.forEach((mutation) => {
          if (mutation.type === 'childList') {
            const addedNodes = Array.from(mutation.addedNodes);
            const hasNewPreviews = addedNodes.some(node => {
              if (node.nodeType !== Node.ELEMENT_NODE) return false;
              
              return node.classList?.contains('dz-preview') ||
                    node.querySelector?.('.dz-preview') ||
                    node.querySelector?.('[data-dz-thumbnail]') ||
                    node.classList?.contains('dz-image');
            });

            if (hasNewPreviews) {
              shouldReinitialize = true;
            }
          }
          
          if (mutation.type === 'attributes' && 
              mutation.target.classList?.contains('dz-preview')) {
            shouldReinitialize = true;
          }
        });

        if (shouldReinitialize) {
          clearTimeout(this.reinitializeTimeout);
          this.reinitializeTimeout = setTimeout(() => {
            this.initializeDragAndDrop();
            this.addMagnifyingGlassToThumbnails();
          }, 100);
        }
      });

      this.dropzoneObserver.observe(dropzoneElement, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'style']
      });

      this.$nextTick(() => {
        this.addMagnifyingGlassToThumbnails();
      });
    },
    addMagnifyingGlassToThumbnails() {
      const dropzoneElement = this.$refs.fileInput?.$el;
      if (!dropzoneElement) {
        return;
      }

      const dropzoneInstance = this.$refs.fileInput?.dropzone;
      const allFiles = dropzoneInstance ? dropzoneInstance.files : [];

      const previews = dropzoneElement.querySelectorAll('.dz-preview:not(.dz-magnifier-added):not(.dz-error)');

      previews.forEach((preview, index) => {
        const imageElement = preview.querySelector('.dz-image img');
        const detailsElement = preview.querySelector('.dz-details');

        if (imageElement && detailsElement && !detailsElement.querySelector('.magnifying-glass-icon')) {
          preview.classList.add('dz-magnifier-added');

          let file = allFiles.find(f => f.previewElement === preview);

          if (!file) {
            file = preview.dropzoneFile || preview._file;
          }

          const fileIndex = file ? allFiles.indexOf(file) : -1;

          if (this.isImageComparisonAnalysis() && this.salsifyProperties.length) {
            preview.classList.add('tall');
            const salsifyPropertySelect = document.createElement('select');
            salsifyPropertySelect.name = 'selected_salsify_properties[]';
            salsifyPropertySelect.classList.add('salsify-property-select');
            salsifyPropertySelect.classList.add('form-control');
            for (const prop of this.salsifyProperties) {
              const option = document.createElement('option');
              option.value = prop;
              option.text = prop;
              salsifyPropertySelect.appendChild(option);
            }
            preview.appendChild(salsifyPropertySelect);
          }

          const magnifyButton = document.createElement('div');
          magnifyButton.className = 'magnifying-glass-icon';
          magnifyButton.innerHTML = '<i class="fas fa-search-plus"></i>';
          magnifyButton.title = 'View full size';

          magnifyButton.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();

            let imageSrc = imageElement.src;
            if (file) {
              if (file.dataURL) {
                imageSrc = file.dataURL;
              } else if (file instanceof File || file instanceof Blob) {
                imageSrc = URL.createObjectURL(file);
              }
            }

            const currentFiles = this.$refs.fileInput?.dropzone?.files || [];
            const currentIndex = file ? currentFiles.indexOf(file) : -1;
            this.openLightbox(imageSrc, currentIndex >= 0 ? currentIndex : 0);
          });

          const filenameDiv = detailsElement.querySelector('.dz-filename');
          if (filenameDiv && filenameDiv.nextSibling) {
            detailsElement.insertBefore(magnifyButton, filenameDiv.nextSibling);
          } else {
            detailsElement.appendChild(magnifyButton);
          }
        }
      });

      this.addRemoveButtonToErrorFiles();
    },
    addRemoveButtonToErrorFiles() {
      const dropzoneElement = this.$refs.fileInput?.$el;
      if (!dropzoneElement) return;

      const dropzoneInstance = this.$refs.fileInput?.dropzone;
      if (!dropzoneInstance) return;

      const errorPreviews = dropzoneElement.querySelectorAll('.dz-preview.dz-error:not(.dz-error-btn-added)');

      errorPreviews.forEach((preview) => {
        preview.classList.add('dz-error-btn-added');

        const file = dropzoneInstance.files.find(f => f.previewElement === preview);

        const removeBtn = document.createElement('div');
        removeBtn.className = 'error-remove-btn';
        removeBtn.innerHTML = '×';
        removeBtn.title = 'Remove file';

        removeBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();

          if (file) {
            dropzoneInstance.removeFile(file);
          } else {
            preview.remove();
          }
        });

        preview.appendChild(removeBtn);
      });
    },
    openLightbox(imageSrc, index = 0) {
      const dropzoneInstance = this.$refs.fileInput?.dropzone;
      const allFiles = dropzoneInstance ? dropzoneInstance.files : [];

      this.lightboxImages = allFiles.map(file => {
        if (file.dataURL) {
          return file.dataURL;
        } else if (file instanceof File || file instanceof Blob) {
          return URL.createObjectURL(file);
        }
        return null;
      }).filter(src => src !== null);

      this.currentLightboxIndex = index;
      this.lightboxImage = this.lightboxImages[index] || imageSrc;
      this.showLightbox = true;

      document.addEventListener('keydown', this.handleLightboxKeydown);
    },
    closeLightbox() {
      this.showLightbox = false;
      this.lightboxImage = null;
      this.lightboxImages = [];
      this.currentLightboxIndex = 0;

      document.removeEventListener('keydown', this.handleLightboxKeydown);
    },
    nextImage() {
      if (this.currentLightboxIndex < this.lightboxImages.length - 1) {
        this.currentLightboxIndex++;
        this.lightboxImage = this.lightboxImages[this.currentLightboxIndex];
      }
    },
    prevImage() {
      if (this.currentLightboxIndex > 0) {
        this.currentLightboxIndex--;
        this.lightboxImage = this.lightboxImages[this.currentLightboxIndex];
      }
    },
    handleLightboxKeydown(e) {
      if (!this.showLightbox) return;

      if (e.key === 'ArrowLeft') {
        this.prevImage();
      } else if (e.key === 'ArrowRight') {
        this.nextImage();
      } else if (e.key === 'Escape') {
        this.closeLightbox();
      }
    },
    downloadLightboxImage() {
      if (!this.lightboxImage) return;

      const link = document.createElement('a');
      link.href = this.lightboxImage;
      link.download = 'image-' + Date.now() + '.jpg';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    convertToBase64(url) {
      if (url.startsWith("data:")) {
        return url;
      }

      return new Promise((resolve, reject) => {
        fetch(url)
          .then((response) => response.blob())
          .then((blob) => {
            const reader = new FileReader();
            reader.readAsDataURL(blob);
            reader.onloadend = () => {
              const base64data = reader.result;
              resolve(base64data);
            };
            reader.onerror = (error) => {
              reject(error);
            };
          })
          .catch((error) => {
            console.error("Error converting to base64:", error);
            reject(error);
          });
      });
    },

    isValidJSON(jsonString) {
      try {
        JSON.parse(jsonString);
        return true;
      } catch (e) {
        return false;
      }
    },

    processAudienceInPrompt(prompt) {
      if (!this.selectedAudiences || this.selectedAudiences.length === 0) {
        return prompt;
      }

      const audienceDescription = this.selectedAudiences[0]?.description;

      if (!audienceDescription || audienceDescription.trim() === '') {
        return prompt;
      }

      if (/%audience%/i.test(prompt)) {
        return prompt.replace(/%audience%/gi, audienceDescription);
      } else {
        const audiencePrefix = `Key shopper insight when performing evaluation: Your target market is ${audienceDescription}\n\n`;
        return audiencePrefix + prompt;
      }
    },

    async loadCustomPrompts() {
      this.customPrompts = ["loading"];
      try {
        const response = await axios.get('/banner/groups/index_custom_prompts', {
          params: {
            type: [CUSTOM_PROMPT_TYPE_ANALYTICS,CUSTOM_PROMPT_TYPE_IMAGE_REORDER,CUSTOM_PROMPT_TYPE_IMAGE_COMPARISON],
            page: 1,
            paginate: false
          }
        });
        
        this.customPrompts = [];
        for (const prompt of response.data) {
          if (!prompt || !prompt.id || !prompt.name || !prompt.prompt_start) {
            console.warn("Invalid prompt data:", prompt);
            continue;
          }
          let applyToPrevious = false;
          let applyToImageNumber = null;
          if (prompt.prompt_start_variations && Array.isArray(prompt.prompt_start_variations)) {
            const variationData = prompt.prompt_start_variations.find(variation => variation.id === String(prompt.id).split('_').pop());
            if (variationData) {
              applyToPrevious = !!variationData.apply_to_previous;
              applyToImageNumber = variationData.apply_to_image_number || null;
            }
          }
          this.customPrompts.push({
            id: prompt.id,
            name: prompt.name,
            launch_url: prompt.launch_url || null,
            display_name: prompt.display_name || prompt.name,
            short_description: prompt.short_description || '',
            icon: prompt.icon || '',
            color: prompt.color || '',
            tag: prompt.tag || '',
            prompt: prompt.prompt_start
              .replaceAll("[current-date]", new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })),
            analysisFormat: prompt.response_format && prompt.response_format.structuredOutputSchema ? prompt.response_format : null,
            summaryCriteria: prompt.summary_criteria,
            includeSelectedTempalteImage: prompt.settings && prompt.settings['include_selected_template_image'] ? true : false,
            includeSelectedTemplateProductImages: prompt.settings && prompt.settings['include_selected_template_product_images'] ? true : false,
            includeAllTemplatesImages: prompt.settings && prompt.settings['include_all_templates_images'] ? true : false,
            unpackImagesOpenCV: prompt.settings && prompt.settings['unpack_images_opencv'] ? true : false,
            compare_two_images: prompt.settings && prompt.settings['compare_two_images'] ? true : false,
            compare_two_products: prompt.settings && prompt.settings['compare_two_products'] ? true : false,
            show_variations: prompt.settings && prompt.settings['show_variations'] ? true : false,
            apply_each_variation: prompt.settings && prompt.settings['apply_each_variation'] ? true : false,
            apply_variation_to_each_image: prompt.settings && prompt.settings['apply_variation_to_each_image'] ? true : false,
            use_openai_websearch: prompt.settings && prompt.settings['use_openai_websearch'] ? true : false,
            max_images_to_process: prompt.settings && prompt.settings['max_images_to_process'] ? prompt.settings['max_images_to_process'] : 0,
            skip_images_from_parsing: prompt.settings && prompt.settings['skip_images_from_parsing'] ? prompt.settings['skip_images_from_parsing'] : 0,
            join_images_and_text: prompt.settings && prompt.settings['join_images_and_text'] ? true : false,
            label_ugc_images: prompt.settings && prompt.settings['label_ugc_images'] ? true : false,
            generate_heatmap: prompt.settings && prompt.settings['generate_heatmap'] ? true : false,
            screenshot_pdp_images: prompt.settings && prompt.settings['screenshot_pdp_images'] ? 1 : 0,
            heatmap_method: prompt.settings && prompt.settings['heatmap_method'] ? prompt.settings['heatmap_method'] : null,
            heatmap_prompt_ai_and_heatmapjs: prompt.settings && prompt.settings['heatmap_prompt_ai_and_heatmapjs'] ? prompt.settings['heatmap_prompt_ai_and_heatmapjs'] : null,
            heatmap_prompt_gpt_image: prompt.settings && prompt.settings['heatmap_prompt_gpt_image'] ? prompt.settings['heatmap_prompt_gpt_image'] : (prompt.settings && prompt.settings['heatmap_prompt'] ? prompt.settings['heatmap_prompt'] : null),
            responseFormat: prompt.response_format ? prompt.response_format : null,
            model: prompt.model,
            variations: prompt.variations ? prompt.variations : [],
            is_variation: prompt.is_variation ? true : false,
            apply_to_previous: applyToPrevious,
            apply_to_image_number: applyToImageNumber,
            pdp_title_and_copy_assessment: prompt.settings && prompt.settings['pdp_title_and_copy_assessment'] ? true : false,
            type: prompt.type,
            isMobileLegibility: prompt.settings && prompt.settings['mobile_legibility_report'] ? true : false,
            dimensions_report: prompt.settings && prompt.settings['dimensions_report'] ? true : false,
          });
        }
      } catch (error) {
        console.error("Error loading custom prompts:", error);
        toastr.error("Failed to load analysis prompts");
        this.customPrompts = [];
      } finally {
        window.__optixLabPrompts = this.customPrompts.filter(p => p.id && p.name);
        this.$nextTick(() => {
          this.initializeLucideIcons();
        });
      }
    },

    initializeLucideIcons() {
      if (typeof window.lucide === 'undefined') {
        const script = document.createElement('script');
        script.src = 'https://unpkg.com/lucide@latest';
        script.onload = () => {
          if (window.lucide) {
            window.lucide.createIcons();
          }
        };
        document.head.appendChild(script);
      } else {
        window.lucide.createIcons();
      }
    },

    handleUnhandledRejection(event) {
      console.error("Unhandled rejection:", event.reason);
      if (this.isAnalyzing) {
        this.isAnalyzing = false;
      }
    },
    
    previousStep() {
      if (this.step > 2) {
        this.step--;
        switch (this.step) {
          case 2:
            this.file = [];
            break;
        }
      }
    },
    
    async nextStep() {
      if (this.isLoadingFileBrowserImages) {
        return;
      }

      if (this.pendingTiffConversions > 0) {
        return;
      }

      if (this.isImageComparisonAnalysis()){
        // force to start in product selection tab
        if (this.step === 2){
          this.activeTab = 1;
        }
        if (this.step === 3 && this.activeTab === 1){
          this.activeTab = 0;
          return;
        }
      }

      if (this.step === 3 && this.isShopperInsightsReport()){
       this.saveCurrentSettings(true);
       if (!this.isShopperInsightsFormComplete()){
        return;
       }
      }

      if (this.step < 3 && this.isShopperInsightsReport()){
        this.loadSavedSettings(true);
      }else{
        if ((this.step === 2 || this.step === 3) && this.isSaveSettings) {
          this.saveCurrentSettings();
        }
      }

      if (this.step === 3) {
        if (this.viewMode === 'list' && this.selectedPromptsListView.length > 0) {
          this.selectedPromptIds = this.selectedPromptsListView.map(p => p.id);
        }
        this.processAllURLInputs();
        this.analysisReportUrl = null;
        this.analysisError = false;
        this.errorMessage = null;
      }
      this.step++;
      if (this.step === 3) {
        this.loadPresets();
      }
      switch (this.step) {
        case 4:
          for (let key in this.selectedPrompts) {
            this.selectedPrompt = null;
            this.selectedPrompt = this.selectedPrompts[key];
            if (this.isImageComparisonAnalysis()) {
              if (!this.selectedProducts?.length > 0) {
                toastr.error(`Plase select a product from the list on Step 1`);
                return;
              }
            }
            await this.runAnalysis(key);
            this.reportsProcessed = key;
          }
          break;
      }
    },
    async loadPresets() {
      if (this.presets && this.presets.length > 0) {
        for (const item of this.presets) {
          try {
            let fetchUrl;
            if (item.thumbnail.startsWith('/temp/') || item.thumbnail.startsWith('http://') || item.thumbnail.startsWith('https://')) {
              fetchUrl = item.thumbnail.startsWith('/') ? `${window.location.origin}${item.thumbnail}` : item.thumbnail;
            } else {
              fetchUrl = `${window.location.origin}/share?file=${item.thumbnail}`;
            }
            const response = await fetch(fetchUrl);
            const blob = await response.blob();

            const reader = new FileReader();
            reader.readAsDataURL(blob);
            await new Promise((resolve) => {
              reader.onloadend = () => {
                if (!this.imageAPreviewUrl) {
                  this.imageAPreviewUrl = reader.result;
                } else if (this.isComparisonAnalysis() && !this.imageBPreviewUrl) {
                  this.imageBPreviewUrl = reader.result;
                }
                resolve();
              };
            });

            const file = new File([blob], item.name, {
              type: this.getFileType(item.name, item.original_path, item.type),
            });

            this.$refs.fileInput.addFile(file);
          } catch (error) {
            console.error("Error processing file from browser:", error);
            toastr.error(`Failed to process file: ${item.name}`);
          }
        }

        this.presets = [];
        localStorage.setItem('optix-lab-presets', JSON.stringify([]));
      }
    },
    startNewAnalysis() {
      this.clearRerunData();
      
      if (this.$refs.fileInput) {
        this.$refs.fileInput.removeAllFiles(true);
      }

      this.step = 2;
      this.file = [];
      this.urlInput = "";
      this.comparisonMultipleInput = "";
      this.compareWithProductUrl = false;
      this.comparisonProductUrl = "";
      this.comparisonMultipleMode = true;
      this.manualItemUrl = "";
      this.categoryUrls = [''];
      this.categoryUrlsText = "";
      this.isAnalyzing = false;
      this.analysisReportUrl = null;
      this.analysisReports = [];
      this.enableSpecificImages = false;
      this.specificImageNumbers = "";
      this.previousAutoPopulatedUrl = "";

      this.analysisError = false;
      this.errorMessage = null;
      
      this.backgroundProcesses = [];
      this.reportsParsed = [];

      this.multipleUrlsInput = "";
      this.productUrls = [''];
      this.productUrlsText = '';
      this.allProductUrls = [];

      if (this.imageAPreviewUrl && this.imageAPreviewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(this.imageAPreviewUrl);
      }

      if (this.imageBPreviewUrl && this.imageBPreviewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(this.imageBPreviewUrl);
      }

      this.imageAPreviewUrl = null;
      this.imageBPreviewUrl = null;

      this.$nextTick(() => {
        if (this.$refs.uploadContainer) {
          this.$refs.uploadContainer.focus();
        }
      });
    },
    
    async retryAnalysis() {
      this.isAnalyzing = true;
      this.analysisReportUrl = null;
      this.analysisError = false;
      this.errorMessage = null;

      try {
        await this.runCustomPromptAnalysis();
        toastr.success("Analysis re-run successfully");
      } catch (error) {
        console.error("Analysis retry failed:", error);
      } finally {
        this.isAnalyzing = false;
      }
    },
    
    async saveCurrentSettings(skipToastrMessage = false) {
      this.isSavingSettings = true;
      try {
        await axios.post("/dashboard/image-analyze-settings", {
          settings: this.currentSettings,
        });
        if (!skipToastrMessage)
          toastr.success("Settings saved successfully");
        this.hasSavedSettings = true;
      } catch (error) {
        if (!skipToastrMessage)
        console.error("Error saving settings:", error);
      } finally {
        this.isSavingSettings = false;
      }
    },
    
    async loadSavedSettings(ignoreSelectedPrompts = false) {
      this.isLoadingSettings = true;
      try {
        const response = await axios.get("/dashboard/image-analyze-settings");

        if (response.data && response.data.status) {
          this.selectedImageType = response.data.selectedImageType;
          if (!ignoreSelectedPrompts){
            this.selectedPromptIds.push(...response.data.selectedPromptId);
          } 
          this.shopperInsightsFormValues = {
              ...this.shopperInsightsFormValues, 
              ...(response.data.shopperInsightsFormValues || {}) 
          };
          this.hasSavedSettings = true;
        } else {
          this.hasSavedSettings = false;
        }
      } catch (error) {
        if (error.response?.status === 404) {
          this.hasSavedSettings = false;
        } else {
          console.error("Error loading settings:", error);
        }
      } finally {
        this.isLoadingSettings = false;
      }
    },
    
    handleSaveSettings() {
      this.saveCurrentSettings();
    },

    loadCustomerName() {
      if (this.user && this.user.company && this.user.company.default_customer_name) {
        this.customerName = this.user.company.default_customer_name;
      }
    },

    async saveCustomerName() {
      if (!this.user || !this.user.company) {
        return;
      }

      // Only save if the value has changed
      const currentValue = this.user.company.default_customer_name || '';
      if (this.customerName === currentValue) {
        return;
      }

      try {
        await axios.post('/dashboard/default-customer-name', {
          default_customer_name: this.customerName,
        });

        // Update the local user data
        if (this.user.company) {
          this.user.company.default_customer_name = this.customerName;
        }
      } catch (error) {
        console.error('Error saving customer name:', error);
      }
    },

    onFileAdded(file) {
      this.setFile(false, file);
      this.$nextTick(() => {
        setTimeout(() => {
          this.addMagnifyingGlassToThumbnails();
        }, 100);
      });
    },
    onThumbnailGenerated(file) {
      this.$nextTick(() => {
        setTimeout(() => {
          this.addMagnifyingGlassToThumbnails();
        }, 100);
      });
    },
    async setFile(isSecondary = false, addedFile = null) {
      await this.$nextTick();

      if (!this.$refs.fileInput) {
        return;
      }

      const files = this.$refs.fileInput.getAcceptedFiles() || [];

      // Always keep the current Dropzone files in sync.
      this.file = files;

      // When setFile is called from vdropzone-removed-file,
      // do not process another existing file by accident.
      if (!addedFile) {
        if (files.length === 0) {
          this.file = [];
          this.imageAPreviewUrl = null;
          this.imageBPreviewUrl = null;
        }

        setTimeout(() => {
          this.initializeDragAndDrop(isSecondary);
        }, 200);

        return;
      }

      // Process exactly the file that triggered vdropzone-file-added.
      const fileA = addedFile;

      let fileName = fileA.name || '';

      if (!fileName && fileA.upload && fileA.upload.filename) {
        fileName = fileA.upload.filename;
      }

      const normalizedFileName = fileName.toLowerCase();

      /*
       * XLSX
       */
      if (normalizedFileName.endsWith('.xlsx')) {
        const reader = new FileReader();

        reader.onload = (e) => {
          try {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: 'array' });

            const visibleSheetNames = workbook.SheetNames.filter((name, index) => {
              const meta = workbook.Workbook?.Sheets?.[index];

              return (
                !meta ||
                meta.Hidden === 0 ||
                meta.Hidden === undefined
              );
            });

            const firstSheetName = visibleSheetNames[0];

            if (!firstSheetName) {
              return;
            }

            const worksheet = workbook.Sheets[firstSheetName];

            const rows = XLSX.utils.sheet_to_json(
              worksheet,
              { header: 1 }
            );

            this.urlsToScrape = [];

            rows.forEach((row) => {
              const url = row[0];

              if (url && typeof url === 'string') {
                this.urlsToScrape.push(url);
              }
            });
          } catch (error) {
            console.error('Error processing XLSX file:', error);
          }
        };

        reader.onerror = (error) => {
          console.error('Error reading XLSX file:', error);
        };

        reader.readAsArrayBuffer(fileA);
      }

      /*
       * PDF
       */
      else if (
        fileA.type === 'application/pdf' ||
        normalizedFileName.endsWith('.pdf')
      ) {
        console.log('[PDF] Processing selected file:', {
          name: fileA.name,
          type: fileA.type,
          size: fileA.size,
        });

        const reader = new FileReader();

        reader.onload = async (e) => {
          try {
            const pdfData = new Uint8Array(e.target.result);

            const pdf = await pdfjsLib
              .getDocument({ data: pdfData })
              .promise;

            if (pdf.numPages > 5) {
              this.$bvToast.toast(
                'PDF files must have 5 pages or less.',
                {
                  title: 'Error',
                  variant: 'danger',
                  solid: true,
                }
              );

              this.$refs.fileInput.removeFile(fileA);

              return;
            }

            let shouldUnpack = false;
            let shouldDetectPdfImageLabels = false;

            if (typeof Swal !== 'undefined') {
              const result = await Swal.fire({
                title: 'PDF Processing Options',
                html: `
    <div class="pdf-options-list">
      <label
        class="pdf-option-card"
        for="swal-unpack-pdf-images"
      >
        <input
          id="swal-unpack-pdf-images"
          class="pdf-option-checkbox"
          type="checkbox"
          checked
        >

        <span class="pdf-option-content">
          <span class="pdf-option-title">
            Unpack PDF Images
          </span>

          <span class="pdf-option-description">
            Extract individual images from each PDF page.
            Turn this off to use each full page as one image.
          </span>
        </span>
      </label>

      <label
        class="pdf-option-card"
        for="swal-detect-pdf-image-labels"
      >
        <input
          id="swal-detect-pdf-image-labels"
          class="pdf-option-checkbox"
          type="checkbox"
          checked
        >

        <span class="pdf-option-content">
          <span class="pdf-option-title">
            Auto-detect image labels
          </span>

          <span class="pdf-option-description">
            Analyze each PDF page and automatically apply
            detected labels to generated images.
          </span>
        </span>
      </label>
    </div>
  `,
                showCancelButton: true,
                confirmButtonText: 'Process PDF',
                cancelButtonText: 'Cancel',
                reverseButtons: true,
                buttonsStyling: false,
                customClass: {
                  popup: 'pdf-options-popup',
                  title: 'pdf-options-title',
                  htmlContainer: 'pdf-options-content',
                  actions: 'pdf-options-actions',
                  confirmButton: 'pdf-options-confirm',
                  cancelButton: 'pdf-options-cancel',
                },
                preConfirm: () => ({
                  shouldUnpack:
                    document.getElementById(
                      'swal-unpack-pdf-images'
                    )?.checked || false,

                  shouldDetectPdfImageLabels:
                    document.getElementById(
                      'swal-detect-pdf-image-labels'
                    )?.checked || false,
                }),
              });

              if (!result.isConfirmed) {
                this.$refs.fileInput.removeFile(fileA);

                return;
              }

              shouldUnpack = !!result.value?.shouldUnpack;

              shouldDetectPdfImageLabels =
                !!result.value?.shouldDetectPdfImageLabels;
            }

            this.isDetectingPdfImageLabels =
              shouldDetectPdfImageLabels;

            const stepsPerPage =
              2 + (shouldDetectPdfImageLabels ? 1 : 0);

            const totalPdfProcessingSteps =
              pdf.numPages * stepsPerPage;

            let completedPdfProcessingSteps = 0;

            this.showPdfProcessingProgress(
              totalPdfProcessingSteps
            );

            /*
             * Remove only the original PDF.
             *
             * This will trigger vdropzone-removed-file and call
             * setFile(false), but because addedFile is null in that
             * call, the method will only sync this.file and will NOT
             * start processing files[0].
             */
            this.$refs.fileInput.removeFile(fileA);

            for (let i = 1; i <= pdf.numPages; i++) {
              this.updatePdfProcessingProgress(
                completedPdfProcessingSteps,
                totalPdfProcessingSteps,
                `Rendering page ${i} of ${pdf.numPages}...`
              );
              const page = await pdf.getPage(i);

              const viewport = page.getViewport({
                scale: 96 / 72,
              });

              const canvas =
                document.createElement('canvas');

              const context =
                canvas.getContext('2d');

              canvas.height = viewport.height;
              canvas.width = viewport.width;

              await page.render({
                canvasContext: context,
                viewport,
              }).promise;

              completedPdfProcessingSteps++;

              this.updatePdfProcessingProgress(
                completedPdfProcessingSteps,
                totalPdfProcessingSteps,
                shouldUnpack
                  ? `Extracting images from page ${i} of ${pdf.numPages}...`
                  : `Creating image from page ${i} of ${pdf.numPages}...`
              );

              const pageImageDataUrl =
                canvas.toDataURL('image/png');

              const pageLabelsPromise =
                shouldDetectPdfImageLabels
                  ? this.detectPdfPageImageLabels(
                    pageImageDataUrl,
                    i,
                    fileName
                  )
                  : Promise.resolve([]);

              let unpackedImages = [];

              if (shouldUnpack) {
                try {
                  const { data } = await axios.post(
                    '/unpack-images-open-cv',
                    {
                      image_url: pageImageDataUrl,
                    }
                  );

                  if (
                    data &&
                    Array.isArray(data) &&
                    data.length > 0
                  ) {
                    unpackedImages = data;
                  }
                } catch (error) {
                  console.error(
                    `Failed to unpack images from PDF page ${i}`,
                    error
                  );
                }
              }

              const createdFilesForPage = [];

              /*
               * Add extracted images.
               */
              if (unpackedImages.length > 0) {
                try {
                  const imagePromises =
                    unpackedImages.map(
                      async (unpackedImageUrl, index) => {
                        const response =
                          await fetch(unpackedImageUrl);

                        if (!response.ok) {
                          throw new Error(
                            `Failed to fetch unpacked image: HTTP ${response.status}`
                          );
                        }

                        const blob =
                          await response.blob();

                        return new File(
                          [blob],
                          `${fileName.replace(/\.pdf$/i, '')}_page_${i}_part_${index + 1}.png`,
                          {
                            type: 'image/png',
                          }
                        );
                      }
                    );

                  const extractedFiles =
                    await Promise.all(imagePromises);

                  extractedFiles.forEach((file) => {
                    createdFilesForPage.push(file);

                    this.$refs.fileInput.addFile(file);
                  });
                } catch (error) {
                  console.error(
                    'Error fetching unpacked images:',
                    error
                  );
                }
              }

              /*
               * Fall back to the rendered full PDF page.
               */
              if (!createdFilesForPage.length) {
                const blob = await this.canvasToBlob(
                  canvas,
                  'image/png'
                );

                const imageFile = new File(
                  [blob],
                  `${fileName.replace(/\.pdf$/i, '')}_page_${i}.png`,
                  {
                    type: 'image/png',
                  }
                );

                createdFilesForPage.push(imageFile);

                this.$refs.fileInput.addFile(
                  imageFile
                );
              }

              completedPdfProcessingSteps++;

              this.updatePdfProcessingProgress(
                completedPdfProcessingSteps,
                totalPdfProcessingSteps,
                shouldDetectPdfImageLabels
                  ? `Detecting labels for page ${i} of ${pdf.numPages}...`
                  : `Processed page ${i} of ${pdf.numPages}`
              );

              /*
               * Apply detected labels to files generated
               * from the current PDF page.
               */
              if (shouldDetectPdfImageLabels) {
                const pageLabels =
                  await pageLabelsPromise;

                await this.applyPdfPageLabelsToFiles(
                  createdFilesForPage,
                  pageLabels,
                  i
                );

                completedPdfProcessingSteps++;

                this.updatePdfProcessingProgress(
                  completedPdfProcessingSteps,
                  totalPdfProcessingSteps,
                  `Processed page ${i} of ${pdf.numPages}`
                );
              }
            }

            if (shouldDetectPdfImageLabels) {
              this.$bvToast.toast(
                'PDF image labels detected.',
                {
                  title: 'Success',
                  variant: 'success',
                  solid: true,
                }
              );
            }
          } catch (error) {
            console.error(
              'Error processing PDF:',
              fileName,
              error
            );

            this.$bvToast.toast(
              'Error processing PDF file.',
              {
                title: 'Error',
                variant: 'danger',
                solid: true,
              }
            );

            const currentFiles =
              this.$refs.fileInput?.dropzone?.files || [];

            if (currentFiles.includes(fileA)) {
              this.$refs.fileInput.removeFile(
                fileA
              );
            }
          } finally {
            this.isDetectingPdfImageLabels = false;
            this.closePdfProcessingProgress();
          }
        };

        reader.onerror = (error) => {
          console.error(
            'Error reading PDF file:',
            fileName,
            error
          );

          this.isDetectingPdfImageLabels = false;
          this.closePdfProcessingProgress();

          this.$bvToast.toast(
            'Error reading PDF file.',
            {
              title: 'Error',
              variant: 'danger',
              solid: true,
            }
          );
        };

        reader.readAsArrayBuffer(fileA);
      }

      /*
       * TIFF
       */
      else if (this.isDroppedTiffFile(fileA)) {
        this.convertDroppedTiffFile(fileA);
      }

      /*
       * Regular image
       */
      else {
        const reader = new FileReader();

        reader.onload = (e) => {
          /*
           * Keep the first image as the primary preview.
           */
          const currentFiles =
            this.$refs.fileInput?.getAcceptedFiles() || [];

          const fileIndex =
            currentFiles.indexOf(fileA);

          if (
            fileIndex === 0 ||
            !this.imageAPreviewUrl
          ) {
            this.imageAPreviewUrl =
              e.target.result;
          }

          if (
            this.isComparisonAnalysis() &&
            fileIndex === 1
          ) {
            this.imageBPreviewUrl =
              e.target.result;
          }
        };

        reader.onerror = (error) => {
          console.error(
            'Error reading image:',
            fileName,
            error
          );
        };

        reader.readAsDataURL(fileA);
      }

      setTimeout(() => {
        this.initializeDragAndDrop(
          isSecondary
        );
      }, 200);
    },

    showPdfProcessingProgress(totalSteps) {
      if (typeof Swal === 'undefined') {
        return;
      }

      Swal.fire({
        title: 'Processing PDF',
        html: `
      <div class="pdf-processing-progress">
        <div
          id="pdf-processing-status"
          class="pdf-processing-status"
        >
          Preparing PDF...
        </div>

        <div class="pdf-processing-progress-track">
          <div
            id="pdf-processing-progress-bar"
            class="pdf-processing-progress-bar"
            style="width: 0%"
          ></div>
        </div>

        <div class="pdf-processing-progress-meta">
          <span id="pdf-processing-progress-text">0%</span>
        </div>
      </div>
    `,
        showConfirmButton: false,
        showCancelButton: false,
        allowOutsideClick: false,
        allowEscapeKey: false,
        customClass: {
          popup: 'pdf-processing-popup',
          title: 'pdf-processing-title',
          htmlContainer: 'pdf-processing-content',
        },
      });
    },

    updatePdfProcessingProgress(
      completedSteps,
      totalSteps,
      status
    ) {
      if (typeof Swal === 'undefined') {
        return;
      }

      const percent = totalSteps > 0
        ? Math.min(
          100,
          Math.round(
            (completedSteps / totalSteps) * 100
          )
        )
        : 0;

      const bar = document.getElementById(
        'pdf-processing-progress-bar'
      );

      const text = document.getElementById(
        'pdf-processing-progress-text'
      );

      const statusElement = document.getElementById(
        'pdf-processing-status'
      );

      if (bar) {
        bar.style.width = `${percent}%`;
      }

      if (text) {
        text.textContent = `${percent}%`;
      }

      if (statusElement) {
        statusElement.textContent = status;
      }
    },

    closePdfProcessingProgress() {
      if (typeof Swal !== 'undefined') {
        Swal.close();
      }
    },
    
    async useSampleFile() {
      try {
        this.isLoading = true;

        const response = await fetch("/api/placeholder/800/600");
        const blob = await response.blob();

        const reader = new FileReader();
        reader.readAsDataURL(blob);
        await new Promise((resolve) => {
          reader.onloadend = () => {
            if (!this.isComparisonAnalysis()) {
              this.imageAPreviewUrl = reader.result;
            } else {
              this.imageAPreviewUrl = reader.result;
            }
            resolve();
          };
        });

        const sampleFile = new File([blob], "sample-image.jpg", {
          type: "image/jpeg",
        });

        if (this.isComparisonAnalysis()) {
          this.$refs.fileInput.addFile(sampleFile);
        } else {
          this.$refs.fileInput.addFile(sampleFile);
        }
      } catch (error) {
        toastr.error("The sample file is not available");
      } finally {
        this.isLoading = false;
      }
    },
    
    async getImagesFromFileBrowserFolder(folderId, limit = 20) {
      try {
        const { data } = await axios.get('/file/folder_content', {
          params: {
            folderId,
            searchKey: '',
            pageNumber: 1,
            pageSize: limit,
            type: 'image,avif,tiff',
          },
        });

        return (data.items || [])
          .filter(item => {
            if (item.id === null && item.is_root === true) {
              return false;
            }

            return ['image', 'avif'].includes(item.type) || this.isTiffItem(item);
          })
          .slice(0, limit);
      } catch (error) {
        console.error('Failed to load folder images:', error);
        toastr.error('Failed to load images from folder');

        return [];
      }
    },

    async fileBrowserItemSelected(items) {
      if (!items || items.length === 0) return;

      if (window.askOptixFileBrowserMode) {
        window.askOptixFileBrowserMode = false;

        window.dispatchEvent(
          new CustomEvent('askOptix:fileBrowserSelected', {
            detail: items,
          })
        );

        return;
      }

      if (this.selectFolderMode === SELECT_FOLDER_MODE.DEFAULT) {
        const maxFiles = 20;

        const existingFiles =
          this.$refs.fileInput?.getAcceptedFiles()?.length || 0;

        const remainingSlots = Math.max(
          0,
          maxFiles - existingFiles
        );

        if (remainingSlots === 0) {
          toastr.warning(`Maximum ${maxFiles} images allowed`);
          return;
        }

        let itemsToAdd = [];

        for (const item of items) {
          if (itemsToAdd.length >= remainingSlots) {
            break;
          }

          if (item.type === 'folder') {
            const folderImages =
              await this.getImagesFromFileBrowserFolder(
                item.id,
                remainingSlots - itemsToAdd.length
              );

            itemsToAdd.push(...folderImages);
          } else {
            itemsToAdd.push(item);
          }
        }

        itemsToAdd = itemsToAdd.slice(0, remainingSlots);

        if (!itemsToAdd.length) {
          toastr.warning('No images found in the selected folder');
          return;
        }

        this.isLoadingFileBrowserImages = true;
        this.fileBrowserImagesLoaded = 0;
        this.fileBrowserImagesTotal = itemsToAdd.length;

        try {
          for (const item of itemsToAdd) {
            try {
              const isTiff = this.isTiffItem(item);

              const response = await fetch(this.fileBrowserItemUrl(item));

              if (!response.ok) {
                throw new Error(
                  `Failed to load ${item.name}: HTTP ${response.status}`
                );
              }

              const blob = await response.blob();

              if (!blob || blob.size === 0) {
                throw new Error(`Empty file returned for ${item.name}`);
              }

              const fileType =
                blob.type ||
                this.getFileType(
                  item.name,
                  item.original_path,
                  item.type
                );

              if (fileType.startsWith('image/')) {
                await this.validateImageBlob(blob, item.name);
              }

              const fileName = isTiff
                ? item.name.replace(/\.tiff?$/i, '') + '.png'
                : item.name;

              const file = new File(
                [blob],
                fileName,
                {
                  type: fileType,
                }
              );

              this.$refs.fileInput.addFile(file);
            } catch (error) {
              console.error(
                'Error processing file from browser:',
                item,
                error
              );

              toastr.error(`Failed to process file: ${item.name}`);
            } finally {
              this.fileBrowserImagesLoaded++;
            }
          }
        } finally {
          this.isLoadingFileBrowserImages = false;
          this.fileBrowserImagesLoaded = 0;
          this.fileBrowserImagesTotal = 0;
        }

        return;
      }

      if (
        this.selectFolderMode ===
        SELECT_FOLDER_MODE.SELECT_AND_CREATE
      ) {
        this.uploadFilesToFolder(items);
      }
    },

    validateImageBlob(blob, fileName = '') {
      return new Promise((resolve, reject) => {
        const objectUrl = URL.createObjectURL(blob);
        const image = new Image();

        image.onload = () => {
          URL.revokeObjectURL(objectUrl);
          resolve();
        };

        image.onerror = () => {
          URL.revokeObjectURL(objectUrl);

          reject(
            new Error(
              `Browser could not decode image "${fileName}", blob type: ${blob.type}, size: ${blob.size}`
            )
          );
        };

        image.src = objectUrl;
      });
    },

    layoutItemSelected(items) {
      if (!items || !items?.length) return;

      for (const item of items) {

        this.$refs.fileInput.addFile(item.originalFile);
      }
    },

    async downloadImageAsDataURL(imageUrl, reduceQuality = false) {
      let response = await fetch(imageUrl);

      if (!response.ok) {
          response = await fetch('/share?file=' + imageUrl);
          if (!response.ok) {
              throw new Error(`Failed to fetch image: ${response.statusText}`);
          }
      }

      const blob = await response.blob();

      if (!reduceQuality) {
          return new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result);
              reader.onerror = reject;
              reader.readAsDataURL(blob);
          });
      }

      // If reduceQuality is true, resize image
      return new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = () => {
              const aspectRatio = img.height / img.width;
              const newWidth = 640;
              const newHeight = newWidth * aspectRatio;

              const canvas = document.createElement('canvas');
              canvas.width = newWidth;
              canvas.height = newHeight;
              const ctx = canvas.getContext('2d');
              ctx.drawImage(img, 0, 0, newWidth, newHeight);

              resolve(canvas.toDataURL('image/jpeg', 0.8)); // You can tweak quality here (0-1)
          };
          img.onerror = reject;
          img.src = URL.createObjectURL(blob);
      });
    },

    async downloadImageWithBothQualities(imageUrl) {
      // Download image and create both high-res and low-res versions
      let response = await fetch(imageUrl);

      if (!response.ok) {
          response = await fetch('/share?file=' + imageUrl);
          if (!response.ok) {
              throw new Error(`Failed to fetch image: ${response.statusText}`);
          }
      }

      const blob = await response.blob();
      
      // Create high-res version
      const highResPromise = new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
      });

      // Create low-res version
      const lowResPromise = new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = () => {
              const aspectRatio = img.height / img.width;
              const newWidth = 640;
              const newHeight = newWidth * aspectRatio;

              const canvas = document.createElement('canvas');
              canvas.width = newWidth;
              canvas.height = newHeight;
              const ctx = canvas.getContext('2d');
              ctx.drawImage(img, 0, 0, newWidth, newHeight);

              resolve(canvas.toDataURL('image/jpeg', 0.8));
          };
          img.onerror = reject;
          img.src = URL.createObjectURL(blob);
      });

      const [highRes, lowRes] = await Promise.all([highResPromise, lowResPromise]);
      
      return {
          highRes: highRes,
          lowRes: lowRes
      };
    },

    getPresetThumbnailUrl(thumbnail) {
      if (!thumbnail) return '';
      if (thumbnail.startsWith('/temp/') || thumbnail.startsWith('http://') || thumbnail.startsWith('https://')) {
        return thumbnail;
      }
      return `/share?file=${thumbnail}`;
    },

    isTiffItem(item) {
      const type = String(item?.type || '').toLowerCase();
      const extension = String(item?.extension || '').toLowerCase();

      return ['tif', 'tiff'].includes(type) || ['tif', 'tiff'].includes(extension);
    },

    fileBrowserItemUrl(item) {
      if (this.isTiffItem(item)) {
        return `${window.location.origin}/share?file=${item.original_path}&lightweight=1&scale_width=2048&format=png`;
      }

      return `${window.location.origin}/share?file=${item.original_path}`;
    },

    isDroppedTiffFile(file) {
      const fileName = String(file?.name || '').toLowerCase();

      return file?.type === 'image/tiff' || fileName.endsWith('.tif') || fileName.endsWith('.tiff');
    },

    isFileStillInDropzone(file) {
      return !!this.$refs.fileInput?.getAcceptedFiles()?.includes(file);
    },

    // addFile() always appends to the end of Dropzone's internal files array, so a plain
    // addFile()+removeFile() swap moves the replacement to the end of a mixed batch instead of
    // keeping it where the original was. Image-order-dependent features (isImageOrderAnalysis(),
    // specificImageNumbers) read that array by position, so the swap has to preserve the index.
    replaceFileInDropzone(originalFile, replacementFile) {
      const dropzone = this.$refs.fileInput?.dropzone;
      const originalIndex = dropzone ? dropzone.files.indexOf(originalFile) : -1;

      this.$refs.fileInput.addFile(replacementFile);

      if (dropzone && originalIndex !== -1) {
        const replacementIndex = dropzone.files.indexOf(replacementFile);

        if (replacementIndex !== -1 && replacementIndex !== originalIndex) {
          dropzone.files.splice(replacementIndex, 1);
          dropzone.files.splice(originalIndex, 0, replacementFile);
        }

        if (replacementFile.previewElement && originalFile.previewElement?.parentNode) {
          originalFile.previewElement.parentNode.insertBefore(replacementFile.previewElement, originalFile.previewElement);
        }
      }

      this.$refs.fileInput.removeFile(originalFile);
    },

    async convertDroppedTiffFile(fileA) {
      this.pendingTiffConversions++;

      try {
        const formData = new FormData();
        formData.append('image', fileA);

        const { data } = await axios.post('/svg/convert-image?format=png', formData);

        if (!this.isFileStillInDropzone(fileA)) {
          return;
        }

        const response = await fetch(data.path);

        if (!response.ok) {
          throw new Error(`Failed to load converted TIFF: HTTP ${response.status}`);
        }

        const blob = await response.blob();

        if (!this.isFileStillInDropzone(fileA)) {
          return;
        }

        const pngFileName = fileA.name.replace(/\.tiff?$/i, '') + '.png';
        const pngFile = new File([blob], pngFileName, { type: 'image/png' });

        this.replaceFileInDropzone(fileA, pngFile);
      } catch (error) {
        console.error('Error converting TIFF file:', fileA, error);

        if (this.isFileStillInDropzone(fileA)) {
          toastr.error(`Failed to process file: ${fileA.name}`);
          this.$refs.fileInput.removeFile(fileA);
        }
      } finally {
        this.pendingTiffConversions--;
      }
    },

    getFileType(filename, originalPath = null, type = null) {
      let extension = filename.includes('.') ? filename.split(".").pop().toLowerCase() : originalPath.split(".").pop().toLowerCase();
      if (type) extension = type;
      switch (extension) {
        case "jpg":
        case "jpeg":
          return "image/jpeg";
        case "png":
          return "image/png";
        case "gif":
          return "image/gif";
        case "webp":
          return "image/webp";
        case "avif":
          return "image/avif";
        case "pdf":
          return "application/pdf";
        default:
          return "image/jpeg";
      }
    },
    
    async runAnalysis(currentIndex = 0) {
      if (this.pendingTiffConversions > 0) {
        toastr.warning('Please wait for TIFF conversion to finish before generating reports');
        return;
      }

      this.isAnalyzing = true;

      this.collectedReportIds = [];
      this.isLastMultipleUrl = false;

      if (!this.currentProductRecordId && this.selectedProductRecordIds && this.selectedProductRecordIds.length > 0) {
        this.currentProductRecordId = this.selectedProductRecordIds[0];
      }

      if (this.hasMultipleUrls) {
        const urlText = this.multipleUrlsInput;
        if (!urlText.trim()) {
          toastr.error('Please enter URLs to process');
          return;
        }
        let regex = /[\s\n\r,]+/
        if (urlText.includes('rossmann.pl')) {
          regex = /[\s\n\r]+/
        }
        this.urlsToScrape = urlText.split(regex).filter(url => url.trim());

        if (this.urlsToScrape.length > 1 && !this.selectedGroupId && !this.createNewGroup) {
          const autoGroupName = this.generateAutoGroupName();
          if (autoGroupName) {
            this.createNewGroup = true;
            this.newGroupName = autoGroupName;
          }
        }
      }

      // Mixed uploaded-assets vs product-URL comparison is handled by the backend
      // so the two sources stay in separate A/B sets.
      // The dimensions report has no foreground path at all: it is built entirely
      // by the queued job, and the synchronous branch would send its placeholder
      // prompt_start to GPT-4o and produce a nonsense generic-markdown report.
      // So it forces the background job even when the user unchecks the box.
      const useBackgroundJob = this.isBackgroundJob
        || this.isDimensionsReport()
        || (this.isProductComparisonAnalysis() && this.compareWithProductUrl);

      //ignore gap-analysis-summary report
      if (useBackgroundJob && this.selectedPrompt?.analysisFormat?.type !== 'gap-analysis-summary') {
        const formData = new FormData();
        let promptId = this.selectedPrompt.id;
        let variationId = 0;
        if (this.selectedPrompt.is_variation) {
          promptId = promptId.split('_').shift();
          variationId = this.selectedPrompt.id.split('_').pop();
        }

        formData.append('custom_prompt_id', promptId);
        formData.append('variation_prompt_id', variationId);
        formData.append('is_reduce_image_quality', this.isReduceImageQuality ? 1 : 0);
        formData.append('url_input', this.multipleUrlsInput);
        formData.append('multiple_urls_mode', false);
        formData.append('is_comparison_analysis', this.isComparisonAnalysis() ? 1 : 0);
        formData.append('is_visual_playbook_analysis', this.isVisualPlayBookAnalysis() ? 1 : 0);
        // Per-job, NOT isDimensionsReport(): this flag switches the whole report
        // type, so in a multi-prompt run it must come from the prompt being
        // submitted (`selectedPrompt`, the loop variable) rather than from
        // slot 0 of the selection.
        formData.append('is_dimensions_analysis', this.selectedPrompt?.dimensions_report ? 1 : 0);
        formData.append('compare_two_images', this.selectedPrompt.compare_two_images ? 1 : 0);
        formData.append('compare_two_products', this.selectedPrompt.compare_two_products ? 1 : 0);
        formData.append('email_user', this.isBackgroundJob ? 1 : 0);
        formData.append('max_images_to_process', this.selectedPrompt.max_images_to_process ?? 0);
        formData.append('skip_images_from_parsing', this.selectedPrompt.skip_images_from_parsing ?? 0);
        formData.append('join_images_and_text', this.selectedPrompt.join_images_and_text ?? 0);
        formData.append('label_ugc_images', this.selectedPrompt.label_ugc_images ?? 0);
        formData.append('generate_heatmap', this.selectedPrompt.generate_heatmap ?? 0);
        formData.append('heatmap_method', this.selectedPrompt.heatmap_method ?? 'gpt_image');
        formData.append('screenshot_pdp_images', this.selectedPrompt.screenshot_pdp_images);
        formData.append('heatmap_prompt_ai_and_heatmapjs', this.selectedPrompt.heatmap_prompt_ai_and_heatmapjs ?? '');
        formData.append('heatmap_prompt_gpt_image', (this.selectedPrompt.heatmap_prompt_gpt_image ? this.selectedPrompt.heatmap_prompt_gpt_image : this.selectedPrompt.heatmap_prompt_image) ?? null);
        formData.append('screenshot_url', this.screenshotUrl);
        formData.append('use_domain_as_client_name', this.useDomainAsClientName);
        formData.append('parse_pdp_images', this.parsePdpImages);
        this.syncAllEditableTablesRowsFromKpis();
        formData.append(
          'editable_prompt_fields',
          JSON.stringify(this.getSelectedEditablePromptFieldsPayload())
        );
        
        if (this.selectedRetailerPrompt) {
          formData.append('selected_retailer', this.selectedRetailerPrompt)
        }

        const selectedImageIndicesForLabels = this.getSelectedImageIndices();

        const uploadedFilesForLabels = Array.isArray(this.file)
          ? this.file
          : (this.file ? [this.file] : []);

        const uploadedImageTypeLabelFiles = Array.isArray(selectedImageIndicesForLabels)
          ? selectedImageIndicesForLabels
            .filter(index => index >= 0 && index < uploadedFilesForLabels.length)
            .map(index => uploadedFilesForLabels[index])
          : uploadedFilesForLabels;

        const uploadedImageTypeLabelsPayload = this.getUploadedImageTypeLabelsPayload(uploadedImageTypeLabelFiles);

        if (uploadedImageTypeLabelsPayload.length > 0) {
          formData.append(
            'uploaded_image_type_labels',
            JSON.stringify(uploadedImageTypeLabelsPayload)
          );
        }
        
        if (this.screenshotUrl) {
          await this.loadProductLists(true);
          
          if (this.availableProducts.length) {
            const matchedProduct = this.screenshotUrl && this.availableProducts
              ? this.availableProducts.find(product =>
                [
                  product.asin,
                  product.tcin,
                  product.walmart_id,
                  product.url,
                ].some(value => value && this.screenshotUrl.includes(String(value)))
              )
              : null;

            if (matchedProduct) {
              formData.append('product_id_list_records_id[]', matchedProduct.id)
            }
          }
        }

        if (this.isImageOrderAnalysis()) {

          this.selectedProducts.forEach(product => {
            if (product.salsify_id) {
              formData.append('salsify_id_list[]', product.salsify_id);
            }
          });

          this.selectedProductLists.forEach(list => {
            if (list.name) {
              formData.append('product_list_names[]', list.name);
              formData.append('product_list_ids[]', list.id);
            }
          });
        }
        
        const comparisonInputForRequest = (
          this.isProductComparisonAnalysis() && this.compareWithProductUrl
        )
          ? this.comparisonProductUrl.trim()
          : this.comparisonMultipleInput;

        if (this.isComparisonAnalysis()) {
          comparisonInputForRequest
            .split(/[\s\n\r]+/)
            .filter(url => url.trim())
            .forEach((url, index) => {
              formData.append('url_input_' + (index + 1), url);
            });
        }
        
        if (this.isProductComparisonAnalysis()) {
          formData.append('comparisonMultipleInput', comparisonInputForRequest);
          formData.append('is_product_comparison_analysis', 1);
          formData.append(
            'compare_uploaded_with_product_url',
            this.compareWithProductUrl ? 1 : 0
          );
        }

        if (this.isImageComparisonAnalysis()) {
          const selects = document.querySelectorAll('.salsify-property-select.form-control');
          selects.forEach(select => {
            formData.append(select.name, select.value);
          });
        }

        if (this.isShopperInsightsReport()) {
          formData.append('shopper_insights_form_values', JSON.stringify(this.shopperInsightsFormValues));
        }
        
        if (this.isVisualPlayBookAnalysis()) {
          if (!this.validatePlaybookReport()) {
            return;
          }

          if (this.inputMode === 'search') {
            formData.append('search_keyword', this.searchKeyword);
            formData.append('include_brands', this.includeBrands)
            formData.append('exclude_brands', this.excludeBrands)
          } else if (this.inputMode === 'products') {
            this.productUrls
              .filter(url => url.trim())
              .map(url => {
                formData.append('product_urls[]', this.convertAsinToAmazonUrl(url.trim()));
              });
          } else if (this.inputMode === 'category') {
            this.categoryUrls.forEach(url => {
              if (url.trim()) {
                formData.append('category_urls[]', url.trim());
              }
              formData.append('include_brands', this.includeBrands)
              formData.append('exclude_brands', this.excludeBrands)
            });
          }


          if (this.inputMode !== 'products') {
            formData.append('count_results', this.countResults);
          }
          formData.append('selected_retailer', this.selectedRetailer);
          formData.append('max_images', this.maxImagesPerProduct);

          if (this.visualBenchmarkReportName && this.visualBenchmarkReportName.trim()) {
            formData.append('report_name', this.visualBenchmarkReportName.trim());
          }
        }

        this.urlsToScrape.forEach(url => {
          formData.append('urls_to_scrape[]', url);
        })

        // Handle specific image selection
        const selectedIndices = this.getSelectedImageIndices();
        if (selectedIndices === null) {
          // Use all images
          for (let i = 0; i < this.file.length; i++) {
            formData.append('images[]', this.file[i]);
          }
        } else {
          selectedIndices.forEach(index => {
            if (index >= 0 && index < this.file.length) {
              formData.append('images[]', this.file[index]);
            }
          });
        }

        if (this.manualItemUrl && this.manualItemUrl.trim()) {
          formData.append('manual_item_url', this.manualItemUrl.trim());
        }

        if (this.selectedGroupId || (this.createNewGroup && this.newGroupName)) {
          if (this.selectedGroupId) {
            formData.append('analytics_history_group_id', this.selectedGroupId);
          } else if (this.createNewGroup && this.newGroupName) {
            formData.append('new_group_name', this.newGroupName.trim());
          }
        }

        if (this.enableSpecificImages && this.specificImageNumbers && this.specificImageNumbers.trim()) {
          formData.append('enable_specific_images', true);
          formData.append('specific_image_numbers', this.specificImageNumbers.trim());
        }

        if (this.selectedProductRecordIds && this.selectedProductRecordIds.length > 0) {
          this.selectedProductRecordIds.forEach(recordId => {
            formData.append('product_id_list_records_id[]', recordId);
          })
        }

        if (this.selectedAudiences && this.selectedAudiences.length > 0) {
          formData.append('selectedAudiences', JSON.stringify(this.selectedAudiences));
        }

        if (this.selectedBrandGuidelines && this.selectedBrandGuidelines.length > 0) {
          formData.append('brand_guidelines', JSON.stringify(this.getSelectedBrandGuidelinesPayload()));
        }

        if (this.selectedSeasonalities && this.selectedSeasonalities.length > 0) {
          formData.append(
            'seasonalities',
            JSON.stringify(this.getSelectedSeasonalitiesPayload())
          );
        }

        if (this.customerName && this.customerName.trim()) {
          formData.append('customer_name', this.customerName.trim());
        }

        if (this.selectedPreviousReportId) {
          formData.append('previous_report_id', this.selectedPreviousReportId);
        }

        if (window._playbookFromVB) {
          formData.append('is_playbook', 1);
          window._playbookFromVB = false;
        }

        let {data} = await axios.post("/analysis-reports/create", formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        this.isAnalyzing = currentIndex < this.selectedPrompts.length - 1;
        if (data?.uuids) {
          data.uuids.forEach((uuid, index) => {
            this.addBackgroundJob({uuid: uuid})
          })
        }
        return;
      }
      
      const hasUploadedImages = this.file && this.file.length > 0;
      
      // if (this.hasMultipleUrls && hasUploadedImages && !this.isBackgroundJob && !this.isComparisonAnalysis() && !this.isVisualPlayBookAnalysis()) {
      //   await this.processMultipleUrlsWithSameImages();
      //   this.isAnalyzing = currentIndex < this.selectedPrompts.length - 1;
      //   return;
      // }
      //
      // if (this.hasMultipleUrls && !this.isBackgroundJob && !this.isComparisonAnalysis() && !this.isVisualPlayBookAnalysis() && !hasUploadedImages) {
      //   await this.processMultipleUrlsSeparately();
      //   this.isAnalyzing = currentIndex < this.selectedPrompts.length - 1;
      //   return;
      // }
      
      try {
        if (this.urlsToScrape.length > 0) {
          this.backgroundProcesses = Array.from(Array(this.urlsToScrape.length).keys());
          let i = 0;
          for (const url of this.urlsToScrape) {
            this.currentProcess = 'Get URL'
            if (i === this.urlsToScrape.length - 1) {
              this.isLastMultipleUrl = true;
            }
            this.currentItemProcessing = i + 1;
            this.generateFakePercentage();

            if (this.selectedProductRecordIds && this.selectedProductRecordIds.length > 1) {
              const recordIndex = i % this.selectedProductRecordIds.length;
              this.currentProductRecordId = this.selectedProductRecordIds[recordIndex];
            }

            i++;
            this.processedUrl = url;
            await this.runCustomPromptAnalysis(url);
          }
        } else {
          this.currentItemProcessing = 1;
          this.generateFakePercentage();
          this.processedUrl = this.selectedPrompt.name;
          await this.runCustomPromptAnalysis();
        }
      } catch (error) {
        console.error("Analysis error:", error);
        if (!this.analysisError) {
          toastr.error("An error occurred during analysis. Please try again.");
        }
      } finally {
        this.isAnalyzing = currentIndex < this.selectedPrompts.length - 1;
        this.finishFakePercentage();
      }
    },

    getSelectedEditablePromptFieldsPayload() {
      const payload = {};

      this.selectedPrompts.forEach(prompt => {
        const fields = this.editablePromptFields[prompt.id];

        if (!fields) {
          return;
        }

        payload[prompt.id] = fields;
      });

      return payload;
    },
    
    prepareApiRequest(settings) {
       if (settings.messages && Array.isArray(settings.messages) && Array.isArray(settings.messages[0]?.content)) {
         settings.messages[0].content.forEach(item => {
           if (item.type === 'text') {
             const imagesCount = settings.messages[0].content.filter(m => m.type === 'image_url').length;
             item.text = item.text.replaceAll("[images_count]", imagesCount).replaceAll('{{image_count}}', imagesCount);
           }
           
           if (item.type === 'image_url') {
             delete item.image_url.highResUrl;
           }
         })
       }
       
       return settings;
    },

    async callOpenAI(settings) {
      let settingsCopy = JSON.parse(JSON.stringify(settings));
      try {
        const response = await axios.post(`/call-open-ai`, this.prepareApiRequest(settingsCopy));

        if (response.data && typeof response.data === "object") {
          return response.data;
        } else {
          console.warn("Unexpected API response format:", response);
          throw new Error("Invalid API response format");
        }
      } catch (error) {
        if (error.name === "AbortError") {
          throw new Error("API request timed out");
        }

        const errorMessage =
          error.response?.data?.error?.message ||
          error.message ||
          "Unknown API error";

        throw new Error(errorMessage);
      }
    },

    async runCustomPromptAnalysis(url = null) {
      if (!this.selectedPrompt) {
        toastr.error("Please select an analysis prompt");
        return;
      }
      
      if (this.isVisualPlayBookAnalysis()) {
        //separate logic for visual book analysis
        return this.processVisualPlayBookAnalysisResponse();
      }
      
      if (this.isProductComparisonAnalysis()) {
        return this.processComparsionProductAnalysis();
      }
      
      if (url) {
        if (url.startsWith("http")) {
          this.urlInput = url;
        } else if (url.startsWith("salsify:")) {
          this.urlInput = window.currentUserCompanyId + '_' + url;
        } else if (url.length === 10) { //ASIN
          this.urlInput = `https://www.amazon.com/dp/${url}/?th=1`;
        }

        this.multipleUrlsInput = this.urlInput;
      }

      if (this.selectedPrompt?.analysisFormat?.type === 'gap-analysis-summary') {
        // This prompt uses special logic that is run from the backend.
        const finalProductRecordId = this.currentProductRecordId;

        const analysisResponse = await axios.post(`/banner/groups/0/analysis-reports`, {
            'api_response': {},
            'api_call_data': {},
            'webscrape_response_html': null,
            'type': this.selectedPrompt?.analysisFormat?.type,
            'data': {},
            'from_url': null,
            'product_urls': this.urlsToScrape,
            'prompt': this.processAudienceInPrompt(this.selectedPrompt.prompt),
            'summary_criteria': this.selectedPrompt.summaryCriteria,
            'response_format': this.selectedPrompt.responseFormat,
            'product_id_list_records_id': finalProductRecordId,
            'customer_name': this.customerName?.trim() || null,
        });

        if (this.selectedPrompt.join_images_and_text) {
          try {
            await axios.get(`/analysis-report/join-images/${analysisResponse.data.id}`);
          } catch (error) {
            //nothing
          }
        }

        const analysisUrl = `/analysis-reports/${analysisResponse.data.id}/${analysisResponse.data.uuid}`;

        if (this.urlsToScrape.length > 1) {
          this.collectedReportIds.push(analysisResponse.data.analytics_history_id);
          if (this.isLastMultipleUrl) {
            if (this.selectedGroupId || (this.createNewGroup && this.newGroupName)) {
              const groupId = await this.createGroupIfNeeded();
              if (groupId && this.collectedReportIds.length > 0) {
                await this.addReportsToGroup(this.collectedReportIds, groupId);
              }
            }

            this.redirectToAnalyticsHistory(this.collectedReportIds);
          }
        } else {
          this.redirectToAnalyticsHistory(analysisResponse.data.analytics_history_id);
        }

        return {
          url: analysisUrl,
          result: analysisResponse.data.data
        };
      }

      let maxImages = -1;
      let skipImagePosition = 0;
      if (this.selectedPrompt.max_images_to_process) {
        maxImages = this.selectedPrompt.max_images_to_process;
      }
      
      if (this.selectedPrompt.skip_images_from_parsing) {
        skipImagePosition = this.selectedPrompt.skip_images_from_parsing;
      }
      
      let productTitle = null;
      let productDescription = null;
      let productBullets = null;

      if (this.isComparisonAnalysis()) {
        let hasUrls = false;
        const urls = this.comparisonMultipleInput.split(/[\s\n\r]+/).filter(url => url.trim());
        hasUrls = urls.length >= 1;

        const filesToProcess = this.getFilteredFiles();
        const hasEnoughFiles = Array.isArray(this.file) && filesToProcess.length >= 1;

        if (!hasUrls && !hasEnoughFiles) {
          toastr.error("Please provide at least one URL or upload at least 1 image for analysis");
          return;
        }

        this.fromUrl = !!hasUrls;
      } else {
        const hasUrl = this.multipleUrlsInput.trim();
        const filesToProcess = this.getFilteredFiles();
        const hasFiles = (Array.isArray(this.file) && filesToProcess.length > 0) || (!Array.isArray(this.file) && this.file);

        if (!hasUrl && !hasFiles) {
          toastr.error("Please provide at least one image or a valid URL for analysis");
          return;
        }

        this.fromUrl = !!hasUrl;
      }

      this.isAnalyzing = true;
      this.analysisReportUrl = null;
      this.analysisError = false;
      this.errorMessage = null;

      if (!this.manualItemUrl.trim() && this.hasUrlInput) {
        if (this.isComparisonAnalysis()) {
          const urls = this.comparisonMultipleInput.split(/[\s\n\r]+/).filter(url => url.trim());
          if (urls.length > 0) {
            this.manualItemUrl = urls[0];
          }
        } else {
          const mainUrl = this.multipleUrlsInput.trim();
          if (mainUrl) {
            const urls = mainUrl.split(/[\s\n\r]+/).filter(url => url.trim());
            if (urls.length > 0) {
              this.manualItemUrl = urls[0];
            }
          }
        }
      }

      try {
        let prompt = this.processAudienceInPrompt(this.selectedPrompt.prompt);

        const responseFormat = this.selectedPrompt.responseFormat?.structuredOutputSchema ? this.selectedPrompt.responseFormat?.structuredOutputSchema : null;

        let allFiles = [];
        
        let webScrapeResponseHtml = null;
        let ugcImagePositions = null;

        if (this.selectedPrompt?.pdp_title_and_copy_assessment) {
          const urlForTitleAssessment = this.multipleUrlsInput.trim();
          if (!urlForTitleAssessment) {
            toastr.error("Please provide a URL for title and copy assessment");
            this.isAnalyzing = false;
            return;
          }

          try {
            const { data } = await axios.post(`/webscrape-title-description`, { 
              url: urlForTitleAssessment 
            });
            
            if (data.success && (data.title || data.description)) {
              let titleDescriptionText = "\n\nScraped Content from URL:\n";
              if (data.title) {
                titleDescriptionText += `Title: ${data.title}\n`;
              }
              if (data.description) {
                titleDescriptionText += `Description: ${data.description}\n`;
              }
              titleDescriptionText += `URL: ${data.url}\n`;
              
              prompt = prompt + titleDescriptionText;
              toastr.success("Successfully extracted title and description from URL");
            } else {
              toastr.warning("Could not extract title or description from the provided URL");
            }
          } catch (error) {
            console.error('Error extracting title and description:', error);
            toastr.warning("Failed to extract title and description, proceeding with regular analysis");
          }
        }

        let processedUrls = [];
        if (this.isComparisonAnalysis()) {
          // Process URLs from textarea
          if (this.comparisonMultipleInput.trim()) {
            const inputUrls = this.comparisonMultipleInput.split(/[\s\n\r]+/).filter(url => url.trim());

            for (let urlIndex = 0; urlIndex < inputUrls.length; urlIndex++) {
              const inputUrl = inputUrls[urlIndex].trim();
              const urlRegex = /(https?:\/\/[^\s]+)/g;
              const urls = inputUrl.match(urlRegex) || [inputUrl]; // If no match, use the input as-is

              for (let i = 0; i < urls.length; i++) {
                const url = urls[i].replace(/"/g, '');
                const { data } = await axios.post(`/webscrape`, { url, max_images_to_process: maxImages, skip_images_from_parsing: skipImagePosition });
                if (data && data.response_html) {
                  webScrapeResponseHtml = data.response_html;
                }
                if (data && data.title) {
                  productTitle = data.title;
                }
                if (data && data.description) {
                  productDescription = data.description;
                }
                
                if (data && data.bullets) {
                  productBullets = data.bullets;
                }
                
                if (data && data.images && data.images.length > 0) {
                  const filteredImages = this.getFilteredImages(data.images);
                  for (const imageUrl of filteredImages) {
                    try {
                      const { highRes, lowRes } = await this.downloadImageWithBothQualities(imageUrl);
                      allFiles.push({
                        type: 'image_url',
                        image_url: { 
                          url: this.isReduceImageQuality ? lowRes : highRes,
                          highResUrl: highRes
                        },
                        source: `url${urlIndex + 1}`
                      });
                    } catch (error) {
                      console.error(`Error processing URL ${urlIndex + 1} image:`, error);
                    }
                  }
                  const imageCount = this.enableSpecificImages && this.specificImageNumbers ? 
                    `${filteredImages.length}/${data.images.length}` : data.images.length;
                  const urlLabel = urlIndex === 0 ? 'first comparison' : urlIndex === 1 ? 'second comparison' : `comparison ${urlIndex + 1}`;
                  prompt = prompt.replaceAll(url, `${url} ${imageCount} images (${urlLabel})`);
                  toastr.success(`Successfully processed URL ${urlIndex + 1}: ${filteredImages.length} of ${data.images.length} images found`);
                } else {
                  toastr.warning(`No images found at URL ${urlIndex + 1}: ${url}`);
                }
                processedUrls.push(url);
              }
            }
          }
          
          if (this.file.length > 0 && !this.urlsToScrape.length) {
            const filesToProcess = this.getFilteredFiles();
            for (const file of filesToProcess) {
              allFiles.push({
                type: 'image_url',
                image_url: { 
                  url: file.dataURL,
                  highResUrl: file.dataURL
                },
                source: 'uploaded_file'
              });
            }
          }
        } else {
          // Single analysis processing
          const urlInputToProcess = this.multipleUrlsInput;
          if (urlInputToProcess.trim() && !this.selectedPrompt?.pdp_title_and_copy_assessment) {
            const urlRegex = /(https?:\/\/[^\s]+)/g;
            let urlsToProcess = [];

            // Split by spaces and line breaks, then extract URLs
            const lines = urlInputToProcess.split(/[\s\n\r]+/);
            for (const line of lines) {
              if (line.trim()) {
                if (line.match(urlRegex)) {
                  urlsToProcess.push(...line.match(urlRegex));
                } else {
                  const trimmedLine = line.trim();
                  if (/^[A-Z0-9]{10}$/i.test(trimmedLine)) {
                    urlsToProcess.push('https://www.amazon.com/dp/' + trimmedLine);
                  } else if (/^A-\d{8,9}$/i.test(trimmedLine)) {
                    urlsToProcess.push('https://www.target.com/p/-/' + trimmedLine.toUpperCase());
                  } else {
                    urlsToProcess.push(trimmedLine);
                  }
                }
              }
            }
            this.currentProcess = 'Scraping images';
            for (let i = 0; i < urlsToProcess.length; i++) {
              const url = urlsToProcess[i].replace(/"/g, '');
              const { data } = await axios.post(`/webscrape`, { url, max_images_to_process: maxImages, skip_images_from_parsing: skipImagePosition });
              processedUrls.push(url);
              if (data && data.title) {
                productTitle = data.title;
              }
              if (data && data.description) {
                productDescription = data.description;
              }
              if (data && data.bullets) {
                productBullets = data.bullets;
              }
              if (data && data.response_html) {
                webScrapeResponseHtml = data.response_html;
              }
              if (data && data.ugc_images) {
                ugcImagePositions = data.ugc_images;
              }
              if (data && data.images && data.images.length > 0) {
                const filteredImages = this.getFilteredImages(data.images);
                for (const imageUrl of filteredImages) {
                  try {
                    const { highRes, lowRes } = await this.downloadImageWithBothQualities(imageUrl);
                    allFiles.push({
                      type: 'image_url',
                      image_url: { 
                        url: this.isReduceImageQuality ? lowRes : highRes,
                        highResUrl: highRes
                      }
                    });
                  } catch (error) {
                    console.error('Error processing image:', error);
                  }
                }
                const imageCount = this.enableSpecificImages && this.specificImageNumbers ? 
                  `${filteredImages.length}/${data.images.length}` : data.images.length;
                prompt = prompt.replaceAll(url, `${url} ${imageCount} images`);
                toastr.success(`Successfully processed URL: ${filteredImages.length} of ${data.images.length} images found`);
              } else {
                toastr.warning(`No images found at URL: ${url}`);
              }
            }
          }

          if (this.file.length > 0 && !this.urlsToScrape.length) { //urlToScrape uses xlsx file, so there's no need to prep that
            const filesToProcess = this.getFilteredFiles();
            let detail = this.isReduceImageQuality ? 'low': 'high';
            if (this.screenshotUrl) {
              detail = 'high';
            }
            for (const file of filesToProcess) {
              allFiles.push({
                type: 'image_url',
                image_url: { 
                  url: file.dataURL,
                  highResUrl: file.dataURL,
                  detail
                }
              });
            }
          }
        }

        let imageContents = [];
        let pdpImages = null;

        if (this.selectedPrompt?.pdp_title_and_copy_assessment) {
          if (allFiles.length === 0 && !prompt.includes("Scraped Content from URL:")) {
            toastr.warning("No content available for analysis. Please provide a URL with extractable content or upload images.");
            this.isAnalyzing = false;
            return;
          }
        }

        if (allFiles.length === 1 && this.selectedPrompt?.unpackImagesOpenCV) {
          try {
            const { data } = await axios.post(`/unpack-images-open-cv`, {
              image_url: allFiles[0].image_url.url
            });
            allFiles = data.map(item => ({
              type: 'image_url',
              image_url: {
                url: item
              }
            }));
            imageContents = allFiles;
          } catch (error) {
            console.error("Failed to unpack images:", error);
            await Swal.fire({
              title: 'Error',
              text: 'Failed to unpack images.',
              icon: 'error',
              confirmButtonText: 'Ok'
            });
            this.isAnalyzing = false;
            return;
          }
        } else {
          imageContents = allFiles.map(image => ({
            type: 'image_url',
            image_url: {
              url: image.image_url.url,
              highResUrl: image.image_url.highResUrl || image.image_url.url,
              detail: image.image_url.detail ?? (this.isReduceImageQuality ? 'low' : 'high')
            }
          }));

          if (this.screenshotUrl) {
            imageContents.unshift({
              type: 'text',
              text: 'Screenshot images:'
            });
            
            if (this.parsePdpImages) {
              const { data } = await axios.post(`/webscrape`, { url: this.screenshotUrl, max_images_to_process: this.selectedPrompt.max_images_to_process });

              if (data.images?.length) {
                pdpImages = data.images
                const filteredImages = this.getFilteredImages(data.images);
                imageContents.push({
                  type: 'text',
                  text: 'PDP Images:'
                });
                for (const imageUrl of filteredImages) {
                  try {
                    const { highRes, lowRes } = await this.downloadImageWithBothQualities(imageUrl);
                    imageContents.push({
                      type: 'image_url',
                      image_url: {
                        url: this.isReduceImageQuality ? lowRes : highRes,
                        highResUrl: highRes
                      }
                    });
                  } catch (error) {
                    console.error('Error processing image:', error);
                  }
                }
              }
            }
          }
        }

        if (this.selectedPrompt.screenshot_pdp_images && this.urlInput) {
          let screenshotImages = await this.capturePageScreenshot({
            bvModalEvent: null,
            url: this.urlInput,
            getImages: true,
            chunks: true,
            chunkHeight: 3200,
            chunkOverlap: 120,
            maxChunks: 10,
            smartScreenshot: !!this.detectRetailer(this.urlInput),
            addFilesToInput: false,
            hideModal: false,
            resetModal: false,
          })

          if (screenshotImages) {
            imageContents.unshift(
              {
                type: 'text',
                text: 'Screenshot Images:'
              },
              ...screenshotImages.map(image => ({
                type: 'image_url',
                image_url: {
                  url: image,
                  highResUrl: image,
                  detail: 'high'
                }
              })),
              {
                type: 'text',
                text: 'PDP Images:'
              },
            );
          }
        }

        prompt = prompt
          .replaceAll('{{url}}', processedUrls.join(', '))
          .replaceAll('{{urls}}', processedUrls.join(', '));
        
        if (productTitle) {
          prompt = prompt.replaceAll('{{title}}', productTitle);
        }
        
        if (productDescription) {
          prompt = prompt.replaceAll('{{description}}', productDescription)
        }
        
        if (productBullets) {
          if (Array.isArray(productBullets)) {
            prompt = prompt.replaceAll('{{bullets}}', productBullets.join("\n"));
          } else {
            prompt = prompt.replaceAll('{{bullets}}', productBullets);
          }
        }

        let apiSettings = {
          messages: [
          {
              role: "user",
              content: [
                {
                  type: "text",
                  text: prompt,
                },
                ...imageContents,
              ],
            },
          ],
          model: this.selectedPrompt.model,
        };

        if (this.selectedPrompt.use_openai_websearch) {
          apiSettings.websearch = true;
        }

        if (responseFormat) {
          apiSettings.response_format = responseFormat;
        }
        this.currentProcess = 'Waiting for GPT response';
        if (this.isPromptAParent(this.selectedPrompt) && this.selectedPrompt.apply_each_variation){
          let counter = 0;
          let apiSettingsForLoggingArray = [];
          let responseMessageArray = [];
          let isApplyToPrevious = false;
          let applyPositions = [];
          let imageIndex = 1;
          
          if (this.selectedPrompt.apply_variation_to_each_image) {
            // Individual mode: apply each variation to each image individually
            for (let imgIdx = 0; imgIdx < imageContents.length; imgIdx++) {
              const imageContent = imageContents[imgIdx];
              const currentImageNum = imgIdx + 1;
              const specificVariations = this.selectedPrompt.variations.filter(variation => {
                const variation_prompt = this.customPrompts.find(prompt => prompt.id === variation);
                if (variation_prompt && variation_prompt.apply_to_image_number) {
                  const targetImageNum = parseInt(variation_prompt.apply_to_image_number);
                  return !isNaN(targetImageNum) && targetImageNum === currentImageNum;
                }
                return false;
              });
              
              if (specificVariations.length > 0) {
                for (const variation of specificVariations) {
                  const variation_prompt = this.customPrompts.find(prompt => prompt.id === variation);
                
                if (variation_prompt) {
                  applyPositions.push(currentImageNum);
                  
                  apiSettings.messages[0] = {
                    role: "user",
                    content: [
                      {
                        type: "text",
                        text: variation_prompt.prompt,
                      },
                      imageContent,
                    ],
                  };

                  const responseMessage = await this.callOpenAI(apiSettings);
                  
                  if (!responseMessage || !responseMessage.content) {
                    throw new Error("Empty response from analysis service");
                  }

                  responseMessage.variationId = variation;
                  responseMessage.variationName = variation_prompt.name;

                  const apiSettingsForLogging = {
                    prompt: variation_prompt.prompt,
                    model: this.selectedPrompt.model,
                    temperature: this.selectedPrompt.temperature,
                    image_url: "1 image attached",
                  };

                  if (responseFormat) {
                    apiSettingsForLogging.response_format = responseFormat;
                  }
                  
                  apiSettingsForLoggingArray.push(apiSettingsForLogging);
                  responseMessageArray.push(responseMessage);
                }
                }
              } else {
                
                for (const variation of this.selectedPrompt.variations) {
                  const variation_prompt = this.customPrompts.find(prompt => prompt.id === variation);
                  if (variation_prompt && !variation_prompt.apply_to_image_number) {
                    applyPositions.push(currentImageNum);
                    
                    apiSettings.messages[0] = {
                      role: "user",
                      content: [
                        {
                          type: "text",
                          text: variation_prompt.prompt,
                        },
                        imageContent,
                      ],
                    };

                    const responseMessage = await this.callOpenAI(apiSettings);
                    
                    if (!responseMessage || !responseMessage.content) {
                      throw new Error("Empty response from analysis service");
                    }

                    responseMessage.variationId = variation;
                    responseMessage.variationName = variation_prompt.name;

                    const apiSettingsForLogging = {
                      prompt: variation_prompt.prompt,
                      model: this.selectedPrompt.model,
                      temperature: this.selectedPrompt.temperature,
                      image_url: "1 image attached",
                    };

                    if (responseFormat) {
                      apiSettingsForLogging.response_format = responseFormat;
                    }
                    
                    apiSettingsForLoggingArray.push(apiSettingsForLogging);
                    responseMessageArray.push(responseMessage);
                  }
                }
              }
            }
          } else {
            // Sequential mode: apply variations to images in order
            const imagesWithSpecificVariations = new Set();
            
            // First pass: handle variations with specific image numbers
            for (const variation of this.selectedPrompt.variations) {
              const variation_prompt = this.customPrompts.find(prompt => prompt.id === variation);
              if (variation_prompt && variation_prompt.apply_to_image_number) {
                const targetImageNum = parseInt(variation_prompt.apply_to_image_number);
                
                if (!isNaN(targetImageNum)) {
                  const targetIndex = targetImageNum - 1;
                  if (targetIndex >= 0 && targetIndex < imageContents.length) {
                    imagesWithSpecificVariations.add(targetImageNum);
                    
                    apiSettings.messages[0] = {
                      role: "user",
                      content: [
                        {
                          type: "text",
                          text: variation_prompt.prompt,
                        },
                        imageContents[targetIndex],
                      ],
                    }
                    
                    const responseMessage = await this.callOpenAI(apiSettings);
                    
                    if (!responseMessage || !responseMessage.content) {
                      throw new Error("Empty response from analysis service");
                    }
                    
                    // Add variation info to responseMessage
                    responseMessage.variationId = variation;
                    responseMessage.variationName = variation_prompt.name;
                    
                    const apiSettingsForLogging = {
                      messages: [
                        {
                          role: "user",
                          content: [
                            {
                              type: "text",
                              text: variation_prompt.prompt,
                            },
                            {
                              type: "image_url", 
                              image_url: `1 image attached`
                            }
                          ],
                        },
                      ],
                      model: this.selectedPrompt.model,
                    };
                    
                    if (responseFormat) {
                      apiSettingsForLogging.response_format = responseFormat;
                    }
                    
                    apiSettingsForLoggingArray.push(apiSettingsForLogging);
                    responseMessageArray.push(responseMessage);
                    applyPositions.push(targetImageNum);
                  }
                }
              }
            }
            // Sequential mode: apply general variations to remaining images in order
            const generalVariations = this.selectedPrompt.variations.filter(variation => {
              const variation_prompt = this.customPrompts.find(prompt => prompt.id === variation);
              return variation_prompt && !variation_prompt.apply_to_image_number;
            });
            
            let variationIndex = 0;
            for (let imgIdx = 0; imgIdx < imageContents.length; imgIdx++) {
              const currentImageNum = imgIdx + 1;
              
              // Skip images that have specific variations
              if (imagesWithSpecificVariations.has(currentImageNum)) {
                continue;
              }
              
              if (generalVariations.length > 0) {
                if (variationIndex >= generalVariations.length) {
                  break;
                }
                
                const variation = generalVariations[variationIndex];
                const variation_prompt = this.customPrompts.find(prompt => prompt.id === variation);
                
                if (variation_prompt) {
                  apiSettings.messages[0] = {
                    role: "user",
                    content: [
                      {
                        type: "text",
                        text: variation_prompt.prompt,
                      },
                      imageContents[imgIdx],
                    ],
                  }
                  
                  const responseMessage = await this.callOpenAI(apiSettings);
                  
                  if (!responseMessage || !responseMessage.content) {
                    throw new Error("Empty response from analysis service");
                  }
                  
                  // Add variation info to responseMessage
                  responseMessage.variationId = variation;
                  responseMessage.variationName = variation_prompt.name;
                  
                  const apiSettingsForLogging = {
                    messages: [
                      {
                        role: "user",
                        content: [
                          {
                            type: "text",
                            text: variation_prompt.prompt,
                          },
                          {
                            type: "image_url", 
                            image_url: `1 image attached`
                          }
                        ],
                      },
                    ],
                    model: this.selectedPrompt.model,
                  };
                  
                  if (responseFormat) {
                    apiSettingsForLogging.response_format = responseFormat;
                  }
                  
                  apiSettingsForLoggingArray.push(apiSettingsForLogging);
                  responseMessageArray.push(responseMessage);
                  applyPositions.push(currentImageNum);
                  
                  variationIndex++;
                }
              }
            }
          }
          
          const result = await this.processAnalysisResponseMultiple(
            responseMessageArray, 
              apiSettingsForLoggingArray,
              imageContents, 
              this.selectedPrompt.variations,
              isApplyToPrevious,
              applyPositions,
              productTitle,
              this.selectedPrompt.apply_variation_to_each_image || false
          );

          this.redirectToAnalyticsHistory(result.id);
        }else{
          if (this.selectedPrompt.label_ugc_images && ugcImagePositions && ugcImagePositions.length && Array.isArray(apiSettings.messages) && Array.isArray(apiSettings.messages[0].content) && apiSettings.messages[0].content.length > 0) {
            apiSettings.messages[0].content.forEach(item => {
              if (item.type === 'text') {
                item.text = item.text + `\n Positions are counted starting from 0 (the first image is position 0). Please analyze all provided images in order.  
Images at positions ${ugcImagePositions.join(', ')} are UGC (user-generated content) photos taken by customers.`;
              }
            })
          }
          const responseMessage = await this.callOpenAI(apiSettings);

          if (!responseMessage || !responseMessage.content) {
            throw new Error("Empty response from analysis service");
          }

          if (responseMessage && responseMessage.content) {
            const apiSettingsForLogging = {
              messages: [
                {
                  role: "user",
                  content: [
                    {
                      type: "text",
                      text: prompt,
                    },
                    {
                      type: "image_url", 
                      image_url: `${imageContents.length} image(s) attached`
                    }
                  ],
                },
              ],
              model: this.selectedPrompt.model,
            };
            if (responseFormat) {
              apiSettingsForLogging.response_format = responseFormat;
            }
            this.currentProcess = 'Creating analysis report';
            const result = await this.processAnalysisResponse(
              responseMessage,
              apiSettingsForLogging,
              imageContents,
              this.selectedPrompt,
              webScrapeResponseHtml,
              false,
              null,
              null,
              pdpImages
            );
          }
        }
      } catch (error) {
        console.error("Custom prompt analysis error:", error);
        this.analysisError = true;

        if (error.response) {
          this.errorMessage = `Server error (${error.response.status}): ${
            error.response.data.message || "Unknown server error"
          }`;
        } else if (error.request) {
          this.errorMessage = "No response from server. Please check your network connection.";
        } else if (error.message && error.message.includes("Invalid JSON")) {
          this.errorMessage = "The analysis service returned an invalid response format.";
        } else if (error.message && error.message.includes("Missing")) {
          this.errorMessage = error.message;
        } else {
          this.errorMessage = error.message || "Failed to analyze images";
        }

      } finally {
        this.isAnalyzing = false;
      }
    },

    replaceInObject(obj, imagesListLength) {
      // Recursive function to handle all types of values in the object
      const replaceValues = (value) => {
        if (typeof value === 'string') {
          // Replace '[num]' in strings
          return value.replaceAll('[num]', imagesListLength);
        } else if (Array.isArray(value)) {
          // Process arrays recursively
          return value.map((item) => replaceValues(item));
        } else if (value !== null && typeof value === 'object') {
          // Process objects recursively
          return Object.keys(value).reduce((acc, key) => {
            acc[key] = replaceValues(value[key]);
            return acc;
          }, {});
        }
        // Return value as-is if it's not a string, array, or object
        return value;
      };

      // Start with the input object
      return replaceValues(obj);
    },

    /**
     * Detect retailer by URL.
     * Returns one of:
     * 'amazon', 'walmart', 'target', 'wegmans', 'shoprite', 'giantfoodstores', 'doordash', 'instacart'
     * or null if not recognized.
     */
    detectRetailer(url) {
      if (!url || typeof url !== 'string') return null;

      if (!/^https?:\/\//i.test(url)) {
        url = 'https://' + url;
      }

      let host;
      try {
        host = new URL(url).hostname.toLowerCase();
      } catch (e) {
        return null;
      }

      if (host.includes('amazon.') || host === 'amzn.to') {
        return 'Amazon';
      }

      // Walmart
      if (host.includes('walmart.')) {
        return 'Walmart';
      }

      // Target
      if (host.includes('target.')) {
        return 'Target';
      }

      // Wegmans
      if (host.includes('wegmans.')) {
        return 'Wegmans';
      }

      // ShopRite
      if (host.includes('shoprite.')) {
        return 'ShopRite';
      }

      // Giant Food Stores
      if (host.includes('giantfoodstores.')) {
        return 'Giant Food Stores';
      }

      // Instacart
      if (host.includes('instacart.')) {
        return 'Instacart';
      }

      if (host.includes('homedepot.')) {
        return 'Home Depot';
      }

      if (host.includes('lowes.')) {
        return 'Lowes';
      }
      
      if (host.includes('mercadolibre.') || host.includes('mercadolivre.')) {
        return 'Mercado Libre';
      }

      return null;
    },

    detectSmartScreenshoot(url) {
      if (!url || typeof url !== 'string') return null;
      return url.includes('homedepot.') || url.includes('lowes.') || url.includes('amazon.')
    },


    async processComparsionProductAnalysis() {
      const filesToProcess = this.getFilteredFiles();
      const urls = this.comparisonMultipleInput.split(/[\s\n\r]+/).filter(url => url.trim());

      if (urls.length < 1 && filesToProcess.length < 1) {
        toastr.error("Please provide at least one URL or upload at least 1 image for comparison analysis");
        return;
      }

      // Check for duplicate URLs
      const uniqueUrls = [...new Set(urls)];
      if (urls.length !== uniqueUrls.length) {
        toastr.warning("Duplicate URLs detected, using unique URLs only");
      }

      let imagesA = [];
      let imagesB = [];
      let imagesC = [];
      let productATitle = 'Product A';
      let productBTitle = 'Product B';
      let productCTitle = '';
      let productADescription = '';
      let productBDescription = '';
      let productCDescription = '';
      let productRetailerA = 'Retailer A';
      let productRetailerB = 'Retailer B';
      let productRetailerC = 'Retailer C';
      let allImages = {
        0: [],
        1: []
      }
      if (uniqueUrls.length > 0) {
        uniqueUrls.forEach((url, index) => {
          if (url.startsWith('salsify:')) {
            uniqueUrls[index] = window.currentUserCompanyId + '_' + url;
          }
        })
        const requestData = {};
        if (uniqueUrls[0]) requestData.url_1 = uniqueUrls[0].trim();
        if (uniqueUrls[1]) requestData.url_2 = uniqueUrls[1].trim();
        if (uniqueUrls[2]) requestData.url_3 = uniqueUrls[2].trim();
        let maxImages = 15;
        let skipImagePosition = 0;
        if (!isNaN(+this.selectedPrompt.max_images_to_process) && +this.selectedPrompt.max_images_to_process > 0) {
          maxImages = +this.selectedPrompt.max_images_to_process;
        }
        
        if (this.selectedPrompt.skip_images_from_parsing) {
          skipImagePosition = this.selectedPrompt.skip_images_from_parsing;
        }
        requestData.max_images = maxImages;
        requestData.use_product_a_max_images = false;
        requestData.skip_images_from_parsing = skipImagePosition;
        const { data } = await axios.post(`/comparison-products-scrape`, requestData);
        let prodAObj = data.product_a;
        let prodBObj = data.product_b;
        let prodCObj = data.product_c || {};
        
        if (uniqueUrls[0]) productRetailerA = this.detectRetailer(uniqueUrls[0].trim());
        if (uniqueUrls[1]) productRetailerB = this.detectRetailer(uniqueUrls[1].trim());
        if (uniqueUrls[2]) productRetailerC = this.detectRetailer(uniqueUrls[2].trim());
        
        if (prodAObj.title) {
          productATitle = prodAObj.title;
        }
        if (prodAObj.description) {
          productADescription = prodAObj.description;
        }
        if (prodBObj.title) {
          productBTitle = prodBObj.title;
        }
        if (prodBObj.description) {
          productBDescription = prodBObj.description;
        }
        
        if (prodCObj.title) {
          productCTitle = prodCObj.title;
        }
        if (prodCObj.description) {
          productCDescription = prodCObj.description;
        }
        if (prodAObj.images && prodAObj.images.length > 0) {
          const filteredImagesA = this.getFilteredImages(prodAObj.images);
          for (const image of filteredImagesA) {
            const { highRes, lowRes } = await this.downloadImageWithBothQualities(image);
            allImages[0].push(highRes);
            imagesA.push({
              type: 'image_url',
              image_url: { url: this.isReduceImageQuality ? lowRes : highRes },
              // source: 'Product A'
            });
          }
        }
        if (prodBObj.images && prodBObj.images.length > 0) {
          const filteredImagesB = this.getFilteredImages(prodBObj.images);
          for (const image of filteredImagesB) {
            const { highRes, lowRes } = await this.downloadImageWithBothQualities(image);
            allImages[1].push(highRes);
            imagesB.push({
              type: 'image_url',
              image_url: { url: this.isReduceImageQuality ? lowRes : highRes },
              // source: 'Product B'
            });
          }
        }
        
        // Handle Product C if URL3 is provided
        if (prodCObj.images && prodCObj.images.length > 0) {
          allImages[2] = [];
          const filteredImagesC = this.getFilteredImages(prodCObj.images);
          for (const image of filteredImagesC) {
            const { highRes, lowRes } = await this.downloadImageWithBothQualities(image);
            allImages[2].push(highRes);
            imagesC.push({
              type: 'image_url',
              image_url: { url: this.isReduceImageQuality ? lowRes : highRes },
              // source: 'Product C'
            });
          }
        }
      } else {
        // For uploaded files, we only support 2-way comparison
        // Split uploaded files between Product A and Product B
        const filesToProcess = this.getFilteredFiles();
        const halfIndex = Math.ceil(filesToProcess.length / 2);
        const filesA = filesToProcess.slice(0, halfIndex);
        const filesB = filesToProcess.slice(halfIndex);
        
        filesA.forEach(file => {
          allImages[0].push(file.dataURL);
          imagesA.push({
            type: 'image_url',
            image_url: { url: file.dataURL },
            // source: 'Product A'
          });
        })
        
        filesB.forEach(file => {
          allImages[1].push(file.dataURL);
          imagesB.push({
            type: 'image_url',
            image_url: { url: file.dataURL },
            // source: 'Product B'
          });
        })
      }
      let prompt = this.processAudienceInPrompt(this.selectedPrompt.prompt);

      const hasThirdProduct = imagesC && imagesC.length > 0;
      if (hasThirdProduct && prompt.includes('two products')) {
        prompt = prompt.replace(/two products/g, 'three products');
        prompt = prompt.replace(/Product A and Product B/g, 'Product A, Product B, and Product C');
        prompt = prompt.replace(/both products/g, 'all three products');
      }
      
      if (uniqueUrls.length > 0) {
        prompt = prompt.replace('{RETAILER_A_NAME}', productRetailerA);
        prompt = prompt.replace('{RETAILER_B_NAME}', productRetailerB);
        prompt = prompt.replace('{RETAILER_C_NAME}', productRetailerC);
      } else {
        prompt = prompt.replace('{RETAILER_A_NAME}', 'Product A');
        prompt = prompt.replace('{RETAILER_B_NAME}', 'Product B');
        prompt = prompt.replace('{RETAILER_C_NAME}', 'Product C');
      }
      
      let contentArray = [
        {
          type: "text",
          text: prompt,
        },
        {
          type: "text",
          text: `Product A: ${productATitle} ${productADescription}`,
        },
        ...imagesA,
        {
          type: "text",
          text: `Product B: ${productBTitle} ${productBDescription}`,
        },
        ...imagesB,
      ];
      
      if (imagesC && imagesC.length > 0) {
        contentArray.push(
          {
            type: "text",
            text: `Product C: ${productCTitle} ${productCDescription}`,
          },
          ...imagesC
        );
      }
      
      let apiSettings = {
        messages: [
          {
              role: "user",
              content: contentArray
          }
        ],
        model: this.selectedPrompt.model,
      }

      let responseMessage = await this.callOpenAI(apiSettings);

      if (!responseMessage || !responseMessage.content) {
        throw new Error("Empty response from analysis service for actions section");
      }
      
      let apiResponseForStorage = responseMessage.content
      let analysisResult = {
        markdown: responseMessage.content
      };
      
      analysisResult.images = allImages;
      analysisResult.details = {
        'url_1': uniqueUrls[0] || '',
        'url_2': uniqueUrls[1] || '',
        'url_3': uniqueUrls[2] || '',
        'date': new Date().toISOString(),
      }

      if (uniqueUrls[0]) {
        if (uniqueUrls[0].includes('salsify:')) {
          analysisResult.item_url_1 = uniqueUrls[0].trim().split('_')[1];
        } else {
          analysisResult.item_url_1 = uniqueUrls[0].trim();
        }
        analysisResult.site_1 = this.extractDomain(uniqueUrls[0].trim());
        analysisResult.site_1_name = this.detectRetailer(uniqueUrls[0].trim());
        analysisResult.item_title_1 = productATitle;
      }
      if (uniqueUrls[1]) {
        analysisResult.item_url_2 = uniqueUrls[1].trim();
        analysisResult.site_2 = this.extractDomain(uniqueUrls[1].trim());
        analysisResult.site_2_name = this.detectRetailer(uniqueUrls[1].trim());
        analysisResult.item_title_2 = productBTitle;
      }
      if (uniqueUrls[2]) {
        analysisResult.item_url_3 = uniqueUrls[2].trim();
        analysisResult.site_3 = this.extractDomain(uniqueUrls[2].trim());
        analysisResult.site_3_name = this.detectRetailer(uniqueUrls[2].trim());
        analysisResult.item_title_3 = productCTitle;
      }

      analysisResult.title = this.selectedPrompt.name;
      analysisResult.selectedPromptId = this.selectedPrompt.id;

      const finalProductRecordId = this.currentProductRecordId;

      const analysisResponse = await axios.post(`/banner/groups/0/analysis-reports`, {
        'api_response': apiResponseForStorage,
        'api_call_data': JSON.stringify(apiSettings),
        'webscrape_response_html': null,
        'type': 'compare-products',
        'data': analysisResult,
        'from_url': true,
        'product_id_list_records_id': finalProductRecordId,
        'customer_name': this.customerName?.trim() || null,
      });

      if (this.selectedPrompt.join_images_and_text) {
        try {
          await axios.get(`/analysis-report/join-images/${analysisResponse.data.id}`);
        } catch (error) {
          //nothing
        }
      }

      const analysisUrl = `/analysis-reports/${analysisResponse.data.id}/${analysisResponse.data.uuid}`;
      this.redirectToAnalyticsHistory(analysisResponse.data.analytics_history_id);
    },

    handleProductUrlsInput() {
      this.isUpdatingFromTextarea = true;

      if (!this.productUrlsText || this.productUrlsText.trim() === '') {
        this.productUrls = [''];
        this.allProductUrls = [];
        this.$nextTick(() => {
          this.isUpdatingFromTextarea = false;
        });
        return;
      }

      const lines = this.productUrlsText.split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0);

      const processedLines = lines.map(line => {
        const productId = this.getProductIdForUrl(line);

        const expandedLine = this.expandProductIdentifierToUrl(line);

        if (productId && expandedLine !== line) {
          this.productIdMapping[expandedLine] = productId;
        }

        if (expandedLine.includes('amazon.com') && expandedLine.includes('/dp/')) {
          return this.truncateAmazonUrl(expandedLine);
        }

        return expandedLine;
      });

      const processedText = processedLines.join('\n');
      if (processedText !== lines.join('\n')) {
        this.productUrlsText = processedText;
      }

      this.allProductUrls = processedLines;
      this.productUrls = processedLines.slice(0, 20);

      if (this.productUrls.length === 0) {
        this.productUrls = [''];
        this.allProductUrls = [];
      }

      this.$nextTick(() => {
        this.isUpdatingFromTextarea = false;
      });
    },

    handleProductUrlsPaste() {
      setTimeout(() => {
        const currentLines = this.productUrlsText.split('\n')
          .map(line => line.trim())
          .filter(line => line.length > 0);

        if (currentLines.length >= 20) {
          return;
        }

        const textarea = this.$refs.productUrlsTextarea;
        if (textarea && textarea.$el) {
          const textareaElement = textarea.$el.querySelector('textarea') || textarea.$el;
          const cursorPosition = textareaElement.selectionStart;
          const currentText = this.productUrlsText;

          if (cursorPosition === currentText.length && currentText.length > 0 && !currentText.endsWith('\n')) {
            this.productUrlsText = currentText + '\n';

            this.$nextTick(() => {
              const newPosition = currentText.length + 1;
              textareaElement.setSelectionRange(newPosition, newPosition);
            });
          } else if (cursorPosition > 0 && cursorPosition < currentText.length) {
            const charBefore = currentText.charAt(cursorPosition - 1);
            const charAfter = currentText.charAt(cursorPosition);

            if (charBefore !== '\n' && charAfter !== '\n') {
              const textBefore = currentText.substring(0, cursorPosition);
              const textAfter = currentText.substring(cursorPosition);

              this.productUrlsText = textBefore + '\n' + textAfter;

              this.$nextTick(() => {
                const newPosition = cursorPosition + 1;
                textareaElement.setSelectionRange(newPosition, newPosition);
              });
            }
          }
        }
      }, 50);
    },

    handleCategoryUrlsInput() {
      this.isUpdatingCategoryFromTextarea = true;

      if (!this.categoryUrlsText || this.categoryUrlsText.trim() === '') {
        this.categoryUrls = [''];
        this.$nextTick(() => {
          this.isUpdatingCategoryFromTextarea = false;
        });
        return;
      }

      const lines = this.categoryUrlsText.split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0);

      this.categoryUrls = lines;

      this.$nextTick(() => {
        this.isUpdatingCategoryFromTextarea = false;
      });
    },

    handleCategoryUrlsPaste() {
      setTimeout(() => {
        const currentLines = this.categoryUrlsText.split('\n')
          .map(line => line.trim())
          .filter(line => line.length > 0);

        const textarea = this.$refs.categoryUrlsTextarea;
        if (textarea && textarea.$el) {
          const textareaElement = textarea.$el.querySelector('textarea') || textarea.$el;
          const cursorPosition = textareaElement.selectionStart;
          const currentText = this.categoryUrlsText;

          if (cursorPosition === currentText.length && currentText.length > 0 && !currentText.endsWith('\n')) {
            this.categoryUrlsText = currentText + '\n';

            this.$nextTick(() => {
              const newPosition = currentText.length + 1;
              textareaElement.setSelectionRange(newPosition, newPosition);
            });
          } else if (cursorPosition > 0 && cursorPosition < currentText.length) {
            const charBefore = currentText.charAt(cursorPosition - 1);
            const charAfter = currentText.charAt(cursorPosition);

            if (charBefore !== '\n' && charAfter !== '\n') {
              const textBefore = currentText.substring(0, cursorPosition);
              const textAfter = currentText.substring(cursorPosition);

              this.categoryUrlsText = textBefore + '\n' + textAfter;

              this.$nextTick(() => {
                const newPosition = cursorPosition + 1;
                textareaElement.setSelectionRange(newPosition, newPosition);
              });
            }
          }
        }
      }, 50);
    },

    convertAsinToAmazonUrl(input) {
      const trimmedInput = input.trim();

      const asinPattern = /^[A-Z0-9]{10}$/i;
      if (asinPattern.test(trimmedInput)) {
        return `https://www.amazon.com/dp/${trimmedInput}`;
      }

      const tcinPattern = /^A-\d{8,9}$/i;
      if (tcinPattern.test(trimmedInput)) {
        return `https://www.target.com/p/-/${trimmedInput.toUpperCase()}`;
      }

      if (trimmedInput.includes('amazon.com') && trimmedInput.includes('/dp/')) {
        return this.truncateAmazonUrl(trimmedInput);
      }

      return input;
    },

    truncateAmazonUrl(url) {
      try {
        const urlObj = new URL(url);

        const dpMatch = urlObj.pathname.match(/\/dp\/([A-Z0-9]{10})/i);

        if (!dpMatch) {
          return url;
        }

        const asin = dpMatch[1];

        return `${urlObj.origin}/dp/${asin}/`;
      } catch (error) {
        return url;
      }
    },

    processTextareaUrls(inputText, setterCallback) {
      if (inputText && inputText.trim()) {
        let regex = /[\s\n\r,]+/
        if (inputText.includes('rossmann.pl')) {
          regex = /[\s\n\r]+/
        }
        const lines = inputText.split(regex).filter(line => line.trim());
        const processedLines = lines.map(line => {
          const productId = this.getProductIdForUrl(line);

          const expandedLine = this.expandProductIdentifierToUrl(line);

          if (productId && expandedLine !== line) {
            this.productIdMapping[expandedLine] = productId;
          }

          if (expandedLine.includes('amazon.com') && expandedLine.includes('/dp/')) {
            return this.truncateAmazonUrl(expandedLine);
          }

          return expandedLine;
        });

        const processedText = processedLines.join('\n');
        if (processedText !== lines.join('\n')) {
          setterCallback(processedText);
        }
      }
    },

    startEditingReportName() {
      this.isEditingReportName = true;
      this.$nextTick(() => {
        if (this.$refs.reportNameInput) {
          this.$refs.reportNameInput.focus();
          this.$refs.reportNameInput.select();
        }
      });
    },

    validatePlaybookReport() {
      if (this.inputMode === 'search' && !this.searchKeyword) {
        toastr.error("Please provide a search keyword");
        return false;
      }

      if (this.inputMode === 'category') {
        const validCategoryUrls = this.categoryUrls.filter(url => url.trim());
        if (validCategoryUrls.length === 0) {
          toastr.error("Please provide at least one category URL");
          return false;
        }
      }

      if (this.inputMode === 'products') {
        const validUrls = this.productUrls.filter(url => url.trim());
        const hasProductListSelection = this.selectedProducts && this.selectedProducts.length > 0;

        if (validUrls.length === 0 && !hasProductListSelection) {
          toastr.error("Please provide at least one product URL/ASIN or select products from a product list");
          return false;
        }
        if (validUrls.length > 20) {
          toastr.error("Maximum 20 product URLs allowed");
          return false;
        }
      }

      if (!this.countResults && this.inputMode !== 'products') {
        toastr.error("Please provide count results for visual playbook analysis");
        return false;
      }

      // Require retailer for search mode only (category mode auto-detects from URL)
      if (this.inputMode === 'search' && !this.selectedRetailer) {
        toastr.error("Please select a retailer");
        return false;
      }
      
      return true;
    },

    async processVisualPlayBookAnalysisResponse() {
      if (!this.validatePlaybookReport()) {
        return;
      }
      
      // showing loading status
      this.showLoadingStatus = true;
      let requestData = {
        max_images: this.maxImagesPerProduct
      };

      if (this.countResults) {
        requestData.count_results = this.countResults;
      }

      if (this.selectedProductRecordIds && this.selectedProductRecordIds.length > 0) {
        requestData.product_id_list_records_id = [...this.selectedProductRecordIds];
      }

      if (this.inputMode === 'search' && this.selectedRetailer) {
        requestData.retailer = this.selectedRetailer;
      }
      
      if (this.inputMode === 'search') {
        requestData.keyword = this.searchKeyword;
        requestData.include_brands = this.includeBrands;
        requestData.exclude_brands = this.excludeBrands;
      } else if (this.inputMode === 'category') {
        const validCategoryUrls = this.categoryUrls.filter(url => url.trim());
        requestData.category_urls = validCategoryUrls;
      } else if (this.inputMode === 'products') {
        let allProductUrls = [];

        const processedUrls = this.productUrls
          .filter(url => url.trim())
          .map(url => this.convertAsinToAmazonUrl(url.trim()));
        allProductUrls.push(...processedUrls);

        if (this.selectedProducts && this.selectedProducts.length > 0) {
          const productListUrls = this.selectedProducts.map(product => {
            return product.asin ? `https://www.amazon.com/dp/${product.asin}` :
                   product.tcin ? `https://www.target.com/p/-/${product.tcin}` :
                   product.walmart_id ? `https://www.walmart.com/ip/${product.walmart_id}` :
                   product.url || '';
          }).filter(url => url.trim());
          allProductUrls.push(...productListUrls);
        }

        requestData.product_urls = allProductUrls;
      }

      this.loadingStatusText = `Step [1/4]: Collecting Product Images`;
      this.loadingStatusHelperText = `This may take a while depending on the number of product images. Usually it takes less than a minute.`;
      
      let data;
      try {
        const response = await axios.post(`/search-scrape`, requestData);
        data = response.data;
      } catch (error) {
        console.error("Image crawling failed:", error);
        this.isAnalyzing = false;
        this.showLoadingStatus = false;
        this.analysisError = true;
        this.errorMessage = "Cannot find any images";
        return;
      }
      let apiResponseForStorage = []
      let responses = [];
      // getting steps for progress bar
      const totalImages = Object.keys(data).length;
      if (Object.keys(data).length > 0) {
        let maxCountImages = 0;
        let allImages = [];
        let allProductLinks = []
        let i = 1;
        for (const key in data) {
          let url = key;
          let prompt = this.processAudienceInPrompt(this.selectedPrompt.prompt).replaceAll('[iterated-url]', `${url} ${data[key]['images'].length} images`);
          let imageContents = [];
          if (data[key]['images'].length > maxCountImages) {
            maxCountImages = data[key]['images'].length;
          }
          let highResImages = [];
          const filteredImages = this.getFilteredImages(data[key]['images']);
          for (const item of filteredImages) {
            const { highRes, lowRes } = await this.downloadImageWithBothQualities(item);
            highResImages.push(highRes);
            imageContents.push({
              type: 'image_url',
              image_url: {
                url: this.isReduceImageQuality ? lowRes : highRes
              }
            })
          }
          allImages.push(highResImages);
          allProductLinks.push(url);
          
          let preparedResponse = this.selectedPrompt.responseFormat.structuredOutputSchema;
          let apiSettings = {
            messages: [
              {
                role: "user",
                content: [
                  {
                    type: 'text',
                    text: prompt
                  },
                  ...imageContents
                ],
              }
            ],
            model: this.selectedPrompt.model,
            response_format: this.replaceInObject(preparedResponse, filteredImages.length),
          };
          this.loadingStatusText = `Step [2/4]: Analyzing image ${i} of ${totalImages}`;
          this.loadingStatusHelperText = `Processing images to understand layout, design elements, and visual patterns. This usually takes up to ~15-20 seconds for each image.`;
          
          let responseMessage = await this.callOpenAI(apiSettings);

          if (!responseMessage || !responseMessage.content) {
            this.clearLoadingStatus();
            throw new Error("Empty response from analysis service for actions section");
          }

          responses.push(responseMessage.content);
          apiResponseForStorage.push(responseMessage.content)
          
          i++;
        }

        let consolidationPrompt = `Consolidate the ${maxCountImages} images from each of the image carousels from the following products (that are best practice examples) into one single playbook with best practices for each of the ${maxCountImages} images.\n\n` +
          responses.map((response, index) => `Analyzed product ${index + 1}:\n${response}`).join('\n\n');

        let prompt = this.processAudienceInPrompt(consolidationPrompt);

        let apiSettings = {
          messages: [
            {
              role: "user",
              content: [
                {
                  type: 'text',
                  text: prompt,
                  content: prompt
                }
              ],
            }
          ],
          model: this.selectedPrompt.model,
          response_format: this.replaceInObject(this.selectedPrompt.responseFormat.structuredOutputSchema, maxCountImages),
        }
        this.loadingStatusText = `Step [3/4]: Creating the Visual Playbook`;
        this.loadingStatusHelperText = `Combining insights from all analyzed images to build a consolidated best practices guide. This step takes roughly 15-30 seconds.`;
        let responseMessage = await this.callOpenAI(apiSettings);

        if (!responseMessage || !responseMessage.content) {
          this.clearLoadingStatus();
          throw new Error("Empty response from analysis service for actions section");
        }
        
        apiResponseForStorage.push(responseMessage.content)
        let analysisResult = JSON.parse(responseMessage.content);
        analysisResult['images'] = allImages;
        analysisResult['details'] = {
          'retailer': this.selectedRetailer,
          'search_term': this.inputMode === 'search' ? this.searchKeyword : null,
          'include_brands': this.includeBrands,
          'exclude_brands': this.excludeBrands,
          'category_urls': this.inputMode === 'category' ? this.categoryUrls.filter(url => url.trim()) : null,
          'product_urls': this.inputMode === 'products' ? this.productUrls.filter(url => url.trim()) : null,
          'product_list_names': (this.inputMode === 'products' && this.selectedProductLists.length > 0) ? this.selectedProductLists.map(list => list.name) : null,
          'selected_products': (this.inputMode === 'products' && this.selectedProducts.length > 0) ? this.selectedProducts.map(p => p.display_name) : null,
          'input_mode': this.inputMode,
          'date': new Date().toISOString(),
          url: this.urlInput
        }
        analysisResult.title = this.selectedPrompt.name;
        analysisResult.productLinks = allProductLinks;
        analysisResult['selectedPromptId'] = this.selectedPrompt.id;
        this.loadingStatusText = `Step [4/4]: Saving Results`;
        this.loadingStatusHelperText = `Almost done. Finalizing and saving your visual playbook. You’ll be able to open the link in a new tab once it's ready.`;
        const finalProductRecordId = this.currentProductRecordId;

        const analysisResponse = await axios.post(`/banner/groups/0/analysis-reports`, {
          'api_response': apiResponseForStorage,
          'api_call_data': JSON.stringify(apiSettings),
          'webscrape_response_html': null,
          'type': this.selectedPrompt?.analysisFormat?.type || 'visual-playbook',
          'data': analysisResult,
          'from_url': this.fromUrl,
          'product_id_list_records_id': finalProductRecordId,
          'customer_name': this.customerName?.trim() || null,
        });

        if (this.selectedPrompt.join_images_and_text) {
          try {
            await axios.get(`/analysis-report/join-images/${analysisResponse.data.id}`);
          } catch (error) {
            //nothing
          }
        }

        const analysisUrl = `/analysis-reports/${analysisResponse.data.id}/${analysisResponse.data.uuid}`;
        // Redirect to analytics history instead of showing success screen
        this.redirectToAnalyticsHistory(analysisResponse.data.analytics_history_id);
        this.clearLoadingStatus();
      }
    },

    async processAnalysisResponse(responseMessage, apiSettings, imageContents, selectedPrompt, webscrapeResponseHtml, skipOpenWindow = false, specificUrl = null, batchId = null, pdpImages = null) {
      try {
        let analysisResult;
        const content = responseMessage.content;
        const isJsonResponse = this.isValidJSON(content);
        
        if (isJsonResponse) {
          try {
            const parsedResponse = JSON.parse(content);
            if (parsedResponse.error) {
              throw new Error(`API error: ${parsedResponse.error}`);
            }
            analysisResult = parsedResponse.properties || parsedResponse;
          } catch (error) {
            console.error("Error parsing JSON response:", error);
            toastr.error("Failed to parse response data");
            throw new Error("Invalid JSON response");
          }
        } else {
          analysisResult = {
            markdown: content
          };
        }

        const apiResponseForStorage = isJsonResponse ? content : JSON.stringify({ content: content });
        
        analysisResult.images = imageContents.filter(image => image?.image_url).map(image => image.image_url.highResUrl || image.image_url.url);
        analysisResult.reportType = selectedPrompt.name;
        analysisResult.selectedPromptId = selectedPrompt.id;
        analysisResult.title = selectedPrompt.name;
        
        if (batchId) {
          analysisResult.batch_id = batchId;
        }

        if (selectedPrompt.generate_heatmap && selectedPrompt.heatmap_prompt) {
          analysisResult.generate_heatmap = true;
          analysisResult.heatmap_prompt = selectedPrompt.heatmap_prompt;
        }

        
        let analysisType;
        if (isJsonResponse && selectedPrompt?.analysisFormat?.type) {
          if (this.isComparisonAnalysis()) {
            if (this.comparisonMultipleInput.trim()) {
              const urls = this.comparisonMultipleInput.split(/[\s\n\r]+/).filter(url => url.trim());

              if (urls[0]) {
                analysisResult.item_url_1 = urls[0];
                analysisResult.site_1 = this.extractDomain(urls[0]);
              }
              if (urls[1]) {
                analysisResult.item_url_2 = urls[1];
                analysisResult.site_2 = this.extractDomain(urls[1]);
              }
              if (urls[2]) {
                analysisResult.item_url_3 = urls[2];
                analysisResult.site_3 = this.extractDomain(urls[2]);
              }
            }
          } else {
            let urlForResult = this.multipleUrlsInput;
            if (urlForResult.trim()) {
              if (urlForResult.includes('salsify:')) urlForResult = urlForResult.split('_')[1];
              analysisResult.item_url = urlForResult;
              analysisResult.site = this.extractDomain(urlForResult);
            } else if (this.manualItemUrl && this.manualItemUrl.trim()) {
              let manualUrl = this.manualItemUrl.trim();
              if (manualUrl.includes('salsify:')) manualUrl = manualUrl.split('_')[1];
              // Use manual URL if no crawled URL is available
              analysisResult.item_url = manualUrl;
              analysisResult.manual_item_url = manualUrl;
              analysisResult.site = this.extractDomain(manualUrl);
            }
          }
          analysisType = selectedPrompt?.analysisFormat?.type;
        } else {
          if (this.isComparisonAnalysis()) {
            if (this.comparisonMultipleInput.trim()) {
              const urls = this.comparisonMultipleInput.split(/[\s\n\r]+/).filter(url => url.trim());

              if (urls[0]) {
                analysisResult.item_url_1 = urls[0];
                analysisResult.site_1 = this.extractDomain(urls[0]);
              }
              if (urls[1]) {
                analysisResult.item_url_2 = urls[1];
                analysisResult.site_2 = this.extractDomain(urls[1]);
              }
              if (urls[2]) {
                analysisResult.item_url_3 = urls[2];
                analysisResult.site_3 = this.extractDomain(urls[2]);
              }
            }
          } else {
            let urlForResult = specificUrl || this.multipleUrlsInput;
            if (urlForResult && urlForResult.trim()) {
              if (urlForResult.includes('salsify:')) urlForResult = urlForResult.split('_')[1];
              analysisResult.item_url = urlForResult;
            } else if (this.manualItemUrl && this.manualItemUrl.trim()) {
              let manualUrl = this.manualItemUrl.trim();
              if (manualUrl.includes('salsify:')) manualUrl = manualUrl.split('_')[1];
              analysisResult.item_url = manualUrl;
              analysisResult.manual_item_url = manualUrl;
            }
          }
          analysisType = "generic-markdown";
        }
        
        if (this.selectedPrompt.isMobileLegibility) {
          analysisType = 'mobile-legibility';
        }

        if (this.screenshotUrl) {
          analysisResult.item_url = this.screenshotUrl;
          await this.loadProductLists(true);

          if (this.availableProducts.length) {
            const matchedProduct = this.screenshotUrl && this.availableProducts
              ? this.availableProducts.find(product =>
                [
                  product.asin,
                  product.tcin,
                  product.walmart_id,
                  product.url,
                ].some(value => value && this.screenshotUrl.includes(String(value)))
              )
              : null;

            if (matchedProduct) {
              this.currentProductRecordId = matchedProduct.id;
            }
          }
          
          // if (this.parsePdpImages) {
          //   const { data } = await axios.post(`/webscrape`, { url: this.screenshotUrl });
          //  
          //   if (data.images?.length) {
          //     analysisResult.pdp_images = data.images;
          //   }
          // }
        }
        
        if (pdpImages) {
          analysisResult.pdp_images = pdpImages;
        }
        
        if (this.useDomainAsClientName) {
          analysisResult.use_domain_as_client_name = this.useDomainAsClientName;
        }
        
        const finalProductRecordId = this.currentProductRecordId;

        const analysisResponse = await axios.post(`/banner/groups/0/analysis-reports`, {
            'api_response': apiResponseForStorage,
            'api_call_data': JSON.stringify(apiSettings),
            'webscrape_response_html': webscrapeResponseHtml,
            'type': analysisType,
            'data': analysisResult,
            'from_url': this.fromUrl,
            'product_id_list_records_id': finalProductRecordId,
            'customer_name': this.customerName?.trim() || null,
        });

        if (this.selectedPrompt.join_images_and_text) {
          try {
            await axios.get(`/analysis-report/join-images/${analysisResponse.data.id}`);
          } catch (error) {
            //nothing
          }
        }
        
        const analysisUrl = `/analysis-reports/${analysisResponse.data.id}/${analysisResponse.data.uuid}`;

        if (this.urlsToScrape.length > 1) {
          this.collectedReportIds.push(analysisResponse.data.analytics_history_id);
          if (this.isLastMultipleUrl) {
            if (this.selectedGroupId || (this.createNewGroup && this.newGroupName)) {
              const groupId = await this.createGroupIfNeeded();
              if (groupId && this.collectedReportIds.length > 0) {
                await this.addReportsToGroup(this.collectedReportIds, groupId);
              }
            }

            if (!skipOpenWindow) {
              this.redirectToAnalyticsHistory(this.collectedReportIds);
            }
          }
        } else {
          if (!skipOpenWindow) {
            this.redirectToAnalyticsHistory(analysisResponse.data.analytics_history_id);
          }
        }
        
        return {
          url: analysisUrl,
          result: analysisResult,
          analytics_history_id: analysisResponse.data.analytics_history_id,
          id: analysisResponse.data.analytics_history_id
        };
      } catch (error) {
        console.error("Error processing analysis:", error);
        throw error;
      }
    },

    async processAnalysisResponseMultiple(responseMessageArray, apiSettingsArray, imageContents, selectedPrompts, isApplyToPrevious, applyPositions, productTitle = null, isMatrixMode = false) {
      try {
        let applyOrder;
        if (isApplyToPrevious) {
          applyOrder = applyPositions.map(position => position);
        }
        let analysisResultArray = [];
        let analysisTypeArray = [];
        let counter = 0;
        for (const responseMessage of responseMessageArray) {
          const content = responseMessage.content;
          
          let selectedPrompt;
          if (responseMessage.variationId) {
            selectedPrompt = this.customPrompts.find(prompt => prompt.id === responseMessage.variationId);
          } else {
            let variationIndex;
            if (isMatrixMode) {
              variationIndex = counter % selectedPrompts.length;
            } else {
              variationIndex = Math.min(counter, selectedPrompts.length - 1);
            }
            selectedPrompt = this.customPrompts.find(prompt => prompt.id === selectedPrompts[variationIndex]);
          }
          let analysisResult;
          const isJsonResponse = this.isValidJSON(content);
          if (isJsonResponse) {
            try {
              const parsedResponse = JSON.parse(content);
              if (parsedResponse.error) {
                throw new Error(`API error: ${parsedResponse.error}`);
              }
              analysisResult = parsedResponse.properties || parsedResponse;
            } catch (error) {
              console.error("Error parsing JSON response:", error);
              toastr.error("Failed to parse response data");
              throw new Error("Invalid JSON response");
            }
          } else {
            analysisResult = {
              markdown: content
            };
          }
          
          let imageIndex;
          if (applyPositions && applyPositions[counter] !== undefined) {
            imageIndex = applyPositions[counter] - 1;
          } else if (isMatrixMode) {
            imageIndex = Math.floor(counter / selectedPrompts.length);
          } else {
            imageIndex = counter;
          }
          
          if (imageIndex < imageContents.length && imageContents[imageIndex]) {
            const imageContent = imageContents[imageIndex];
            
            if (imageContent.image_url) {
              const imageUrl = imageContent.image_url.url || imageContent.image_url;
              const highResUrl = imageContent.image_url.highResUrl || imageUrl;
              analysisResult.images = [highResUrl];
            } else {
              analysisResult.images = [];
            }
          } else {
            analysisResult.images = [];
          }
          
          analysisResult.imageNumber = imageIndex + 1;
          analysisResult.variationName = responseMessage.variationName || (selectedPrompt ? selectedPrompt.name : 'Unknown Variation');
          
          if (!isMatrixMode && selectedPrompts.length > 1) {
            analysisResult.title = selectedPrompt.name;
          } else {
            analysisResult.title = this.selectedPrompt.name;
          }

          
          let analysisType;
          if (this.isComparisonAnalysis()) {
            if (this.comparisonMultipleInput.trim()) {
              const urls = this.comparisonMultipleInput.split(/[\s\n\r]+/).filter(url => url.trim());

              if (urls[0]) {
                analysisResult.item_url_1 = urls[0].trim();
                analysisResult.site_1 = this.extractDomain(urls[0].trim());
              }
              if (urls[1]) {
                analysisResult.item_url_2 = urls[1].trim();
                analysisResult.site_2 = this.extractDomain(urls[1].trim());
              }
              if (urls[2]) {
                analysisResult.item_url_3 = urls[2].trim();
                analysisResult.site_3 = this.extractDomain(urls[2].trim());
              }
            }
          } else {
            const urlForResult = this.multipleUrlsInput;
            if (urlForResult.trim()) {
              analysisResult.item_url = urlForResult;
            } else if (this.manualItemUrl && this.manualItemUrl.trim()) {
              analysisResult.item_url = this.manualItemUrl.trim();
              analysisResult.manual_item_url = this.manualItemUrl.trim();
            }
          }
          if (isJsonResponse && selectedPrompt?.analysisFormat?.type) {
            if (this.isComparisonAnalysis()) {
              // Extract sites for comparison analysis
              if (this.comparisonMultipleInput.trim()) {
                const urls = this.comparisonMultipleInput.split(/[\s\n\r]+/).filter(url => url.trim());

                if (urls[0]) {
                  analysisResult.site_1 = this.extractDomain(urls[0].trim());
                }
                if (urls[1]) {
                  analysisResult.site_2 = this.extractDomain(urls[1].trim());
                }
                if (urls[2]) {
                  analysisResult.site_3 = this.extractDomain(urls[2].trim());
                }
              }
            } else {
              const urlForResult = this.multipleUrlsInput;
              if (urlForResult.trim()) {
                analysisResult.site = this.extractDomain(urlForResult);
              } else if (this.manualItemUrl && this.manualItemUrl.trim()) {
                analysisResult.site = this.extractDomain(this.manualItemUrl.trim());
              }
            }
            analysisType = selectedPrompt?.analysisFormat?.type;
          } else {
            if (!isMatrixMode && selectedPrompts.length > 1) {
              analysisResult.title =  selectedPrompt.name;
            } else {
              analysisResult.title = this.selectedPrompt.name;
            }
            analysisType = "gap-analysis";
          }
          
          counter ++;
          analysisResult.main_title = this.selectedPrompt.name;
          analysisResult.selectedPromptId = this.selectedPrompt.id;
          
          analysisResultArray.push(analysisResult);
          analysisTypeArray.push(analysisType);
        }
        
        //******************************************************/
        //***************** Actions Section ********************/
        //******************************************************/
        let actions_prompt = this.processAudienceInPrompt(this.selectedPrompt.prompt) + "\n";

        let position = 1;
        
        // Build re report with the responses from all the variants
        let usedPositions = [];
        
        if (isMatrixMode) {
          
          const responsesByImage = {};
          
          for (let responseIdx = 0; responseIdx < responseMessageArray.length; responseIdx++) {
            const imageNum = applyPositions && applyPositions[responseIdx] ? applyPositions[responseIdx] : responseIdx + 1;
            const content = responseMessageArray[responseIdx].content;
            const variationPrompt = this.customPrompts.find(p => p.id === selectedPrompts[responseIdx % selectedPrompts.length]);
            const variationName = variationPrompt ? variationPrompt.name : `Variation ${(responseIdx % selectedPrompts.length) + 1}`;
            
            if (!responsesByImage[imageNum]) {
              responsesByImage[imageNum] = [];
            }
            
            responsesByImage[imageNum].push({
              variationName,
              content
            });
            
          }
          
          const sortedImageNums = Object.keys(responsesByImage).map(num => parseInt(num)).sort((a, b) => a - b);
          
          for (const imageNum of sortedImageNums) {
            actions_prompt += `Position ${imageNum}: \n`;
            for (const response of responsesByImage[imageNum]) {
              actions_prompt += `  - ${response.variationName}: ${response.content}\n`;
            }
            actions_prompt += "\n";
          }
          
        } else {
          analysisResultArray.forEach((result) => {
            if (isApplyToPrevious && applyPositions.length) {
              position = applyPositions.shift();
              if (usedPositions.includes(position)) {
                position += ' (same image as previous)';
              }
              usedPositions.push(position);
            }
            actions_prompt = actions_prompt + "Position " + position + ": " + result.markdown.content + "\n";
            if (!isApplyToPrevious) {
              position ++;
            }
          });
        }

        let apiSettings = {
          messages: [
          {
              role: "user",
              content: [
                {
                  type: "text",
                  text: actions_prompt
                }
              ],
            },
          ],
          model: this.selectedPrompt.model,
        };
        
        let responseMessage = await this.callOpenAI(apiSettings);
        
        if (!responseMessage || !responseMessage.content) {
          throw new Error("Empty response from analysis service for actions section");
        }

        apiSettingsArray.push(apiSettings);
        responseMessageArray.push(responseMessage)
        analysisResultArray[0].actions = responseMessage.content;


        //******************************************************/
        //************** Product Name section ******************/
        //******************************************************/

        let productNamePrompt = `
          You are an AI product image analyzer. Given an image of a product (such as a box or package), extract and return a short but complete product title that includes:

          The brand name (if visible),

          The product type or name (e.g. "fine point permanent markers"),

          The quantity (e.g. "12 count"),

          And any other key product attribute visible on the packaging.
          Use the information that can be seen clearly in the image (e.g. printed text, logos, visual indicators).

          Return only the product title, no extra text.

          For example, if the image is of a Sharpie product showing 12 fine point permanent markers, return:
          "Sharpie fine point permanent markers, 12 count".
          
          If no clear product title can be extracted from the image, return exactly: UNKNOWN_PRODUCT_TITLE (without quotes, no extra text).
        `;

        let apiSettingsProd = JSON.parse(JSON.stringify(apiSettings));


        // using the first image to get the product name
        apiSettingsProd.messages[0].content[0].text = productNamePrompt;
        apiSettingsProd.messages[0].content.push(
            {
             type: "image_url", 
             image_url:  { url: imageContents[0].image_url.url }
            }
        )
       
        responseMessage = await this.callOpenAI(apiSettingsProd);
        
        if (!responseMessage || !responseMessage.content) {
          throw new Error("Empty response from analysis service for product name section");
        }

        if (responseMessage && responseMessage.content) {
         apiSettingsProd.messages[0].content[1] = 
            {
             type: "image_url", 
             image_url: "1 image attached"
            }
        }

        apiSettingsArray.push(apiSettingsProd);
        responseMessageArray.push(responseMessage)
        analysisResultArray[0].sub_title = responseMessage.content;
        if (responseMessage.content.includes("UNKNOWN_PRODUCT_TITLE")) {
          analysisResultArray[0].sub_title = productTitle || ''; //clear if not found
        }
        analysisResultArray[0].selectedPromptId = this.selectedPrompt.id;
        if (typeof applyOrder !== 'undefined') {
          analysisResultArray[0].applyOrder = applyOrder;
        }
        
        if (isMatrixMode) {
          
          const restructuredResults = [];
          
          const resultsByImage = {};
          
          for (let resultIdx = 0; resultIdx < analysisResultArray.length; resultIdx++) {
            const result = analysisResultArray[resultIdx];
            const imageNum = result.imageNumber || 1;
            
            if (!resultsByImage[imageNum]) {
              resultsByImage[imageNum] = [];
            }
            
            resultsByImage[imageNum].push(result);
          }
          
          for (let imgIdx = 0; imgIdx < imageContents.length; imgIdx++) {
            const imageNum = imgIdx + 1;
            const variationResults = resultsByImage[imageNum] || [];
            
            if (variationResults.length > 0) {
              const combinedResult = {
                ...variationResults[0], // Use first variation as base
                title: `Image ${imgIdx + 1} Analysis`,
                main_title: this.selectedPrompt.name,
                markdown: {
                  content: variationResults.map((vr, idx) => {
                    const variationName = vr.variationName || `Variation ${idx + 1}`;
                    return `**${variationName}:**\n${vr.markdown.content || vr.markdown}`;
                  }).join('\n\n')
                },
                _variations: variationResults, // Keep original variations for reference
                _imageIndex: imgIdx
              };
              
              if (imageContents[imgIdx] && imageContents[imgIdx].image_url) {
                const imageUrl = imageContents[imgIdx].image_url.url || imageContents[imgIdx].image_url;
                const highResUrl = imageContents[imgIdx].image_url.highResUrl || imageUrl;
                combinedResult.images = [highResUrl];
              }
              
              restructuredResults.push(combinedResult);
            }
          }
          
          analysisResultArray = restructuredResults;
        }

        const analysisType = analysisTypeArray.length > 0 ? analysisTypeArray[0] : "gap-analysis";
        
        const finalProductRecordId = this.currentProductRecordId;

        const analysisResponse = await axios.post(`/banner/groups/0/analysis-reports`, {
              'api_response': responseMessageArray,
              'api_call_data': JSON.stringify(apiSettingsArray),
              'webscrape_response_html': null,
              'type': analysisType,
              'data': JSON.stringify(analysisResultArray),
              'from_url': this.fromUrl,
              'product_id_list_records_id': finalProductRecordId,
              'customer_name': this.customerName?.trim() || null,
        });
        
        if (this.selectedPrompt.join_images_and_text) {
          try {
            await axios.get(`/analysis-report/join-images/${analysisResponse.data.id}`); 
          } catch (error) {
            //nothing
          }
        }

        const analysisUrl = `/analysis-reports/${analysisResponse.data.id}/${analysisResponse.data.uuid}`;

        return {
            url: analysisUrl,
            result: analysisResultArray,
            id: analysisResponse.data.analytics_history_id
        };

      } catch (error) {
        console.error("Error processing analysis:", error);
        throw error;
      }
    },

    extractDomain(url) {
      if (url.includes('salsify:')) return 'Salsify';
      const urlObj = new URL(url);
      let domain = urlObj.hostname;
      
      const parts = domain.split('.');
      if (parts.length >= 2) {
        domain = parts.slice(-2).join('.');
      }
      
      domain = domain.charAt(0).toUpperCase() + domain.slice(1);
      
      return domain;
    },

    handleGlobalPaste(event) {
      if (this.step !== 3) return;
      
      const activeElement = document.activeElement;
      const isInputFocused = activeElement && (
        activeElement.tagName === 'INPUT' || 
        activeElement.tagName === 'TEXTAREA' ||
        activeElement.contentEditable === 'true'
      );
      
      if (isInputFocused) {
        return;
      }
      
      event.preventDefault();
      
      if (this.$refs.uploadContainer) {
        this.$refs.uploadContainer.focus();
      }
      
      this.handlePaste(event);
    },

    handleGlobalDragOver(event) {
      if (this.step !== 3) return;
      
      event.preventDefault();
      event.stopPropagation();
      
      document.body.classList.add('dragging-files');
    },

    handleGlobalDragLeave(event) {
      if (this.step !== 3) return;
      
      if (event.target === document.documentElement || event.target === document.body) {
        document.body.classList.remove('dragging-files');
      }
    },

    handleGlobalDrop(event) {
      if (this.step !== 3) return;
      event.preventDefault();
      event.stopPropagation();
      
      document.body.classList.remove('dragging-files');
      
      if (event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files.length > 0) {
        const files = Array.from(event.dataTransfer.files);
        
        const imageFiles = files.filter(file => file.type.startsWith('image/'));
        
        if (imageFiles.length === 0) {
          toastr.warning("Please drop image files only");
          return;
        }
        
        imageFiles.forEach(file => {
          this.$refs.fileInput.addFile(file);
        });
        
        toastr.success(`${imageFiles.length} image(s) added successfully`);
      }
    },

    setupGlobalListeners() {
      document.addEventListener('paste', this.handleGlobalPaste);
      
      document.addEventListener('dragover', this.handleGlobalDragOver);
      document.addEventListener('dragleave', this.handleGlobalDragLeave);
      document.addEventListener('drop', this.handleGlobalDrop);
    },

    removeGlobalListeners() {
      document.removeEventListener('paste', this.handleGlobalPaste);
      document.removeEventListener('dragover', this.handleGlobalDragOver);
      document.removeEventListener('dragleave', this.handleGlobalDragLeave);
      document.removeEventListener('drop', this.handleGlobalDrop);
      
      document.body.classList.remove('dragging-files');
    },

    handlePaste(event) {
      if (!event.defaultPrevented) {
        event.preventDefault();
      }

      if (!event.clipboardData || !event.clipboardData.items) {
        toastr.warning("Clipboard data not available");
        return;
      }

      const items = event.clipboardData.items;
      let imagesFound = 0;
      let textFound = false;

      for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf("image") !== -1) {
          const blob = items[i].getAsFile();
          if (!blob) continue;

          const pastedFile = new File(
            [blob],
            `pasted-image-${Date.now()}-${i}.png`,
            {
              type: blob.type,
            }
          );

          if (this.isComparisonAnalysis()) {
            if (imagesFound === 0) {
              const reader = new FileReader();
              reader.readAsDataURL(blob);
              reader.onloadend = () => {
                this.imageAPreviewUrl = reader.result;
              };
            } else if (imagesFound === 1) {
              const reader = new FileReader();
              reader.readAsDataURL(blob);
              reader.onloadend = () => {
                this.imageBPreviewUrl = reader.result;
              };
            }
            this.$refs.fileInput.addFile(pastedFile);
          } else {
            if (imagesFound === 0) {
              const reader = new FileReader();
              reader.readAsDataURL(blob);
              reader.onloadend = () => {
                this.imageAPreviewUrl = reader.result;
              };
            }
            this.$refs.fileInput.addFile(pastedFile);
          }
          
          imagesFound++;
        }
      }

      if (imagesFound === 0 && this.activeTab === 1) { //ignore paste action in case URLs/ASINs tab is not selected
        for (let i = 0; i < items.length; i++) {
          if (items[i].type === "text/plain") {
            items[i].getAsString((text) => {
              const trimmedText = text.trim();
              
              if (trimmedText) {
                const urlPattern = /^(https?:\/\/|www\.|[a-zA-Z0-9-]+\.[a-zA-Z]{2,}|[A-Z0-9]{10}|B[A-Z0-9]{9}|A-\d{8,9})/i;
                
                if (urlPattern.test(trimmedText) || trimmedText.includes('.')) {
                  const processedUrl = this.processURLInput(trimmedText);
                  
                  if (this.isComparisonAnalysis()) {
                    if (this.comparisonMultipleInput.trim()) {
                      this.comparisonMultipleInput += '\n' + processedUrl;
                    } else {
                      this.comparisonMultipleInput = processedUrl;
                    }
                    toastr.success("URL added to comparison list");
                  } else {
                    if (this.multipleUrlsInput.trim()) {
                      this.multipleUrlsInput += '\n' + processedUrl;
                    } else {
                      this.multipleUrlsInput = processedUrl;
                    }
                  }
                  
                  textFound = true;
                } else {
                  toastr.warning("Pasted text doesn't appear to be a valid URL, ASIN, or TCIN");
                }
              } else {
                toastr.warning("Pasted text is empty");
              }
            });
            break;
          }
        }
      }

      if (imagesFound > 0) {
        toastr.success(`${imagesFound} image(s) pasted successfully`);
        
        if (this.$refs.uploadContainer) {
          this.$refs.uploadContainer.focus();
        }
      } else if (!textFound) {
        setTimeout(() => {
          if (!textFound) {
            toastr.warning("No image or valid URL found in clipboard");
          }
        }, 100);
      }
    },

    isASIN(input) {
      const asinPattern = /^B[A-Z0-9]{9}$/i;
      return asinPattern.test(input.trim());
    },

    convertASINToURL(asin) {
      return `https://www.amazon.com/dp/${asin.trim().toUpperCase()}`;
    },

    isTCIN(input) {
      const tcinPattern = /^A-\d{8,9}$/i;
      return tcinPattern.test(input.trim());
    },

    convertTCINToURL(tcin) {
      return `https://www.target.com/p/-/${tcin.trim().toUpperCase()}`;
    },

    processURLInput(input) {
      const trimmedInput = input.trim();
      
      if (!trimmedInput) {
        return trimmedInput;
      }
      
      if (this.isASIN(trimmedInput)) {
        return this.convertASINToURL(trimmedInput);
      }
      
      if (this.isTCIN(trimmedInput)) {
        return this.convertTCINToURL(trimmedInput);
      }

      if (trimmedInput.includes('amazon.com') && trimmedInput.includes('/dp/')) {
        return this.truncateAmazonUrl(trimmedInput);
      }

      if (trimmedInput.startsWith('http://') || trimmedInput.startsWith('https://')) {
        return trimmedInput;
      }
      
      if (trimmedInput.includes('.') && !trimmedInput.includes(' ')) {
        return `https://${trimmedInput}`;
      }
      
      return trimmedInput;
    },

    processAllURLInputs() {
      if (this.isComparisonAnalysis()) {
        if (this.comparisonMultipleInput.trim()) {
          // Process URLs in textarea
          const urls = this.comparisonMultipleInput.split(/[\s\n\r]+/).filter(url => url.trim());
          const processedUrls = urls.map(url => this.processURLInput(url));
          this.comparisonMultipleInput = processedUrls.join('\n');
        }
      } else {
        if (this.urlInput) {
          this.urlInput = this.processURLInput(this.urlInput);
        }
      }
    },
    
    isPromptAParent(aPrompt){
        return !aPrompt.is_variation;
    },

    openSelectFolderModal(mode) {
      this.selectFolderMode = mode;
      this.$bvModal.show('select-folder-modal')
    },

    openLayoutModal() {
      this.$bvModal.show('select-layout-modal')
    },

    async checkBynderAvailability() {
      const { data } = await axios.post('/bynder/check-platform');

      this.isBynderAvailable = data;
    },

    openExternalAssetsModal(source) {
      this.externalAssetsSource = source;
      this.$bvModal.show('external-assets-modal');
    },

    async processAssetToFile(path, name, extension) {
      const response = await fetch(
        `${window.location.origin}/share?file=${path}`
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch asset: ${response.statusText}`);
      }

      const blob = await response.blob();

      const reader = new FileReader();

      reader.readAsDataURL(blob);

      await new Promise((resolve) => {
        reader.onloadend = () => {
          if (!this.imageAPreviewUrl) {
            this.imageAPreviewUrl = reader.result;
          } else if (this.isComparisonAnalysis() && !this.imageBPreviewUrl) {
            this.imageBPreviewUrl = reader.result;
          }
          resolve();
        };
      });

      const fileName = `${name}.${extension}`;

      const file = new File([blob], fileName, {
        type: blob.type || this.getFileType(fileName),
      });

      return file;
    },

    async handleExternalAssetsSelected(selectedAssets) {
      if (!selectedAssets || selectedAssets.length === 0) return;

      for (const asset of selectedAssets) {
        try {
          const file = await this.processAssetToFile(asset.path, asset.name, asset.extension);

          this.$refs.fileInput.addFile(file);
        } catch (error) {
          console.error("Error processing Bynder asset:", error);
          toastr.error(`Failed to process asset: ${asset.name}`);
        }
      }
    },

    async uploadFilesToFolder(items) {
      const folderId = items[0].id;
      const folderName = items[0].name;
      const type = items[0].type;
      if (type !== "folder") {
        toastr.error("Please select a folder to save images");
        return;
      }
      if (!folderId) {
        toastr.error("Invalid folder selected");
        return;
      }
      if (this.file.length === 0) {
        toastr.error("No images to upload");
        return;
      }

      const uploadedFiles = [];

      for (const f of this.$refs.fileInput.getAcceptedFiles()) {
        const blob = f instanceof File ? f : new Blob([f]);
        const file = new File([blob], f.name || `uploaded-${Date.now()}.png`, {
          type: f.type || "image/png",
        });
        uploadedFiles.push(file);
      }
      
      try {
        Swal.fire({
          title: 'Uploading...',
          html: `<div class="progress-bar-container" style="text-align:center">
                    <div class="progress">
                            <div id="upload_progress_bar" class="progress-bar progress-bar-animated progress-bar-striped bg-success" role="progressbar" style="width: 0%" aria-valuenow="0" aria-valuemin="0" aria-valuemax="100"></div>
                    </div>
                </div>`,
          allowOutsideClick: false,
          showConfirmButton: false,
          showCancelButton: false,
        });
        const uploadedData = await this.uploadFileInChunks(uploadedFiles, folderId, false);
        if (uploadedData && uploadedData.length > 0) {
          toastr.success(`Successfully uploaded ${uploadedFiles.length} images to ${folderName}`);
        } else {
          toastr.error(`Failed to upload images to ${folderName}`);
        }
      } catch (e) {
        toastr.error(`Error uploading images to ${folderName}`);
      } finally {
        Swal.close();
      }
    },
    async  uploadFileInChunks(files, selectedFolderId, isReplace) {
      const CHUNK_SIZE = 5 * 1024 * 1024;
      
      const uploadedData = (await uploadFolderFile(files, selectedFolderId, isReplace)).map(item => ({
        ...item,
        file: files[Number(item.data.key)],
      }));

      await Promise.all(uploadedData.map(async ({data, file, ...item}) => {
        const size = data.size;
        const chunks = Math.ceil(size / CHUNK_SIZE);
        let chunk = 1;
        let uploadedBytes = 0;

        while (chunk <= chunks) {
          const offset = (chunk - 1) * CHUNK_SIZE;
          const signed_res = await Requests.signPartUpload({ ...item, partNumber: chunk });
          const chunk_file = file.slice(offset, offset + CHUNK_SIZE);

          await uploadFile(signed_res.data.url, chunk_file, percent => {
            uploadedBytes += (chunk_file.size * percent);
            const percentCompleted = Math.round((uploadedBytes / size) * 100);
            $('#upload_progress_bar').css('width', percentCompleted + '%');
          });

          chunk++;
        }
        try {
          let res;
          res = await Requests.getMultipartUploadedParts(item);
          await Requests.completeMultipartUpload({
            ...item,
            parts: res.data.parts,
          });
        } catch (e) {
          if (e.response?.data?.message) {
            console.error(e.response.data.message);
          } else {
            toastr.error(`Something went wrong!`);
          }
        }
      }))
      return uploadedData;
    },
    async handleExportToLayout(payload) {
      this.isExporting = true;
      this.layoutURL = null;
      try {
        // getting all files from the file input
        this.$bvModal.show('export-status');
        const files = this.$refs.fileInput.getAcceptedFiles();
        if (files.length === 0) {
          toastr.error("No files to export");
          return;
        }
        const formData = new FormData();
        for (let i = 0; i < files.length; i++) {
          formData.append(`file_${i + 1}`, files[i]);
          formData.append(`filename_${i + 1}`, files[i].name);
        }
        formData.append('customer_id', payload.customer_id);
        const imageResponse = await axios.post('/banner/upload_bk_image', formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
        });
        const uploadedImage = imageResponse.data;
        const createLayoutTemplatePayload = {
          backgroundThemeImages: uploadedImage.map(item => item.path),
          name: payload.templateName,
          selectedLayout: payload.layout.id,
          strategy: payload.strategy
        };
        if (payload.strategy === IMPORT_INTO_EXISTS_TEMPLATE_STRATEGY) {
          createLayoutTemplatePayload.template_ids = payload.template_ids
        }
        const  createLayoutTemplateResponse = await axios.post(`/banner/${payload.customer_id}/group/create_layout_and_template_from_image`, createLayoutTemplatePayload, {
          headers: {
            'Content-Type': 'application/json'
          }
        })
        this.isExporting = false;
        if (createLayoutTemplateResponse.data && createLayoutTemplateResponse.data.layout_id) {
          this.layoutURL = '/banner/' + createLayoutTemplateResponse.data.customer_id + '/group/' + createLayoutTemplateResponse.data.layout_id + '/show';
          toastr.success("Exported to layout successfully");
        } else {
          toastr.error("Failed to export to layout");
        }
      } catch (error) {
        this.isExporting = false;
        if (error.response && error.response.data && error.response.data.message) {
          toastr.error(error.response.data.message);
        } else {
          toastr.error("Failed to export to layout");
        }
      }
     
    },

    onUsePreviousAsinClick() {
      if (this.isLoading) {
        return;
      }
      this.usePreviousImages(1);
    },

    seedLastUrlFromProp() {
      if (this.lastUrl && typeof this.lastUrl === 'string' && this.lastUrl.trim()) {
        const firstUrl = this.lastUrl.trim().split('\n').map((line) => line.trim()).find(Boolean);
        if (firstUrl) {
          this.lastUrlData = firstUrl;
        }
      }
    },

    async usePreviousImages(getUrl = false) {
      try {
        this.isLoading = true;

        const params = { get_url: getUrl };

        if (window.currentUserCompanyId) {
          params.company_id = window.currentUserCompanyId;
        }

        const { data } = await axios.get('/analysis-report/last-used-images', { params });

        if (!data || data.status === 'error') {
          toastr.error(data?.message || 'Failed to load previous images');
          return;
        }
        
        if (getUrl) {
          // Handle comparison URLs
          if (data.comparison_urls && data.comparison_urls.is_comparison) {
            // Populate textarea
            const urls = [];
            if (data.comparison_urls.item_url_1) urls.push(data.comparison_urls.item_url_1);
            if (data.comparison_urls.item_url_2) urls.push(data.comparison_urls.item_url_2);
            if (data.comparison_urls.item_url_3) urls.push(data.comparison_urls.item_url_3);
            this.comparisonMultipleInput = urls.join('\n');
            toastr.success('Previous comparison URLs loaded successfully');
          } else if (data.urls && Array.isArray(data.urls) && data.urls.length > 0) {
            this.multipleUrlsInput = data.urls.join('\n');
            toastr.success('Previous URLs loaded successfully');
          } else if (data.url) {
            this.multipleUrlsInput = data.url;
            toastr.success('Previous URL loaded successfully');
          }
          return;
        }

        const images = data.images || [];
        const urlToImagesMap = data.url_to_images_map || {};
        
        
        this.urlToImagesMapping = urlToImagesMap;
        
        // If so, populate multipleUrlsInput to trigger batch processing
        if (data.manual_item_url) {
          const urlCount = (data.manual_item_url.match(/\n/g) || []).length + 1;
          if (urlCount > 1) {
            this.multipleUrlsInput = data.manual_item_url;
            toastr.info(`Loaded ${urlCount} URLs and their corresponding images from previous reports`);
          } else {
            this.manualItemUrl = data.manual_item_url;
          }
        } else if (data.item_url) {
          this.manualItemUrl = data.item_url;
        }

        if (images.length === 0) {
          toastr.warning('No previous images found');
          return;
        }

        if (this.$refs.fileInput) {
          this.$refs.fileInput.removeAllFiles(true);
        }

        this.enableSpecificImages = false;
        this.specificImageNumbers = "";

        for (const [index, imageData] of images.entries()) {
          try {
            const filename = imageData.name || `previous-image-${Date.now()}-${index}.jpg`;
            const base64Image = imageData.image || imageData;
            const file = dataURLtoFile(base64Image, filename);

            if (index === 0) {
              this.imageAPreviewUrl = base64Image;
            } else if (index === 1 && this.isComparisonAnalysis()) {
              this.imageBPreviewUrl = base64Image;
            }

            this.$refs.fileInput.addFile(file);
          } catch (error) {
            console.error('Error processing base64 image:', error);
          }
        }

        toastr.success(`Added ${images.length} previous images`);
      } catch (error) {
        console.error('Error fetching previous images:', error);
        toastr.error(error.response?.data?.message || 'Failed to load previous images');
      } finally {
        this.isLoading = false;
      }
    },

    async fetchLastUrl() {
      try {
        const params = { get_url: true };
        if (window.currentUserCompanyId) {
          params.company_id = window.currentUserCompanyId;
        }
        const { data } = await axios.get('/analysis-report/last-used-images', { params });

        if (data && data.status !== 'error') {
          if (data.comparison_urls && data.comparison_urls.is_comparison) {
            this.lastUrlData = data.comparison_urls.item_url_1 || null;
          } else if (data.urls && Array.isArray(data.urls) && data.urls.length > 0) {
            this.lastUrlData = data.urls[0];
          } else if (data.url) {
            this.lastUrlData = data.url;
          }
        }
      } catch (error) {
        console.error('Failed to fetch last URL:', error);
      }
    },

    openScreenshotModal() {
      this.$bvModal.show('page-screenshot-modal');
      this.parsePdpImages = false;
    },

    resetScreenshotModal() {
      // this.screenshotUrl = '';
      this.isCapturingScreenshot = false;
      this.screenshotAsChunks = false;
      this.showScreenshotAdvanced = false;
    },

    async capturePageScreenshot(bvModalEventOrOptions = null, url = null, getImages = null) {
      let options = {};

      // Support new object-based signature
      if (
        bvModalEventOrOptions &&
        typeof bvModalEventOrOptions === 'object' &&
        (
          Object.prototype.hasOwnProperty.call(bvModalEventOrOptions, 'bvModalEvent') ||
          Object.prototype.hasOwnProperty.call(bvModalEventOrOptions, 'url') ||
          Object.prototype.hasOwnProperty.call(bvModalEventOrOptions, 'getImages') ||
          Object.prototype.hasOwnProperty.call(bvModalEventOrOptions, 'chunks') ||
          Object.prototype.hasOwnProperty.call(bvModalEventOrOptions, 'chunkHeight') ||
          Object.prototype.hasOwnProperty.call(bvModalEventOrOptions, 'chunkOverlap') ||
          Object.prototype.hasOwnProperty.call(bvModalEventOrOptions, 'maxChunks') ||
          Object.prototype.hasOwnProperty.call(bvModalEventOrOptions, 'smartScreenshot') ||
          Object.prototype.hasOwnProperty.call(bvModalEventOrOptions, 'addFilesToInput') ||
          Object.prototype.hasOwnProperty.call(bvModalEventOrOptions, 'hideModal') ||
          Object.prototype.hasOwnProperty.call(bvModalEventOrOptions, 'resetModal')
        )
      ) {
        options = {
          bvModalEvent: bvModalEventOrOptions.bvModalEvent ?? null,
          url: bvModalEventOrOptions.url ?? null,
          getImages: bvModalEventOrOptions.getImages ?? false,
          chunks: bvModalEventOrOptions.chunks ?? this.screenshotAsChunks,
          chunkHeight: bvModalEventOrOptions.chunkHeight ?? this.screenshotChunkHeight,
          chunkOverlap: bvModalEventOrOptions.chunkOverlap ?? this.screenshotChunkOverlap,
          maxChunks: bvModalEventOrOptions.maxChunks ?? this.screenshotMaxChunks,
          smartScreenshot: bvModalEventOrOptions.smartScreenshot ?? this.smartScreenshot,
          addFilesToInput: bvModalEventOrOptions.addFilesToInput ?? true,
          hideModal: bvModalEventOrOptions.hideModal ?? true,
          resetModal: bvModalEventOrOptions.resetModal ?? true,
        };
      } else {
        // Legacy signature support
        options = {
          bvModalEvent: bvModalEventOrOptions,
          url,
          getImages: getImages ?? false,
          chunks: this.screenshotAsChunks,
          chunkHeight: this.screenshotChunkHeight,
          chunkOverlap: this.screenshotChunkOverlap,
          maxChunks: this.screenshotMaxChunks,
          smartScreenshot: this.smartScreenshot,
          addFilesToInput: true,
          hideModal: true,
          resetModal: true,
        };
      }

      const {
        bvModalEvent,
        getImages: shouldReturnImages,
        addFilesToInput,
        hideModal,
        resetModal,
      } = options;

      let { url: targetUrl } = options;

      if (bvModalEvent) {
        bvModalEvent.preventDefault();
      }

      if (!targetUrl && !this.screenshotUrl) {
        toastr.error('Please enter a URL or ASIN');
        return shouldReturnImages ? [] : undefined;
      }

      if (!targetUrl) {
        try {
          new URL(this.screenshotUrl);
        } catch (e) {
          toastr.error('Please enter a valid URL or ASIN');
          return shouldReturnImages ? [] : undefined;
        }

        targetUrl = this.screenshotUrl;
      }

      this.isCapturingScreenshot = true;

      try {
        const { data } = await axios.post('/capture-page-screenshot', {
          url: targetUrl,
          format: 'jpeg',
          chunks: options.chunks,
          chunk_height: options.chunkHeight,
          chunk_overlap: options.chunkOverlap,
          max_chunks: options.maxChunks,
          smart_screenshot: options.smartScreenshot,
        });

        if (data.status === 'error') {
          toastr.error(data.message || 'Failed to capture screenshot');
          return shouldReturnImages ? [] : undefined;
        }

        const images = [];

        if (options.chunks) {
          (data.chunks || []).forEach((chunk, index) => {
            if (!chunk?.image) {
              return;
            }

            images.push(chunk.image);

            if (addFilesToInput) {
              const timestamp = Date.now();
              const filename = `screenshot-${timestamp}-${index}.jpg`;
              const file = dataURLtoFile(chunk.image, filename);

              if (this.$refs.fileInput) {
                this.$refs.fileInput.addFile(file);
              }
            }
          });
        } else if (data.image) {
          images.push(data.image);

          if (addFilesToInput) {
            const timestamp = Date.now();
            const filename = `screenshot-${timestamp}.jpg`;
            const file = dataURLtoFile(data.image, filename);

            if (this.$refs.fileInput) {
              this.$refs.fileInput.addFile(file);
            }
          }

          this.imageAPreviewUrl = data.image;
        }

        toastr.success('Screenshot captured successfully');

        if (hideModal) {
          this.$bvModal.hide('page-screenshot-modal');
        }

        if (resetModal) {
          this.resetScreenshotModal();
        }

        if (shouldReturnImages) {
          return images;
        }
      } catch (error) {
        console.error('Error capturing screenshot:', error);
        toastr.error(error.response?.data?.message || 'Failed to capture screenshot');
        return shouldReturnImages ? [] : undefined;
      } finally {
        this.isCapturingScreenshot = false;
      }
    },

    downloadReports() {
      // Prepare the data for XLSX
      const data = this.analysisReports.map((report, index) => ({
        "Report #": index + 1,
        "Report Link": window.location.origin + (typeof report === 'object' ? report.url : report),
        "Original URL": typeof report === 'object' ? report.originalUrl : '',
      }));

      // Create a worksheet
      const ws = XLSX.utils.json_to_sheet(data);

      // Create a workbook
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Reports");

      // Generate the Excel file and trigger a download
      const date = new Date();
      const timestamp = date
        .toISOString()
        .replace(/[-T:]/g, "_") // Replace special characters with underscores
        .split(".")[0]; // Remove milliseconds
      const filename = `AnalysisReports_${timestamp}.xlsx`;
      
      // Generate the Excel file and trigger a download
      XLSX.writeFile(wb, filename);
    },
    clearLoadingStatus() {
      this.showLoadingStatus = false;
      this.loadingStatusText = 'Preparing to run...';
      this.loadingStatusHelperText = '';
    },
    
    formatDate(dateStr) {
      return new Date(dateStr).toLocaleString();
    },

    redirectToAnalyticsHistory(reportIds = null, forceRedirect = false) {
      if (!forceRedirect) {
        if (this.selectedPrompts && this.selectedPrompts.length > 1 && this.reportsProcessed < this.selectedPrompts.length) {
          return false;
        }

        if (this.urlsToScrape.length > 1 && !this.isLastMultipleUrl) {
          return false;
        }
      }
      // Set redirecting flag to show success message
      this.isRedirecting = true;

      // Show success message briefly before redirecting
      const baseUrl = '/optix';

      setTimeout(() => {
        let url = baseUrl;

        if (reportIds) {
          if (Array.isArray(reportIds)) {
            // Multiple report IDs - join them with commas
            if (reportIds.length > 0) {
              reportIds = [...new Set(reportIds)];
              url = `${baseUrl}?filter_report_ids=${reportIds.join(',')}&_t=${Date.now()}#reports`;
            }
          } else {
            // Single report ID
            url = `${baseUrl}?filter_report_id=${reportIds}&_t=${Date.now()}#reports`;
          }
        } else {
          url = `${baseUrl}#reports`;
        }

        const inIframe = () => {
          try {
            return window.self !== window.top;
          } catch (e) {
            return true;
          }
        };

        if (inIframe()) {
          window.parent.postMessage({
            type: 'analytics-redirect',
            url: url
          }, '*');

          try {
            window.parent.location.href = url;
          } catch (e) {
            console.error('Cross-origin iframe detected, using postMessage for redirect');
          }
        } else {
          window.location.href = url;
        }
      }, 1500); // 1.5 second delay to show success message
    },

    // Audiences Management Methods
    toggleAudiencesList() {
      this.showAudiencesList = !this.showAudiencesList;
      if (!this.showAudiencesList) {
        this.creatingFromAnalysisFlow = false;
      }

      this.updateHeaderButton();
    },

    openNewAudienceModal() {
      if (!this.showAudiencesList) {
        this.showAudiencesList = true;
        this.updateHeaderButton();
        this.creatingFromAnalysisFlow = true;
      }
      this.$nextTick(() => {
        this.showAudienceModal('create');
      });
    },

    updateHeaderButton() {
      const button = document.getElementById('configureAudiencesButton');

      if (!button) {
        return;
      }

      if (this.showAudiencesList || this.showBrandGuidelinesList) {
        button.innerHTML = '<i class="bi bi-arrow-left mr-1"></i> Back to Optix Lab';
        return;
      }

      button.innerHTML = '<i class="bi bi-people mr-1"></i> Configure Audiences';
    },

    updateAudienceAnalysis(audiencies, selectedAudiences) {
      this.audiences = audiencies;
      this.selectedAudiences = selectedAudiences;
      this.showAudiencesList = false;
      this.updateHeaderButton();
      this.creatingFromAnalysisFlow = false;
      this.showAudienceModalFlag = false;
    },

    updateAudience(audiencies, selectedAudiences) {
      this.audiences = audiencies;
      this.selectedAudiences = selectedAudiences;
    },

    async loadAudiences() {
      this.isLoadingAudiences = true;
      try {
        const response = await axios.get('/audiences', {
          params: {
            sort_by: this.audiencesSortBy,
            sort_order: this.audiencesSortOrder,
          }
        });
        if (response.data.success) {
          this.audiences = response.data.data;
          if (this.isRerunMode && this.rerunData && this.rerunData.audience) {
            this.applyRerunPreloadData(this.rerunData);
          }
        }
      } catch (error) {
        console.error('Error loading audiences:', error);
        toastr.error('Failed to load audiences');
      } finally {
        this.isLoadingAudiences = false;
      }
    },

    showAudienceModal(mode) {
      this.audienceModalMode = mode;
      this.showAudienceModalFlag = true;
    },
    async checkPlatformAvailability(platform) {
      const { data } = await axios.post('/platforms/is-platform-available', { platform });

      return data.available;
    },

    togglePimDamDropdown(event) {
      event.stopPropagation();
      this.showPimDamDropdown = !this.showPimDamDropdown;

      if (this.showPimDamDropdown) {
        this.$nextTick(() => {
          document.addEventListener('click', this.closePimDamDropdown);
        });
      } else {
        document.removeEventListener('click', this.closePimDamDropdown);
        event.target.closest('button').blur();
      }
    },

    closePimDamDropdown(event) {
      const dropdown = this.$el.querySelector('.pim-dam-dropdown');
      const gearBtn = this.$el.querySelector('.pim-dam-gear-btn');

      if (dropdown && !dropdown.contains(event.target) && !gearBtn.contains(event.target)) {
        this.showPimDamDropdown = false;
        document.removeEventListener('click', this.closePimDamDropdown);
      }
    },

    openSetupConnectionModal() {
      this.showPimDamDropdown = false;
      document.removeEventListener('click', this.closePimDamDropdown);
      $(document).trigger('open-setup-connection-modal');
    },
    handleOpenFileBrowser(payload) {
      this.selectFolderModal.viewTabs = payload.viewTabs;
    },
    // Brand Guidelines Management Methods
    toggleBrandGuidelinesList() {
      this.showBrandGuidelinesList = !this.showBrandGuidelinesList;

      if (this.showBrandGuidelinesList) {
        this.showAudiencesList = false;
      }

      if (!this.showBrandGuidelinesList) {
        this.creatingBrandGuidelineFromAnalysisFlow = false;
        this.isBrandGuidelinesEditMode = false;
      }

      this.updateHeaderButton();
    },

    openNewBrandGuideline() {
      if (!this.showBrandGuidelinesList) {
        this.showBrandGuidelinesList = true;
        this.showAudiencesList = false;
        this.creatingBrandGuidelineFromAnalysisFlow = true;
        this.updateHeaderButton();
      }
    },

    updateBrandGuidelinesAnalysis(brandGuidelines, selectedBrandGuidelines) {
      this.brandGuidelines = brandGuidelines;
      this.selectedBrandGuidelines = selectedBrandGuidelines;
      this.showBrandGuidelinesList = false;
      this.creatingBrandGuidelineFromAnalysisFlow = false;
      this.isBrandGuidelinesEditMode = false;
      this.updateHeaderButton();
    },

    updateBrandGuidelines(brandGuidelines, selectedBrandGuidelines = this.selectedBrandGuidelines) {
      this.brandGuidelines = brandGuidelines;
      this.selectedBrandGuidelines = selectedBrandGuidelines;
    },

    async loadBrandGuidelines() {
      this.isLoadingBrandGuidelines = true;

      try {
        const result = await axios.get('/brand-guidelines');

        if (result.status === 200) {
          this.brandGuidelines = result.data.data || result.data;
          if (
            this.isRerunMode &&
            this.rerunData &&
            Array.isArray(this.rerunData.brand_guidelines)
          ) {
            const selectedIds = this.rerunData.brand_guidelines.map(String);

            this.selectedBrandGuidelines = this.brandGuidelines.filter(
              guideline => selectedIds.includes(String(guideline.id))
            );
          }
        }
      } catch (error) {
        console.error('Error loading brand guidelines:', error);
        toastr.error('Failed to load brand guidelines');
      } finally {
        this.isLoadingBrandGuidelines = false;
      }
    },
    getSelectedBrandGuidelinesPayload() {
      return this.selectedBrandGuidelines.map((guideline) => ({
        id: guideline.id,
        brand_name: guideline.brand_name,
        voice: guideline.voice,
        visual_style: guideline.visual_style,
        guidelines: guideline.guidelines,
        documents_count: guideline.docs_count || guideline.documents_count || 0,
        images_count: guideline.images_count || 0,
      }));
    },
    async loadSeasonalities() {
      this.isLoadingSeasonalities = true;

      try {
        const response = await axios.get('/seasonalities');

        if (response.data.success) {
          this.seasonalities = response.data.data || [];
        }
      } catch (error) {
        console.error('Error loading seasonalities:', error);
        toastr.error('Failed to load seasonalities');
      } finally {
        this.isLoadingSeasonalities = false;
      }
    },
    onSeasonalityCreated(seasonality) {
      this.seasonalities = [
        seasonality,
        ...this.seasonalities.filter(item => item.id !== seasonality.id),
      ];

      this.selectedSeasonalities = [
        ...this.selectedSeasonalities,
        seasonality,
      ];
    },
    getSelectedSeasonalitiesPayload() {
      return this.selectedSeasonalities.map((seasonality) => ({
        id: seasonality.id,
        name: seasonality.name,
        description: seasonality.description,
      }));
    },
    async detectPdfPageImageLabels(pageImageDataUrl, pageNumber, fileName) {
      if (!pageImageDataUrl) {
        return [];
      }

      try {
        const { data } = await axios.post('/detect-pdf-page-image-labels', {
          image_url: pageImageDataUrl,
          page: pageNumber,
          file_name: fileName || null,
        });

        return Array.isArray(data?.labels) ? data.labels : [];
      } catch (error) {
        console.error(`Failed to detect PDF page labels for page ${pageNumber}:`, error);
        return [];
      }
    },

    normalizePdfPageLabels(labels, pageNumber) {
      return (Array.isArray(labels) ? labels : [])
        .map((item, index) => {
          const label = String(item.label || item.image_type_label || '').trim();

          return {
            page: Number(item.page || pageNumber),
            part: Number(item.part || index + 1),
            label,
            confidence: item.confidence || null,
          };
        })
        .filter(item => item.label);
    },

    canvasToBlob(canvas, mimeType = 'image/png') {
      return new Promise((resolve, reject) => {
        canvas.toBlob(blob => {
          if (!blob) {
            reject(new Error('Failed to create blob from PDF page canvas'));
            return;
          }

          resolve(blob);
        }, mimeType);
      });
    },

    async applyPdfPageLabelsToFiles(files, labels, pageNumber) {
      const normalizedLabels = this.normalizePdfPageLabels(labels, pageNumber);

      if (!files.length || !normalizedLabels.length) {
        return;
      }

      await this.$nextTick();

      files.forEach((file, index) => {
        let label = '';

        if (files.length === 1) {
          // Full-page fallback: one uploaded image represents the whole PDF page.
          // Put all labels into this one input.
          label = normalizedLabels.map(item => item.label).join(', ');
        } else {
          // Unpacked images: sync by visual order.
          label = normalizedLabels[index]?.label || '';
        }

        if (!label) {
          return;
        }

        const currentIndex = this.uploadedImageFiles.indexOf(file);
        const key = this.getImageTypeLabelKey(
          file,
          currentIndex !== -1 ? currentIndex : index
        );

        // Не перетираем ручной label.
        if (!this.imageTypeLabels[key]) {
          this.$set(this.imageTypeLabels, key, label);
        }
      });

      this.isImageTypeLabelsOpen = true;
    },
    getImageTypeLabelKey(file, index) {
      if (file?.upload?.uuid) {
        return file.upload.uuid;
      }

      return [
        file?.name || `image-${index + 1}`,
        file?.size || 0,
        file?.lastModified || 0,
      ].join('__');
    },

    syncImageTypeLabels() {
      const activeKeys = this.uploadedImageFiles.map((file, index) => {
        const key = this.getImageTypeLabelKey(file, index);

        if (typeof this.imageTypeLabels[key] === 'undefined') {
          this.$set(this.imageTypeLabels, key, '');
        }

        return key;
      });

      Object.keys(this.imageTypeLabels).forEach(key => {
        if (!activeKeys.includes(key)) {
          this.$delete(this.imageTypeLabels, key);
        }
      });
    },

    applyRerunImageLabels(imageLabels) {
      if (!Array.isArray(imageLabels) || !imageLabels.length) {
        return;
      }

      this.syncImageTypeLabels();

      imageLabels.forEach((item, fallbackIndex) => {
        const label = String(item?.label || '').trim();

        if (!label) {
          return;
        }

        const imageNumber = Number(item?.image_number);
        const index = imageNumber > 0
          ? imageNumber - 1
          : fallbackIndex;

        const file = this.uploadedImageFiles[index];

        if (!file) {
          return;
        }

        const key = this.getImageTypeLabelKey(file, index);

        this.$set(this.imageTypeLabels, key, label);
      });

      if (this.hasImageTypeLabels) {
        this.isImageTypeLabelsOpen = true;
      }
    },

    getImageTypeLabelForFile(file, index) {
      const key = this.getImageTypeLabelKey(file, index);

      return (this.imageTypeLabels[key] || '').trim();
    },

    getUploadedImageTypeLabelsPayload(files = this.uploadedImageFiles) {
      return files
        .map((file, index) => ({
          image_number: index + 1,
          file_name: file?.name || null,
          label: this.getImageTypeLabelForFile(file, index),
        }))
        .filter(item => item.label);
    },
  },
};
</script>
<style lang="scss" scoped>
// Keep the existing image flow mounted so filters, file inputs and selections survive Back.
.image-analysis-with-visibility > :not(.commerce-visibility-container) {
  display: none !important;
}

.configure-audiences-btn {
  border-color: #dee2e6;
  color: #212529;

  &:hover {
    background-color: #f8f9fa;
    border-color: #dee2e6;
    color: #212529;
  }
}

.audiences-config {
  padding: 0 0 6px;
}

/* Match Save top gap to edit-top-bar margin-bottom (16px).
   Optix .card-body padding-top is 1.25rem (20px). */
.optix-brand-guidelines--editing {
  margin-top: calc(16px - 1.25rem);
}

.audiences-config__backrow {
  margin: 2px 0 10px;
}

.audiences-config__cardbody {
  padding: 24px;
}

.audiences-config__cardhead {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 24px;
}

.audiences-config__back {
  color: #1abc9c;
  text-decoration: none;
  transition-property: color, background-color, border-color, text-decoration-color, fill, stroke;
  transition-timing-function: cubic-bezier(.4, 0, .2, 1);
  transition-duration: .15s;
  padding-top: .5rem;
  padding-bottom: .5rem;
  font-weight: 500;
  font-size: .875rem;
  line-height: 1.25rem;
  padding-left: 1rem;
  padding-right: 1rem;
  border-radius:6px;

  &:hover {
    background-color: #f1f5f9;
    color: #1abc9ccc;
    text-decoration: none;
  }
}

.audiences-config__back svg {
  width: 16px;
  height: 16px;
}

.pim-dam-gear-btn {
  border-color: #dee2e6;
  color: #212529;
  padding: 0.25rem 0.5rem;
  background-color: transparent;

  &:hover {
    background-color: #e9ecef;
    border-color: #dee2e6;
    color: #212529;
  }

  &:focus,
  &:active,
  &:focus:active,
  &.focus,
  &:not(:disabled):not(.disabled):active:focus {
    outline: none !important;
    box-shadow: none !important;
    background-color: #e9ecef;
    border-color: #dee2e6;
    color: #212529 !important;
  }
}

.pim-dam-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 4px;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 280px;
  z-index: 1000;
}

.pim-dam-dropdown-header {
  padding: 12px 16px;
  font-weight: 600;
  font-size: 14px;
  color: #212529;
  border-bottom: 1px solid #f0f0f0;
}

.pim-dam-dropdown-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
}

.pim-dam-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #212529;
}

.pim-dam-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #adb5bd;

  &.connected {
    background-color: #209c86;
  }
}

.pim-dam-status {
  font-size: 14px;
  color: #6c757d;

  &.connected {
    color: #209c86;
  }
}

.pim-dam-connect-link {
  font-size: 14px;
  color: #212529;
  text-decoration: none;
  padding: 4px 12px;
  border-radius: 4px;
  border: 1px solid transparent;

  &:hover {
    border-color: #dee2e6;
    background-color: #f8f9fa;
    text-decoration: none;
  }
}

.pim-dam-warning {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  font-size: 13px;
  color: #6c757d;

  i {
    color: #adb5bd;
  }
}

.card-selection-container {
  padding: 20px;
  background-color: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
}

.card-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 320px));
  gap: 20px;
  max-width: 800px;
}

.selection-card {
  background: white;
  border-radius: 8px;
  border: 2px solid #e0e0e0;
  padding: 24px;
  display: flex;
  flex-direction: column;
  height: 280px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  cursor: pointer;
  position: relative;
}

.selection-card:hover {
  border-color: #d0d0d0;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  transform: translateY(-1px);
}

.selection-card.selected {
  border-color: #10b981;
  background: #f0fdf4;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1), 0 4px 12px rgba(16, 185, 129, 0.15);
  transform: translateY(-2px);
}

.selection-card.selected:hover {
  border-color: #059669;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.15), 0 6px 16px rgba(16, 185, 129, 0.2);
}

.selected-indicator {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 24px;
  height: 24px;
  background: #10b981;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: scaleIn 0.2s ease-out;
}

.checkmark {
  color: white;
}

.font-icons {
  font-size: 48px;
}

@keyframes scaleIn {
  0% {
    transform: scale(0);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
}

.card-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.icon-container {
  margin-bottom: 16px;
}

.card-icon {
  width: 48px;
  height: 48px;
  color: #666;
  stroke-width: 1.5;
}

.card-title {
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin: 0 0 12px 0;
}

.card-description {
  font-size: 14px;
  color: #666;
  line-height: 1.5;
  margin: 0;
  flex: 1;
  display: flex;
  align-items: center;
}

.select-button {
  width: 100%;
  padding: 12px 24px;
  border: 1px solid #ddd;
  border-radius: 6px;
  background: white;
  color: #333;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 20px;
}

.select-button:hover {
  background: #f8f9fa;
  border-color: #bbb;
}

.select-button:active {
  background: #e9ecef;
  transform: translateY(1px);
}

.select-button.selected {
  background: #10b981;
  color: white;
  border-color: #10b981;
}

.select-button.selected:hover {
  background: #059669;
  border-color: #059669;
}

.form-check label {
  cursor: pointer;
}

@media (max-width: 640px) {
  .card-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }
  
  .selection-card {
    height: 260px;
    padding: 20px;
  }
  
  .card-selection-container {
    padding: 16px;
  }
}
.prompts-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-bottom: 24px;
}

@media (max-width: 1200px) {
  .prompts-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 640px) {
  .prompts-grid {
    grid-template-columns: 1fr;
  }
}

.prompt-card {
  background: #fff;
  border: 1px solid #c0c0c0;
  border-radius: 12px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 170px;
  transition: box-shadow 0.2s ease, border-color 0.2s ease, transform 0.2s ease;
  position: relative;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}

.prompt-card:hover {
  border-color: rgba(32, 156, 134, 0.5);
  box-shadow: 0 4px 14px rgba(0,0,0,0.06);

  .prompt-icon {
    background: #209c86;
    color: #ffffff;
  }
}

.prompt-card.disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.prompt-card-header {
  display: flex;
  gap: 12px;
}

.prompt-icon {
  min-width: 40px;
  height: 40px;
  border-radius: 8px;
  background: rgba(32, 156, 134, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #209c86;
  font-size: 20px;
}

.prompt-title {
  font-weight: 600;
  font-size: 16px;
  color: #111827;
}

.prompt-desc {
  font-size: 13px;
  color: #6b7280;
}

.prompt-card-footer {
  margin-top: 12px;
  
  .btn {
    background: none;
  }
  
  :hover > & {
    .btn {
      background: rgba(32, 156, 134, 0.1);
    }
  }
}

.prompt-tag-badge {
  position: absolute;
  top: -8px;
  right: -8px;
  background: #209c86;
  color: #ffffff;
  font-size: 11px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 12px;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  z-index: 1;
}

.optix-lab-analyzer {
  padding: 20px 0;
}

.step-content {
  padding: 20px;
  min-height: 400px;
}

.upload-container {
  border: 2px solid #eaeaea;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 15px;
  background: white;
}

.upload-container:focus {
  outline: none;
  border-color: #80bdff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.paste-instruction {
  background-color: #fbfcfd;
  border: 2px dashed #ccc;
  border-radius: 4px;
  padding: 18px;
  text-align: center;
  margin-bottom: 15px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.paste-instruction:hover {
  background-color: #e9ecef;
  border-color: #adb5bd;
}

.paste-instruction .b-icon {
  margin-right: 8px;
  vertical-align: middle;
}

.upload-count {
  margin: 10px 0;
  text-align: center;
}

.upload-count .badge {
  font-size: 14px;
  padding: 5px 10px;
}

.quick-start-options {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.settings-group {
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;
}

::v-deep .vue-dropzone {
  border: 2px dashed #dee2e6;
  border-radius: 8px;
  background: #ffffff;
  min-height: 120px;
  padding: 20px;
  transition: all 0.3s ease;
  flex-wrap: wrap;
}

::v-deep .vue-dropzone:hover {
  background: #fafafa;
  border-color: #adb5bd;
}

::v-deep .vue-dropzone.dz-drag-hover {
  background: #e8f4f8;
  border-color: #007bff;
}

::v-deep .dz-message {
  font-size: 14px;
  color: #6c757d;
  margin: 0;
}

::v-deep .dz-preview.dz-image-preview {
  margin: 8px;
  min-width: 150px;
  min-height: 150px;
}

::v-deep .dz-preview.dz-image-preview.tall{
  margin-bottom: 30px;
}

::v-deep .dz-preview.dz-image-preview.tall option{
  padding-right: 30px;
}

::v-deep .dz-preview .dz-image {
  border-radius: 4px;
  overflow: hidden;
  border: 1px solid #dee2e6;
  width: 150px;
  height: 150px;
}

::v-deep .dz-preview .dz-image img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

::v-deep .dz-preview .dz-details {
  background: rgba(33, 150, 243, 0.8);
  color: white;
  padding: 4px 6px;
  font-size: 11px;
  min-height: auto;
}

::v-deep .dz-preview.dz-error .dz-error-message {
  top: auto !important;
  bottom: 0 !important;
  pointer-events: none;
}

::v-deep .dz-preview.dz-error .dz-remove {
  position: absolute !important;
  top: 5px !important;
  right: 5px !important;
  bottom: auto !important;
  left: auto !important;
  z-index: 30 !important;
  background: #dc3545 !important;
  color: white !important;
  border-radius: 50% !important;
  width: 28px !important;
  height: 28px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  font-size: 0 !important;
  cursor: pointer !important;
  border: 2px solid white !important;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2) !important;
  pointer-events: auto !important;
}

::v-deep .dz-preview.dz-error .dz-remove::before {
  content: '×' !important;
  font-size: 18px !important;
  font-weight: bold !important;
  line-height: 1 !important;
}

::v-deep .dz-preview.dz-error .dz-remove:hover {
  background: #c82333 !important;
  transform: scale(1.1);
}

::v-deep .dz-preview.dz-error .error-remove-btn {
  position: absolute !important;
  top: 5px !important;
  right: 5px !important;
  z-index: 100 !important;
  background: #dc3545 !important;
  color: white !important;
  border-radius: 50% !important;
  width: 28px !important;
  height: 28px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  cursor: pointer !important;
  border: 2px solid white !important;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2) !important;
  font-size: 16px !important;
  font-weight: bold !important;
  line-height: 1 !important;
}

::v-deep .dz-preview.dz-error .error-remove-btn:hover {
  background: #c82333 !important;
  transform: scale(1.1);
}

.generating-spinner {
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
}

.report-summary-card {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 20px;
  margin: 20px 0;
}

.generation-steps {
  display: flex;
  justify-content: center;
  gap: 40px;
  margin: 30px 0;
}

.step-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 20px;
  border-radius: 8px;
  transition: all 0.3s ease;
  min-width: 120px;
}

.step-item i {
  font-size: 24px;
  margin-bottom: 8px;
  transition: all 0.3s ease;
}

.step-item span {
  font-size: 14px;
  font-weight: 500;
  text-align: center;
}

.step-item.active {
  background: #e3f2fd;
  color: #1976d2;
  transform: scale(1.05);
}

.step-item.active i {
  color: #1976d2;
  animation: bounce 1s infinite;
}

.step-item.completed {
  background: #e8f5e8;
  color: #2e7d2e;
}

.step-item.completed i {
  color: #2e7d2e;
}

@keyframes bounce {
  0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
  40% { transform: translateY(-10px); }
  60% { transform: translateY(-5px); }
}

@media (max-width: 768px) {
  .generation-steps {
    flex-direction: column;
    gap: 20px;
    align-items: center;
  }

  .step-item {
    min-width: 200px;
  }
}

.dropzone-container {
  position: relative;
  transition: all 0.3s ease;
}

.dropzone-container.loading ::v-deep .vue-dropzone {
  filter: blur(3px);
  pointer-events: none;
  opacity: 0.7;
}

.file-browser-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

.file-browser-loading-overlay .loading-content {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: #495057;
}

.manual-item-url-btn:focus {
  box-shadow: none;
  outline: none;
  text-decoration: none;
}

.image-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: flex-start;
  align-items: stretch;
  margin: 10px 0 10px 0;
  padding: 0;
}
.image-card {
  position: relative;
  /* flex: 1 1 calc(25% - 12px); */
  box-sizing: border-box;
  /* min-width: 140px;           
  max-width: 1fr; */
  border-radius: 8px;
  overflow: hidden;
  background: #f4f4f4;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06);
  width: 60px;
  height: 60px;
}
.image-card img {
  display: block;
  width: 100%;
  height: 100%;
  aspect-ratio: 1 / 1;
  object-fit: cover;
  vertical-align: middle;
  transition: transform .25s ease;
}

.audiences-table thead th {
  position: sticky;
  top: 181px;
  z-index: 98;
  background-color: #ffffff !important;
  border-top: 1px solid #dee2e6;
}

.advanced-dropdown .rotate-icon {
  transform: rotate(-180deg);
  transition: transform 0.3s ease;
}

.advanced-dropdown i {
  transition: transform 0.3s ease;
}

.see-job-id-link {
  color: #007bff;
  cursor: pointer;
}

.see-job-id-link:hover {
  color: #4335D6 !important;
  text-decoration: underline;
}

.job-id-container {
  display: flex;
  flex-direction: column;
}

.job-id-display {
  display: inline-flex;
  align-items: center;
  margin-top: 2px;
  font-size: 0.9em;
  color: #555;
}

.copy-icon {
  cursor: pointer;
  color: #6c757d;

  &:hover {
    color: #007bff;
  }
}

.lightbox-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
}

.lightbox-modal {
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.lightbox-header {
  position: fixed;
  top: 20px;
  right: 20px;
  display: flex;
  gap: 8px;
  z-index: 10000;
}

.lightbox-download,
.lightbox-close {
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 50%;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #fff;
  transition: background 0.2s ease;

  svg {
    color: #fff;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
}

.lightbox-image {
  max-width: 100%;
  max-height: 90vh;
  object-fit: contain;
  border-radius: 8px;
  user-select: none;
  -webkit-user-drag: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

.lightbox-nav {
  position: fixed;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 50%;
  width: 52px;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #fff;
  transition: background 0.2s ease;
  z-index: 10000;

  svg {
    color: #fff;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
}

.lightbox-prev {
  left: 40px;
}

.lightbox-next {
  right: 40px;
}

.lightbox-counter {
  position: fixed;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  z-index: 10000;
}

.view-toggle-buttons {
  display: flex;
  align-items: center;
  background: #f3f4f6;
  border-radius: 8px;
  padding: 4px;
  gap: 2px;
}

.view-toggle-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 32px;
  border: none;
  background: transparent;
  border-radius: 6px;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #e5e7eb;
    color: #374151;
  }

  &.active {
    background: #fff;
    color: #111827;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  i {
    font-size: 16px;
  }
}

.list-view-container {
  width: 100%;
}

.list-view-split {
  display: flex;
  gap: 24px;
}

.list-view-left-panel {
  flex: 0 0 40%;
  max-width: 40%;
  max-height: 500px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.analysis-types-list {
  flex: 1;
  overflow-y: auto;
}

.analysis-type-item {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #f3f4f6;
  cursor: pointer;
  transition: background-color 0.15s ease;
  gap: 12px;

  &:hover {
    background-color: #f9fafb;
  }

  &.select-all-item {
    position: sticky;
    top: 0;
    z-index: 10;
    background-color: #f9fafb;
    border-bottom: 2px solid #e5e7eb;
  }
}

.clear-selection-link {
  color: #637381;
  font-size: 14px;
  text-decoration: none;
}

.analysis-type-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: #f3f4f6;
  border-radius: 8px;
  color: #6b7280;
  flex-shrink: 0;

  i {
    font-size: 14px;
  }

  &.selected {
    background: #10b981;
    color: #fff;

    img {
      filter: brightness(0) invert(1);
    }
  }
}

.analysis-type-name-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
}

.analysis-type-name {
  font-size: 14px;
}

.analysis-type-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.analysis-type-info-icon {
  font-size: 16px;
  color: #9ca3af;
  opacity: 0;
  transition: opacity 0.15s ease;
  cursor: pointer;

  &:hover {
    color: #6b7280;
  }
}

.analysis-type-item:hover .analysis-type-info-icon {
  opacity: 1;
}

.prompt-tag-badge-inline {
  background: #209c86;
  color: #ffffff;
  font-size: 11px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 12px;
}

.list-view-right-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
}

@media (max-width: 992px) {
  .list-view-split {
    flex-direction: column;
  }

  .list-view-left-panel {
    flex: none;
    max-width: 100%;
  }

  .analysis-types-list {
    max-height: 300px;
  }
}

</style>

<style>
/* Unscoped: optix URL hint link hover must beat global .btn-link / theme rules */
.pdf-options-popup {
  width: 560px !important;
  max-width: calc(100vw - 32px) !important;
  padding: 28px 30px 24px !important;
  border-radius: 14px !important;
}

.pdf-options-title {
  padding: 0 !important;
  margin: 0 0 20px !important;
  font-size: 26px !important;
  line-height: 1.25 !important;
  color: #343a40 !important;
}

.pdf-options-content {
  padding: 0 !important;
  margin: 0 !important;
}

.pdf-options-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  text-align: left;
}

.pdf-option-card {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin: 0;
  padding: 14px 16px;
  border: 1px solid #dee2e6;
  border-radius: 10px;
  background: #f8f9fa;
  cursor: pointer;
  transition:
    border-color 0.15s ease,
    background-color 0.15s ease;
}

.pdf-option-card:hover {
  border-color: #5cb99f;
  background: #f6fbf9;
}

.pdf-option-checkbox {
  flex: 0 0 auto;
  width: 18px;
  height: 18px;
  margin: 2px 0 0;
  accent-color: #198b76;
}

.pdf-option-content {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.pdf-option-title {
  font-size: 16px;
  font-weight: 600;
  line-height: 1.3;
  color: #343a40;
}

.pdf-option-description {
  margin-top: 3px;
  font-size: 13px;
  line-height: 1.45;
  color: #6c757d;
}

.pdf-options-actions {
  justify-content: flex-end !important;
  gap: 10px;
  width: 100%;
  margin: 22px 0 0 !important;
}

.pdf-options-confirm,
.pdf-options-cancel {
  min-height: 40px;
  padding: 8px 18px;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 500;
}

.pdf-options-confirm {
  border: 1px solid #198b76;
  background: #198b76;
  color: #fff;
}

.pdf-options-confirm:hover {
  background: #157965;
  border-color: #157965;
}

.pdf-options-cancel {
  border: 1px solid #198b76;
  background: #fff;
  color: #198b76;
}

.pdf-options-cancel:hover {
  background: #f6fbf9;
}

.pdf-processing-popup {
  width: 480px !important;
  max-width: calc(100vw - 32px) !important;
  padding: 28px 30px !important;
  border-radius: 14px !important;
}

.pdf-processing-title {
  padding: 0 !important;
  margin: 0 0 20px !important;
  font-size: 24px !important;
  line-height: 1.25 !important;
  color: #343a40 !important;
}

.pdf-processing-content {
  padding: 0 !important;
  margin: 0 !important;
}

.pdf-processing-progress {
  width: 100%;
}

.pdf-processing-status {
  margin-bottom: 12px;
  font-size: 14px;
  color: #6c757d;
}

.pdf-processing-progress-track {
  width: 100%;
  height: 8px;
  overflow: hidden;
  background: #e9ecef;
  border-radius: 999px;
}

.pdf-processing-progress-bar {
  height: 100%;
  background: #198b76;
  border-radius: inherit;
  transition: width 0.25s ease;
}

.pdf-processing-progress-meta {
  margin-top: 8px;
  text-align: right;
  font-size: 13px;
  font-weight: 600;
  color: #495057;
}
.use-previous-asin-hint-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  margin-top: 0.25rem;
}

.use-previous-asin-hint-row .use-previous-asin-link {
  flex-shrink: 0;
  font-size: inherit;
  font-weight: inherit;
  line-height: inherit;
  color: #6c757d;
  cursor: pointer;
  text-decoration: none;
}

.use-previous-asin-hint-row .use-previous-asin-link:hover,
.use-previous-asin-hint-row .use-previous-asin-link:focus {
  color: #6c757d;
  text-decoration: underline !important;
}

.use-previous-asin-hint-row .use-previous-asin-link--disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.use-previous-asin-hint-row .use-previous-asin-link--disabled:hover,
.use-previous-asin-hint-row .use-previous-asin-link--disabled:focus {
  color: #6c757d;
  text-decoration: none !important;
}

.magnifying-glass-icon {
  text-align: center;
  padding: 6px 0;
  cursor: pointer !important;
  transition: all 0.2s ease;
  margin: 4px 0;
}

.magnifying-glass-icon * {
  cursor: pointer !important;
}

.magnifying-glass-icon i {
  color: #007bff;
  font-size: 22px;
  transition: all 0.2s ease;
}

.magnifying-glass-icon:hover i {
  color: #0056b3;
  transform: scale(1.1);
}

.magnifying-glass-icon:active i {
  transform: scale(0.95);
}

.custom-accessibility-tabs {
  background: #FFFFFF;
  border-radius: 12px;
  border: none;
}

.custom-accessibility-tabs .nav-tabs {
  border: none !important;
  display: flex;
  justify-content: space-between;
  background: #f1f3f5;
  border-radius: 8px;
  padding: 8px !important;
}

.custom-accessibility-tabs .nav-item {
  flex: 1;
  text-align: center;
  padding-left: 2px;
  padding-right: 2px;
}

.custom-accessibility-tabs .nav-link {
  font-weight: 600;
  color: #637381;
  border: none !important;
  background: transparent !important;
  border-radius: 8px !important;
  transition: all 0.2s ease-in-out;
  padding: 0.75rem 1rem;
}

.custom-accessibility-tabs .nav-link.active,
.custom-accessibility-tabs .nav-link:hover {
  background-color: #ffffff !important;
  color: #212b36 !important;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.05);
}

.custom-accessibility-tabs .nav-link {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.reversed-tabs .nav {
  display: flex;
  flex-direction: row-reverse;
}

.custom-accessibility-tabs .b-icon {
  font-size: 1.1rem;
}

.vue-dropzone {
  border: 2px dashed #dee2e6 !important;
  border-radius: 8px !important;
  background-color: #ffffff !important;
  min-height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  padding: 20px;
}

.dropzone-message {
  text-align: center;
  color: #212b36;
  font-family: "Inter", sans-serif;
}

.dropzone-icon {
  color: #919eab;
  margin-bottom: 8px;
}

.dropzone-icon svg {
  width: 28px;
  height: 28px;
}

.dropzone-text {
  font-size: 14px;
  color: #637381;
  margin: 0;
}

.dropzone-text a {
  color: #2196f3;
  font-weight: 600;
  text-decoration: none;
}

.dropzone-text a:hover {
  text-decoration: underline;
}

.dropzone-subtext {
  font-size: 12px;
  color: #919eab;
  margin-top: 4px;
}

.btn-group-style{
  background-color: #ffffff !important;
  color: #212b36 !important;
  border-color: #dee2e6;
  padding: 10px 18px 9px 18px;
}

.btn-group-style:hover{
  border-color: #dee2e6;
}

.advanced-dropdown button{
  background-color: #ffffff !important;
  color: #212b36 !important;
  border-color: #dee2e6;
  padding: 10px 18px 9px 18px;
}

.advanced-dropdown button:hover{
  border-color: #dee2e6;
}

#background_job_optix,
#reduce_image_quality,
#use_domain_as_client_name,
#save_settings {
  -webkit-appearance: none !important;
  -moz-appearance: none !important;
  appearance: none !important;
  width: 1em !important;
  height: 1em !important;
  margin-top: 0.25em !important;
  vertical-align: top !important;
  background-color: #fff !important;
  background-repeat: no-repeat !important;
  background-position: center !important;
  background-size: contain !important;
  border: 1px solid rgba(0, 0, 0, 0.25) !important;
  border-radius: 0.25em !important;
}

#background_job_optix:checked,
#reduce_image_quality:checked,
#use_domain_as_client_name:checked,
#save_settings:checked {
  background-color: #5cb99f !important;
  border-color: #5cb99f !important;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23fff' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round' d='M3.5 8l3 3l6-6'/%3e%3c/svg%3e") !important;
}

#background_job_optix:focus,
#reduce_image_quality:focus,
#use_domain_as_client_name:focus,
#save_settings:focus {
  border-color: #5cb99f !important;
  outline: 0 !important;
  box-shadow: 0 0 0 0.2rem rgba(92, 185, 159, 0.25) !important;
}

.generate-report{
  color: #ffffff !important;
  background-color: #5cb99f !important;
  border-color: #5cb99f !important;
  padding: 8px 24px;
  font-weight: 500;
  width: 100%;
}

.generate-report:hover{
  background-color: #4aa88e !important;
  border-color: #4aa88e !important;
}

.right-panel-footer {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #e9ecef;
}

.advanced-toggle {
  display: flex;
  align-items: center;
  cursor: pointer;
  color: #637381;
  font-size: 14px;
  padding: 8px 0;
  user-select: none;
}

.advanced-toggle:hover {
  color: #212b36;
}

.advanced-toggle .fa-chevron-down {
  transition: transform 0.2s ease;
  font-size: 12px;
}

.advanced-toggle .rotate-icon {
  transform: rotate(180deg);
}

.advanced-panel {
  font-size: 14px;
}

.advanced-options-toggle {
  display: flex;
  align-items: center;
  cursor: pointer;
  color: #212b36;
  font-size: 14px;
  user-select: none;
}

.advanced-options-toggle:hover {
  color: #637381;
}

.advanced-options-toggle .fa-chevron-right {
  transition: transform 0.2s ease;
  font-size: 10px;
}

.advanced-options-toggle .rotate-90 {
  transform: rotate(90deg);
}

.h-fit {
  height: fit-content;
}

/* Editable report title in header */
.editable-title-container {
  display: inline-block;
}

.report-title-display {
  cursor: pointer;
}

.report-title-display .edit-pencil-icon {
  opacity: 0;
  transition: opacity 0.2s ease;
  font-size: 0.9rem;
}

.report-title-display:hover .edit-pencil-icon {
  opacity: 1;
}

.editable-report-title {
  font-size: 1.25rem;
  font-weight: 700;
  border: none;
  border-bottom: 2px solid #3490dc;
  background: transparent;
  padding: 0 0 2px 0;
  outline: none;
  min-width: 350px;
}

.editable-report-title::placeholder {
  color: #6c757d;
  font-weight: 400;
}

/* Visual Benchmark Report simplified UI styles */
.visual-benchmark-input {
  border: 1px solid #ced4da;
  border-radius: 0.375rem;
}

.visual-benchmark-input:focus {
  border-color: #5cb99f;
  box-shadow: 0 0 0 0.2rem rgba(92, 185, 159, 0.25);
}

.visual-benchmark-select {
  border: 1px solid #ced4da;
  border-radius: 0.375rem;
}

.visual-benchmark-select:focus {
  border-color: #5cb99f;
  box-shadow: 0 0 0 0.2rem rgba(92, 185, 159, 0.25);
}

.visual-benchmark-input-group .input-group-text {
  background-color: #f8f9fa;
  border: 1px solid #ced4da;
  border-right: none;
  color: #6c757d;
}

.visual-benchmark-input-group .form-control {
  border-left: none;
}

.visual-benchmark-input-group .form-control:focus {
  border-color: #ced4da;
  box-shadow: none;
}

.visual-benchmark-input-group:focus-within .input-group-text {
  border-color: #5cb99f;
}

.visual-benchmark-input-group:focus-within .form-control {
  border-color: #5cb99f;
}

.salsify-property-select {
  position: absolute;
  margin-top: 4px;
  width: 100%;
  cursor: pointer !important;
}
.cursor-pointer {
  cursor: pointer;
}
.image-loading-overlay {
  min-width: 280px;
  padding: 24px 32px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 10px 35px rgba(0, 0, 0, 0.12);
}

.image-loading-spinner {
  width: 2.5rem;
  height: 2.5rem;
}

.image-loading-progress {
  width: 260px;
  height: 6px;
  margin: 0 auto;
  overflow: hidden;
  background: #e9ecef;
  border-radius: 999px;
}

.image-loading-progress-bar {
  height: 100%;
  background: #0d6efd;
  border-radius: inherit;
  transition: width 0.2s ease;
}
</style>
